import React, { useState, useRef, useEffect, useMemo } from 'react';
import './App.css';
import { marked } from 'marked';
import GoogleAnalytics from './GoogleAnalytics';

// Add PDF.js import - we'll load it dynamically
declare global {
  interface Window {
    pdfjsLib: any;
  }
}

interface Card {
  id: number;
  content: string;
  position: { x: number; y: number };
  conversationHistory: string[];
  title?: string;
  width?: number;
  height?: number;
  model?: string; // Add model field
  expiresAt?: number; // Add expiration timestamp
  isInteractive?: boolean; // Add interactive flag
  htmlContent?: string; // Add HTML content for interactive cards
  imageData?: string; // Add image data for pasted images
}

interface Image {
  id: number;
  url: string;
  position: { x: number; y: number };
  description?: string;
}

interface Command {
  undo: () => void;
  redo: () => void;
}

interface PDF {
  id: number;
  url: string;
  position: { x: number; y: number };
  width: number;
  height: number;
}

interface Link {
  fromId: number;
  toId: number;
}

// Add new interface for vision messages
interface VisionMessage {
  role: string;
  content: Array<{
    type: string;
    text?: string;
    image_url?: {
      url: string;
    };
  }>;
}

// Add these new interfaces near the top with other interfaces
interface ResizeHandle {
  cursor: string;
  position: {
    top?: string;
    bottom?: string;
    left?: string;
    right?: string;
  };
}

interface ResizeStart {
  x: number;
  y: number;
  width: number;
  height: number;
  handle: string;
  originalPosition: { x: number; y: number };
}

// Add these new interfaces
interface DrawingState {
  isDrawing: boolean;
  startCardId: number | null;
  endPosition: { x: number; y: number } | null;
}

// Add this new interface for WebSearch
interface WebSearchResult {
  title: string;
  url: string;
  snippet: string;
}

function App() {
  const [cards, setCards] = useState<Card[]>(() => {
    const savedCards = localStorage.getItem('cards');
    // Check if this is the first session
    if (!savedCards) {
      // Return initial state with intro cards
      return [
        {
          id: Date.now(),
          content: "### Try research features!\n\nUse special commands in your cards:\n\n**Quick Search:**\n- `/search latest news about AI`\n- `/search best restaurants in Tokyo`\n\n**Deep Research:**\n- `/deep climate change impacts`\n- `/deep history of artificial intelligence`\n\n**Parse Webpages:**\n- `/parse https://example.com/article`\n- `/parse https://en.wikipedia.org/wiki/Artificial_intelligence`\n\nDeep research performs multi-step analysis with comprehensive reports. Click Generate to use any feature!",
          position: { x: window.innerWidth/2 - 450, y: window.innerHeight/2 - 350 }, // Moved up 200px
          conversationHistory: [],
          width: 300,
          height: 400,
          model: 'openai', // Add default model
        },
        {
          id: Date.now() + 1,
          content: "### If you want to use AI...\n\nYou'll need an OpenAI API key. Or message https://x.com/@0sebo and they'll send you one.\n\nRemember to *double-click this card to edit it, or click anywhere on the canvas to create new cards. Select and delete this card if you're done with it.*",
          position: { x: window.innerWidth/2 - 100, y: window.innerHeight/2 - 350 }, // Moved up 200px
          conversationHistory: [],
          width: 300,
          height: 300,
          model: 'openai', // Add default model
        },
        {
          id: Date.now() + 2,
          content: "### Try web search!\n\nStart your content with `/search` followed by what you want to know.\n\nExamples:\n- `/search latest news about AI`\n- `/search who won the world cup 2022`\n- `/search best coffee shops in New York`\n\nClick the Generate button to search the web and get an answer based on current information.",
          position: { x: window.innerWidth/2 + 250, y: window.innerHeight/2 - 350 }, // Moved up 200px and to the right
          conversationHistory: [],
          width: 300,
          height: 320,
          model: 'openai', // Add default model
        }
      ];
    }
    return JSON.parse(savedCards);
  });
  const [images, setImages] = useState<Image[]>([]);
  const [inputPosition, setInputPosition] = useState({ x: 50, y: 50 });
  const [isDragging, setIsDragging] = useState(false);
  const [draggedCardId, setDraggedCardId] = useState<number | null>(null);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [inputValue, setInputValue] = useState("");
  const [editingCardId, setEditingCardId] = useState<number | null>(null);
  const [draggedImageId, setDraggedImageId] = useState<number | null>(null);
  const [selectedImageId, setSelectedImageId] = useState<number | null>(null);
  const [undoStack, setUndoStack] = useState<Command[]>([]);
  const [redoStack, setRedoStack] = useState<Command[]>([]);
  const [activeCardId, setActiveCardId] = useState<number | null>(null);
  const [uploadingImageForCard, setUploadingImageForCard] = useState<number | null>(null);
  const [tempCard, setTempCard] = useState<Card | null>(null);
  const [tempCardContent, setTempCardContent] = useState('');
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [draggedElementId, setDraggedElementId] = useState<number | null>(null);
  const [clipboard, setClipboard] = useState<{ type: 'card' | 'image', data: Card | Image } | null>(null);
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [duplicatedItemId, setDuplicatedItemId] = useState<number | null>(null);
  const [focusedCardId, setFocusedCardId] = useState<number | null>(null);
  const [renderedContents, setRenderedContents] = useState<{ [key: number]: string }>({});
  const [activeInputCardId, setActiveInputCardId] = useState<number | null>(null);
  const [resizingCardId, setResizingCardId] = useState<number | null>(null);
  const [resizeStart, setResizeStart] = useState<ResizeStart | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const [generatingCardId, setGeneratingCardId] = useState<number | null>(null);
  const [hoveredCardId, setHoveredCardId] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [pdfs, setPdfs] = useState<PDF[]>([]);
  const [links, setLinks] = useState<Link[]>(() => {
    const savedLinks = localStorage.getItem('links');
    return savedLinks ? JSON.parse(savedLinks) : [];
  });

  // Add this new state to track generated cards
  const [generatedCardIds, setGeneratedCardIds] = useState<Set<number>>(() => {
    const savedGeneratedCardIds = localStorage.getItem('generatedCardIds');
    return new Set(savedGeneratedCardIds ? JSON.parse(savedGeneratedCardIds) : []);
  });

  // Add this new state to track cards waiting for input
  const [pendingInputCards, setPendingInputCards] = useState<Set<number>>(new Set());

  // Add this new state to track newly created cards
  const [newCardId, setNewCardId] = useState<number | null>(null);

  // Add this new state to store the static rectangles
  const [staticRectangles, setStaticRectangles] = useState<{ [key: string]: any[] }>({});

  // Add these new state variables near the top where other state variables are declared
  const [openAIKey, setOpenAIKey] = useState(() => localStorage.getItem('openAIKey') || '');
  const [deepSeekKey, setDeepSeekKey] = useState(() => localStorage.getItem('deepSeekKey') || '');
  const [claudeKey, setClaudeKey] = useState(() => localStorage.getItem('claudeKey') || '');
  const [selectedModel, setSelectedModel] = useState(() => {
    const savedModel = localStorage.getItem('selectedModel');
    // If the saved model was 'claude' or 'ollama', default to 'openai' instead
    return (savedModel === 'claude' || savedModel === 'ollama') ? 'openai' : (savedModel || 'openai');
  });
  const [showConfigModal, setShowConfigModal] = useState(false);

  // Add this new state near your other state declarations
  const [hoveredImageId, setHoveredImageId] = useState<number | null>(null);

  // Add a new state for tracking which image is being interpreted
  const [interpretingImageId, setInterpretingImageId] = useState<number | null>(null);

  // Add these new state variables
  const [drawingState, setDrawingState] = useState<DrawingState>({
    isDrawing: false,
    startCardId: null,
    endPosition: null,
  });

  const [markerPosition, setMarkerPosition] = useState(() => {
    const savedMarker = localStorage.getItem('markerPosition');
    return savedMarker ? JSON.parse(savedMarker) : { x: 0, y: 0 };
  });

  const [isDraggingMarker, setIsDraggingMarker] = useState(false);
  const [markerDragOffset, setMarkerDragOffset] = useState<{ x: number; y: number } | null>(null);

  // Add new state variables for selection box
  const [selectionBox, setSelectionBox] = useState<{
    start: { x: number; y: number } | null;
    current: { x: number; y: number } | null;
    isActive: boolean;
  }>({
    start: null,
    current: null,
    isActive: false
  });

  // Add new state for context menu
  const [contextMenu, setContextMenu] = useState<{
    visible: boolean;
    x: number;
    y: number;
  }>({
    visible: false,
    x: 0,
    y: 0
  });

  // Add a state to track shift key being pressed
  const [isShiftPressed, setIsShiftPressed] = useState(false);
  const [isSelectionModeOn, setIsSelectionModeOn] = useState(false);

  // Add this state near the other state declarations (around line 150-160)
  const [isSearching, setIsSearching] = useState(false);

  // Add state to track when actual dragging movement has started
  const [isActuallyDragging, setIsActuallyDragging] = useState(false);

  // Add these new state variables near the other state declarations (around line 114)
  const [streamingCardId, setStreamingCardId] = useState<number | null>(null);
  const [streamingContent, setStreamingContent] = useState('');
  const [streamingDone, setStreamingDone] = useState(false);

  // Add this new state for document mode
  const [expandedCardId, setExpandedCardId] = useState<number | null>(() => {
    const saved = localStorage.getItem('expandedCardId');
    return saved ? parseInt(saved) : null;
  });
  const [documentMode, setDocumentMode] = useState(() => {
    const saved = localStorage.getItem('expandedCardId');
    return saved !== null;
  });

  // Add background color state
  const [backgroundColor, setBackgroundColor] = useState(() => {
    const saved = localStorage.getItem('backgroundColor');
    return saved || '#faedcd';
  });

  // Add background image state
  const [backgroundImage, setBackgroundImage] = useState<string | null>(() => {
    const saved = localStorage.getItem('backgroundImage');
    return saved || null;
  });

  // Add mouse position state for grid highlighting
  const [mousePosition, setMousePosition] = useState<{ x: number; y: number } | null>(null);

  // Add voice recording states
  const [recordingCardId, setRecordingCardId] = useState<number | null>(null);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);

  const appRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const backgroundImageInputRef = useRef<HTMLInputElement>(null);
  const isInitialMount = useRef(true);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const audioQueueRef = useRef<string[]>([]);

  // Add these new helper functions near the top of your component
  const isElementInViewport = (position: { x: number; y: number }, width: number, height: number): boolean => {
    // Convert viewport bounds to canvas space
    const viewportLeft = -pan.x / scale;
    const viewportTop = -pan.y / scale;
    const viewportRight = (window.innerWidth - pan.x) / scale;
    const viewportBottom = (window.innerHeight - pan.y) / scale;

    // Add padding to prevent popping at edges (adjust the 500 value as needed)
    const padding = 500;
    
    return !(position.x + width < viewportLeft - padding ||
             position.x > viewportRight + padding ||
             position.y + height < viewportTop - padding ||
             position.y > viewportBottom + padding);
  };

  // Remove the global preventDefault effect
  useEffect(() => {
    // Remove or comment out these lines
    // const preventDefault = (e: Event) => e.preventDefault();
    // document.addEventListener('selectstart', preventDefault);
    // return () => {
    //   document.removeEventListener('selectstart', preventDefault);
    // };
  }, []);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const mobileKeywords = ['android', 'webos', 'iphone', 'ipad', 'ipod', 'blackberry', 'windows phone'];
      const isMobileDevice = mobileKeywords.some(keyword => userAgent.includes(keyword));
      const isNarrowScreen = window.matchMedia("(max-width: 768px)").matches;
      setIsMobile(isMobileDevice || isNarrowScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    // Check orientation change for mobile devices
    window.addEventListener('orientationchange', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
      window.removeEventListener('orientationchange', checkMobile);
    };
  }, []);

  // Add effect to center viewport on marker position when page loads
  useEffect(() => {
    const centerX = window.innerWidth / 2;
    const centerY = window.innerHeight / 2;
    setPan({
      x: centerX - markerPosition.x * scale,
      y: centerY - markerPosition.y * scale
    });
  }, []); // Only run on mount

  useEffect(() => {
    const renderMarkdown = async () => {
      const renderer = new marked.Renderer();
      
      // Update the link renderer to handle different argument patterns
      renderer.link = ((href: string, title: string | null, text: string) => {
        // Check if the first argument is an object (new marked behavior)
        if (typeof href === 'object') {
          const linkObject = href as { href: string; title: string | null; text: string };
          href = linkObject.href;
          title = linkObject.title;
          text = linkObject.text;
        }

        return `<a href="${href}" title="${title || ''}" target="_blank" rel="noopener noreferrer">${text}</a>`;
      }) as any;

      marked.setOptions({ 
        renderer,
        breaks: true,  // This makes single line breaks render as <br> tags
        gfm: true      // Enable GitHub Flavored Markdown which supports HTML
      });

      const newRenderedContents: { [key: number]: string } = {};
      for (const card of cards) {
        newRenderedContents[card.id] = await marked(card.content || 'Double-click to edit');
      }
      setRenderedContents(newRenderedContents);
    };

    renderMarkdown();
  }, [cards]);

  useEffect(() => {
    console.log('Component mounted, loading saved state...');
    const loadSavedState = () => {
      try {
        const savedState = localStorage.getItem('canvasState');
        console.log('Raw saved state:', savedState);

        if (savedState) {
          const parsedState = JSON.parse(savedState);
          console.log('Parsed state:', parsedState);

          if (parsedState.cards && Array.isArray(parsedState.cards)) {
            setCards(parsedState.cards);
            console.log('Set cards:', parsedState.cards);
          }
          if (parsedState.images && Array.isArray(parsedState.images)) {
            setImages(parsedState.images);
            console.log('Set images:', parsedState.images);
          }
          if (parsedState.pdfs && Array.isArray(parsedState.pdfs)) {
            setPdfs(parsedState.pdfs);
            console.log('Set pdfs:', parsedState.pdfs);
          }
          if (parsedState.inputPosition) {
            setInputPosition(parsedState.inputPosition);
            console.log('Set inputPosition:', parsedState.inputPosition);
          }
        } else {
          console.log('No saved state found in localStorage');
        }
      } catch (error) {
        console.error('Error loading data from localStorage:', error);
      }
    };

    loadSavedState();
  }, []);

  useEffect(() => {
    if (isInitialMount.current) {
      console.log('Initial mount, skipping save');
      isInitialMount.current = false;
    } else {
      console.log('State changed, saving...');
      saveState();
    }
  }, [cards, images, pdfs, inputPosition]);

  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      if (e.ctrlKey) {
        const rect = appRef.current?.getBoundingClientRect();
        if (!rect) return;
        
        // Get raw mouse position relative to viewport
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        // Get the point in the document where the mouse is pointing
        const pointX = (mouseX - pan.x) / scale;
        const pointY = (mouseY - pan.y) / scale;

        const zoomFactor = 0.12; // Increased for much faster zooming
        const delta = e.deltaY > 0 ? -1 : 1;
        
        setScale(prevScale => {
          const newScale = prevScale * (1 + delta * zoomFactor);
          const boundedNewScale = Math.min(Math.max(newScale, 0.25), 4);
          
          // Calculate new pan position to keep the point under mouse fixed
          setPan(prevPan => ({
            x: mouseX - pointX * boundedNewScale,
            y: mouseY - pointY * boundedNewScale,
          }));
          
          return boundedNewScale;
        });
      } else {
        setPan(prevPan => ({
          x: prevPan.x - e.deltaX * 0.5,
          y: prevPan.y - e.deltaY * 0.5,
        }));
      }
    };

    const appElement = appRef.current;
    if (appElement) {
      appElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (appElement) {
        appElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [pan, scale]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip global paste handling when in document mode
      if (documentMode) return;
      
      if ((e.metaKey || e.ctrlKey) && e.key === 'v' && focusedCardId !== null) {
        e.preventDefault();
        navigator.clipboard.readText().then(pastedText => {
          // Try to parse as JSON first
          try {
            JSON.parse(pastedText);
            // If it parses successfully, it's probably our card format, so ignore it
            // when pasting into a card's content
            return;
          } catch {
            // If it fails to parse as JSON, it's regular text, so paste it
            if (editingCardId === focusedCardId) {
              setTempCardContent(prev => prev + pastedText);
            } else {
              setCards(prevCards =>
                prevCards.map(card =>
                  card.id === focusedCardId
                    ? { ...card, content: card.content + pastedText }
                    : card
                )
              );
            }
          }
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [focusedCardId, editingCardId, documentMode]);

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Skip global key handling when in document mode
      if (documentMode) return;
      
      if (editingCardId !== null) {
        if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
          console.log('Bold command detected');
          e.preventDefault();
          formatText('bold');
        } else if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
          console.log('Italic command detected');
          e.preventDefault();
          formatText('italic');
        } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'X') {
          console.log('Strikethrough command detected');
          e.preventDefault();
          formatText('strikethrough');
        } else if ((e.metaKey || e.ctrlKey) && e.key === 'h') {
          console.log('Highlight command detected');
          e.preventDefault();
          formatText('highlight');
        }
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'v' && activeInputCardId === null && focusedCardId === null) {
        // Handle paste when not editing any card
        e.preventDefault();
        navigator.clipboard.readText().then(pastedText => {
          if (pastedText) {
            // Calculate position - use mouse position if available, otherwise center of viewport
            let x, y;
            if (mousePosition) {
              x = mousePosition.x;
              y = mousePosition.y;
            } else {
              // Center of viewport in canvas coordinates
              x = (window.innerWidth / 2 - pan.x) / scale;
              y = (window.innerHeight / 2 - pan.y) / scale;
            }
            
            const newCard: Card = {
              id: Date.now(),
              content: pastedText,
              position: { x, y },
              conversationHistory: [],
              model: selectedModel,
              width: 250,
              height: Math.max(100, Math.min(300, pastedText.length * 0.5)), // Dynamic height based on content
            };
            
            setCards(prevCards => [...prevCards, newCard]);
            
            // Select the new card
            setSelectedCardId(newCard.id);
            setSelectedItems(new Set([newCard.id]));
          }
        }).catch(err => {
          console.error('Failed to read clipboard:', err);
        });
      }
    };

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [editingCardId, activeInputCardId, focusedCardId, mousePosition, pan, scale, selectedModel, documentMode]);

  // Add useEffect hooks to save state changes to localStorage
  useEffect(() => {
    localStorage.setItem('cards', JSON.stringify(cards));
  }, [cards]);

  useEffect(() => {
    localStorage.setItem('links', JSON.stringify(links));
  }, [links]);

  useEffect(() => {
    localStorage.setItem('generatedCardIds', JSON.stringify(Array.from(generatedCardIds)));
  }, [generatedCardIds]);

  // Add this new effect to save expandedCardId
  useEffect(() => {
    if (expandedCardId !== null) {
      localStorage.setItem('expandedCardId', expandedCardId.toString());
    } else {
      localStorage.removeItem('expandedCardId');
    }
  }, [expandedCardId]);

  // Save background color to localStorage
  useEffect(() => {
    localStorage.setItem('backgroundColor', backgroundColor);
  }, [backgroundColor]);

  // Save background image to localStorage
  useEffect(() => {
    if (backgroundImage) {
      localStorage.setItem('backgroundImage', backgroundImage);
    } else {
      localStorage.removeItem('backgroundImage');
    }
  }, [backgroundImage]);

  // Validate that expanded card still exists
  useEffect(() => {
    if (expandedCardId !== null && !cards.find(c => c.id === expandedCardId)) {
      setExpandedCardId(null);
      setDocumentMode(false);
    }
  }, [cards, expandedCardId]);

  // Add this new effect to optimize rerendering
  useEffect(() => {
    let rafId: number;
    let lastPan = { x: pan.x, y: pan.y };
    let lastScale = scale;

    const checkForUpdates = () => {
      if (lastPan.x !== pan.x || lastPan.y !== pan.y || lastScale !== scale) {
        lastPan = { x: pan.x, y: pan.y };
        lastScale = scale;
        // Force a rerender only when pan or scale actually changes
        setForceUpdate(prev => !prev);
      }
      rafId = requestAnimationFrame(checkForUpdates);
    };

    rafId = requestAnimationFrame(checkForUpdates);
    return () => cancelAnimationFrame(rafId);
  }, []);

  // Add this new state near your other state declarations
  const [forceUpdate, setForceUpdate] = useState(false);

  // Add this effect to check for and delete expired cards
  useEffect(() => {
    const checkExpiredCards = () => {
      const now = Date.now();
      const expiredCardIds = cards
        .filter(card => card.expiresAt && card.expiresAt <= now)
        .map(card => card.id);
      
      if (expiredCardIds.length > 0) {
        setCards(prevCards => prevCards.filter(card => !expiredCardIds.includes(card.id)));
      }
    };

    // Check on mount and then every minute
    checkExpiredCards();
    const interval = setInterval(checkExpiredCards, 60000);
    
    return () => clearInterval(interval);
  }, [cards]);

  // Add a new useEffect to track shift key for multiselect indicator
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't update shift state when in document mode to prevent re-renders
      if (documentMode) return;
      
      if (e.shiftKey) {
        setIsShiftPressed(true);
      }
    };
    
    const handleKeyUp = (e: KeyboardEvent) => {
      // Don't update shift state when in document mode to prevent re-renders
      if (documentMode) return;
      
      if (!e.shiftKey) {
        setIsShiftPressed(false);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [documentMode]);

  // Clear shift pressed state when entering document mode
  useEffect(() => {
    if (documentMode) {
      setIsShiftPressed(false);
    }
  }, [documentMode]);

  // Cleanup recording when component unmounts or page is refreshed
  useEffect(() => {
    const handleBeforeUnload = () => {
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      // Cleanup recording on unmount
      if (mediaRecorder && mediaRecorder.state === 'recording') {
        mediaRecorder.stop();
        if ((mediaRecorder as any).timer) {
          clearInterval((mediaRecorder as any).timer);
        }
      }
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [mediaRecorder]);

  // Conditional rendering for mobile
  if (isMobile) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        width: '100vw',
        fontFamily: '"Times New Roman", Times, serif',
        fontSize: '24px',
        textAlign: 'center',
        padding: '20px',
        backgroundColor: '#f0f0f0',
        boxSizing: 'border-box',
        position: 'fixed',
        top: 0,
        left: 0,
      }}>
        <div>
          <h1 style={{ marginBottom: '20px' }}>Desktop Only</h1>
        </div>
      </div>
    );
  }

  // Add this near the top of your component, where other constants are defined
  const wiggleKeyframes = `
    @keyframes subtleWiggle {
      0% { transform: rotate(0deg); }
      25% { transform: rotate(0.2deg); }
      75% { transform: rotate(-0.2deg); }
      100% { transform: rotate(0deg); }
    }
  `;

  const generateButtonKeyframes = `
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `;

  const addCommand = (command: Command) => {
    setUndoStack(prevStack => [...prevStack, command]);
    setRedoStack([]); // Clear redo stack when a new action is performed
  };

  const undo = () => {
    if (undoStack.length > 0) {
      const command = undoStack[undoStack.length - 1];
      command.undo();
      setUndoStack(prevStack => prevStack.slice(0, -1));
      setRedoStack(prevStack => [...prevStack, command]);
    }
  };

  const redo = () => {
    if (redoStack.length > 0) {
      const command = redoStack[redoStack.length - 1];
      command.redo();
      setRedoStack(prevStack => prevStack.slice(0, -1));
      setUndoStack(prevStack => [...prevStack, command]);
    }
  };

  // Save state to localStorage
  const saveState = () => {
    console.log('Saving state...');
    console.log('Current cards:', cards);
    console.log('Current images:', images);
    console.log('Current pdfs:', pdfs);
    console.log('Current inputPosition:', inputPosition);
    
    try {
      const stateToSave = {
        cards,
        images,
        pdfs,
        inputPosition,
      };
      localStorage.setItem('canvasState', JSON.stringify(stateToSave));
      console.log('Saved state to localStorage:', stateToSave);
    } catch (error) {
      console.error('Error saving data to localStorage:', error);
    }
  };

  // Modify the handleMouseDown function to support permanent selection mode
  const handleMouseDown = (e: React.MouseEvent, id?: number) => {
    if (id) {
      e.stopPropagation();
      e.preventDefault();

      // If cmd/ctrl is pressed, we only want to handle drawing
      if (e.metaKey || e.ctrlKey) {
        return;
      }

      setIsDragging(true);
      setDraggedElementId(id);
      setDragStart({
        x: e.clientX,
        y: e.clientY
      });

      // Set the draggedCardId if it's a card
      const card = cards.find(c => c.id === id);
      if (card && editingCardId !== id) {
        setDraggedCardId(id);
      }

      // Update this part to include PDFs
      if (e.altKey) {
        setIsDuplicating(true);
        setDuplicatedItemId(null);
        setSelectedItems(new Set([id]));
      } else {
        setIsDuplicating(false);
        if (!(e.shiftKey || isSelectionModeOn)) {
          if (!selectedItems.has(id)) {
            setSelectedItems(new Set([id]));
          }
        } else {
          setSelectedItems(prev => {
            const newSet = new Set(prev);
            if (newSet.has(id)) {
              newSet.delete(id);
            } else {
              newSet.add(id);
            }
            return newSet;
          });
        }
      }
    } else if (e.target === e.currentTarget) {
      // If clicking on the canvas and not an item, start a selection box
      const canvasX = (e.clientX - pan.x) / scale;
      const canvasY = (e.clientY - pan.y) / scale;
      
      // Only start selection box if not holding shift (to maintain existing selections)
      if (!(e.shiftKey || isSelectionModeOn)) {
        setSelectedItems(new Set()); // Clear selection if not holding shift or in selection mode
      }
      
      setSelectionBox({
        start: { x: canvasX, y: canvasY },
        current: { x: canvasX, y: canvasY },
        isActive: true
      });
    }
  };

  // Modify handleMouseMove to update the selection box
  const handleMouseMove = (e: React.MouseEvent) => {
    // Track mouse position for grid highlighting
    const canvasX = (e.clientX - pan.x) / scale;
    const canvasY = (e.clientY - pan.y) / scale;
    setMousePosition({ x: canvasX, y: canvasY });
    
    if (selectionBox.isActive && selectionBox.start) {
      // Update the current position of selection box
      const canvasX = (e.clientX - pan.x) / scale;
      const canvasY = (e.clientY - pan.y) / scale;
      
      setSelectionBox(prev => ({
        ...prev,
        current: { x: canvasX, y: canvasY }
      }));
      
      // Select items that intersect with the selection box
      const selectionRect = {
        left: Math.min(selectionBox.start.x, canvasX),
        top: Math.min(selectionBox.start.y, canvasY),
        right: Math.max(selectionBox.start.x, canvasX),
        bottom: Math.max(selectionBox.start.y, canvasY)
      };
      
      // Get all items that intersect with the selection box
      const intersectingCardIds = cards.filter(card => {
        const cardRect = {
          left: card.position.x,
          top: card.position.y,
          right: card.position.x + (card.width || 250),
          bottom: card.position.y + (card.height || 100)
        };
        
        return !(
          cardRect.right < selectionRect.left ||
          cardRect.left > selectionRect.right ||
          cardRect.bottom < selectionRect.top ||
          cardRect.top > selectionRect.bottom
        );
      }).map(card => card.id);
      
      const intersectingImageIds = images.filter(image => {
        // For simplicity, use a fixed size for images
        const imgRect = {
          left: image.position.x,
          top: image.position.y,
          right: image.position.x + 300, // Assuming 300px as default width
          bottom: image.position.y + 300 // Assuming 300px as default height
        };
        
        return !(
          imgRect.right < selectionRect.left ||
          imgRect.left > selectionRect.right ||
          imgRect.bottom < selectionRect.top ||
          imgRect.top > selectionRect.bottom
        );
      }).map(image => image.id);
      
      const intersectingPdfIds = pdfs.filter(pdf => {
        const pdfRect = {
          left: pdf.position.x,
          top: pdf.position.y,
          right: pdf.position.x + pdf.width,
          bottom: pdf.position.y + pdf.height
        };
        
        return !(
          pdfRect.right < selectionRect.left ||
          pdfRect.left > selectionRect.right ||
          pdfRect.bottom < selectionRect.top ||
          pdfRect.top > selectionRect.bottom
        );
      }).map(pdf => pdf.id);
      
      // Update selected items
      setSelectedItems(prev => {
        const newSelection = new Set(e.shiftKey ? Array.from(prev) : []);
        [...intersectingCardIds, ...intersectingImageIds, ...intersectingPdfIds].forEach(id => {
          newSelection.add(id);
        });
        return newSelection;
      });
    } else if (isDraggingMarker && markerDragOffset) {
      // Calculate new marker position based on mouse, pan, scale, and offset
      const canvasX = (e.clientX - pan.x) / scale;
      const canvasY = (e.clientY - pan.y) / scale;
      setMarkerPosition({
        x: canvasX - markerDragOffset.x,
        y: canvasY - markerDragOffset.y
      });
    } else if (drawingState.isDrawing) {
      setDrawingState(prev => ({
        ...prev,
        endPosition: {
          x: (e.clientX - pan.x) / scale,
          y: (e.clientY - pan.y) / scale
        }
      }));
    } else if (isDragging && dragStart && draggedElementId !== null) {
      const dx = (e.clientX - dragStart.x) / scale;
      const dy = (e.clientY - dragStart.y) / scale;

      // Set isActuallyDragging to true when actual movement has started
      if (!isActuallyDragging && (Math.abs(dx) > 2 || Math.abs(dy) > 2)) {
        setIsActuallyDragging(true);
      }

      if (isDuplicating && duplicatedItemId === null) {
        // Create a single duplicate
        const cardToDuplicate = cards.find(card => card.id === draggedElementId);
        const imageToDuplicate = images.find(image => image.id === draggedElementId);

        if (cardToDuplicate) {
          const newCard = {
            ...cardToDuplicate,
            id: Date.now(),
            position: {
              x: cardToDuplicate.position.x + dx,
              y: cardToDuplicate.position.y + dy
            }
          };
          setCards(prevCards => [...prevCards, newCard]);
          setDuplicatedItemId(newCard.id);
          setDraggedElementId(newCard.id);
          setSelectedItems(new Set([newCard.id]));
        } else if (imageToDuplicate) {
          const newImage = {
            ...imageToDuplicate,
            id: Date.now(),
            position: {
              x: imageToDuplicate.position.x + dx,
              y: imageToDuplicate.position.y + dy
            }
          };
          setImages(prevImages => [...prevImages, newImage]);
          setDuplicatedItemId(newImage.id);
          setDraggedElementId(newImage.id);
          setSelectedItems(new Set([newImage.id]));
        }
      } else {
        // Move selected items
        setCards(prevCards =>
          prevCards.map(card =>
            selectedItems.has(card.id)
              ? {
                  ...card,
                  position: {
                    x: card.position.x + dx,
                    y: card.position.y + dy
                  }
                }
              : card
          )
        );

        setImages(prevImages =>
          prevImages.map(image =>
            selectedItems.has(image.id)
              ? {
                  ...image,
                  position: {
                    x: image.position.x + dx,
                    y: image.position.y + dy
                  }
                }
              : image
          )
        );
      }

      setPdfs(prevPdfs =>
        prevPdfs.map(pdf =>
          selectedItems.has(pdf.id)
            ? {
                ...pdf,
                position: {
                  x: pdf.position.x + dx,
                  y: pdf.position.y + dy
                }
              }
            : pdf
        )
      );

      // After updating card positions, update the colors of the squares
      setStaticRectangles(prevRectangles => {
        const newRectangles = { ...prevRectangles };
        Object.keys(newRectangles).forEach(key => {
          newRectangles[key] = newRectangles[key].map(square => ({
            ...square,
            color: `hsl(${Math.random() * 360}, 70%, 70%)`,
          }));
        });
        return newRectangles;
      });

      setDragStart({
        x: e.clientX,
        y: e.clientY
      });
    } else if (resizingCardId !== null) {
      handleResize(e);
    }
  };

  // Modify handleMouseUp to complete the selection box
  const handleMouseUp = () => {
    if (selectionBox.isActive) {
      setSelectionBox({
        start: null,
        current: null,
        isActive: false
      });
    }
    
    setDrawingState({
      isDrawing: false,
      startCardId: null,
      endPosition: null
    });
    setIsDragging(false);
    setIsActuallyDragging(false); // Reset the actual dragging state
    setDraggedElementId(null);
    setDragStart(null);
    setDraggedCardId(null); // Reset the draggedCardId
    handleResizeEnd();
    if (isDraggingMarker) {
      setIsDraggingMarker(false);
      setMarkerDragOffset(null);
      localStorage.setItem('markerPosition', JSON.stringify(markerPosition));
    }
    if (isDuplicating) {
      setIsDuplicating(false);
      if (duplicatedItemId !== null) {
        setSelectedItems(new Set([duplicatedItemId]));
      }
      setDuplicatedItemId(null);
    }
  };

  const handleGenerate = (text: string) => {
    console.log('handleGenerate called with text:', text);
    if (text.trim()) {
      const newCard: Card = {
        id: Date.now(),
        content: text,
        position: { x: inputPosition.x + 320, y: inputPosition.y },
        conversationHistory: [],
        model: selectedModel, // Make sure model is assigned
      };
      console.log('Creating new card:', newCard);
      
      // Generate response based on selected model
      const generateResponse = async () => {
        console.log(`Using ${selectedModel} model for generation`);
        const response: string = await generateResponseWithModel(newCard.id, text);
        
        console.log('Generated response:', response);
        setCards(prevCards => {
          const responseCard: Card = {
            id: Date.now(),
            content: response,
            position: { x: newCard.position.x + 260, y: newCard.position.y },
            conversationHistory: [text, response],
            model: selectedModel, // Make sure model is assigned to response card
          };
          console.log('Creating response card:', responseCard);
          return [...prevCards, newCard, responseCard];
        });
      };

      generateResponse();
    }
  };

  // Add a helper function for generating responses with the selected model
  const generateResponseWithModel = async (cardId: number, prompt: string): Promise<string> => {
    if (selectedModel === 'deepseek') {
      return generateDeepSeekResponse(cardId, prompt, false);
    } else if (selectedModel === 'claude-sonnet-4-20250514') {
      return generateClaudeResponse(cardId, prompt, false);
    } else {
      return generateChatGPTResponse(cardId, prompt, false);
    }
  };

  const handleDeleteItem = () => {
    if (selectedCardId !== null) {
      const cardToDelete = cards.find(card => card.id === selectedCardId);
      if (cardToDelete) {
        const deleteCardCommand: Command = {
          undo: () => setCards(prevCards => [...prevCards, cardToDelete]),
          redo: () => setCards(prevCards => prevCards.filter(card => card.id !== selectedCardId))
        };
        addCommand(deleteCardCommand);
        deleteCardCommand.redo();
      }
      setSelectedCardId(null);
    } else if (selectedImageId !== null) {
      const imageToDelete = images.find(image => image.id === selectedImageId);
      if (imageToDelete) {
        const deleteImageCommand: Command = {
          undo: () => setImages(prevImages => [...prevImages, imageToDelete]),
          redo: () => setImages(prevImages => prevImages.filter(image => image.id !== selectedImageId))
        };
        addCommand(deleteImageCommand);
        deleteImageCommand.redo();
      }
      setSelectedImageId(null);
    }
  };

  const handleDeleteSelectedItems = () => {
    // Don't delete if an input is active
    if (activeInputCardId !== null) return;

    const cardIdsToDelete = new Set(cards.filter(card => selectedItems.has(card.id)).map(card => card.id));
    const imageIdsToDelete = new Set(images.filter(image => selectedItems.has(image.id)).map(image => image.id));
    const pdfIdsToDelete = new Set(pdfs.filter(pdf => selectedItems.has(pdf.id)).map(pdf => pdf.id));

    const deletedCards = cards.filter(card => cardIdsToDelete.has(card.id));
    const deletedImages = images.filter(image => imageIdsToDelete.has(image.id));
    const deletedPdfs = pdfs.filter(pdf => pdfIdsToDelete.has(pdf.id));

    const deleteCommand: Command = {
      undo: () => {
        setCards(prevCards => [...prevCards, ...deletedCards]);
        setImages(prevImages => [...prevImages, ...deletedImages]);
        setPdfs(prevPdfs => [...prevPdfs, ...deletedPdfs]);
        setSelectedItems(new Set([...deletedCards.map(card => card.id), ...deletedImages.map(image => image.id), ...deletedPdfs.map(pdf => pdf.id)]));
      },
      redo: () => {
        setCards(prevCards => prevCards.filter(card => !cardIdsToDelete.has(card.id)));
        setImages(prevImages => prevImages.filter(image => !imageIdsToDelete.has(image.id)));
        setPdfs(prevPdfs => prevPdfs.filter(pdf => !pdfIdsToDelete.has(pdf.id)));
        setSelectedItems(new Set());
      }
    };

    addCommand(deleteCommand);
    deleteCommand.redo();
  };

  // Enhance the handleKeyDown function to handle more keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Skip global keyboard handling if we're editing a card
    if (editingCardId !== null) {
      return;
    }

    console.log('Key pressed:', e.key, 'Meta key:', e.metaKey, 'Ctrl key:', e.ctrlKey);

    // Handle delete if no input is active
    if ((e.key === 'Delete' || e.key === 'Backspace') && selectedItems.size > 0 && activeInputCardId === null) {
      e.preventDefault();
      handleDeleteSelectedItems();
    } else if (e.key === 'Enter' && selectedCardId !== null && !editingCardId) {
      // Open selected card in document view with Enter key
      e.preventDefault();
      setExpandedCardId(selectedCardId);
      setDocumentMode(true);
    } else if (e.key === 'Escape') {
      // Clear selection on Escape key
      e.preventDefault();
      setSelectedItems(new Set());
      setSelectedCardId(null);
      setSelectedImageId(null);
      setContextMenu({ ...contextMenu, visible: false }); // Hide context menu on Escape
    } else if (e.ctrlKey || e.metaKey) {
      if (e.key === 'a' && activeInputCardId === null) {
        e.preventDefault();
        // Select all cards, images, and PDFs
        const allIds = new Set([
          ...cards.map(card => card.id),
          ...images.map(image => image.id),
          ...pdfs.map(pdf => pdf.id)
        ]);
        setSelectedItems(allIds);
      } else if (e.key === 'd' && selectedItems.size > 0) {
        // Duplicate selected items with Ctrl+D
        e.preventDefault();
        handleDuplicateSelectedItems();
      } else if (e.key === 'z') {
        e.preventDefault();
        undo();
      } else if (e.key === 'y' || (e.shiftKey && e.key === 'z')) {
        e.preventDefault();
        redo();
      }
    }
  };

  // Add function to duplicate multiple selected items
  const handleDuplicateSelectedItems = () => {
    // Clone cards
    const selectedCards = cards.filter(card => selectedItems.has(card.id));
    const newCards = selectedCards.map(card => ({
      ...card,
      id: Date.now() + Math.random(), // Ensure unique ID
      position: {
        x: card.position.x + 20,
        y: card.position.y + 20
      }
    }));

    // Clone images
    const selectedImages = images.filter(image => selectedItems.has(image.id));
    const newImages = selectedImages.map(image => ({
      ...image,
      id: Date.now() + Math.random(),
      position: {
        x: image.position.x + 20,
        y: image.position.y + 20
      }
    }));

    // Clone PDFs
    const selectedPdfs = pdfs.filter(pdf => selectedItems.has(pdf.id));
    const newPdfs = selectedPdfs.map(pdf => ({
      ...pdf,
      id: Date.now() + Math.random(),
      position: {
        x: pdf.position.x + 20,
        y: pdf.position.y + 20
      }
    }));

    // Create a command for undo/redo
    const duplicateCommand: Command = {
      undo: () => {
        setCards(prevCards => prevCards.filter(card => !newCards.some(nc => nc.id === card.id)));
        setImages(prevImages => prevImages.filter(image => !newImages.some(ni => ni.id === image.id)));
        setPdfs(prevPdfs => prevPdfs.filter(pdf => !newPdfs.some(np => np.id === pdf.id)));
        setSelectedItems(new Set(Array.from(selectedItems)));
      },
      redo: () => {
        setCards(prevCards => [...prevCards, ...newCards]);
        setImages(prevImages => [...prevImages, ...newImages]);
        setPdfs(prevPdfs => [...prevPdfs, ...newPdfs]);
        // Select the newly created items
        const newItemIds = new Set([
          ...newCards.map(card => card.id),
          ...newImages.map(image => image.id),
          ...newPdfs.map(pdf => pdf.id)
        ]);
        setSelectedItems(newItemIds);
      }
    };

    addCommand(duplicateCommand);
    duplicateCommand.redo();
  };

  // Add a context menu handler
  const handleContextMenu = (e: React.MouseEvent) => {
    // Only show context menu if there are items selected
    if (selectedItems.size > 0) {
      e.preventDefault();
      setContextMenu({
        visible: true,
        x: e.clientX,
        y: e.clientY
      });
    }
  };

  // Close context menu when clicking outside
  const handleClickOutsideContextMenu = () => {
    if (contextMenu.visible) {
      setContextMenu({ ...contextMenu, visible: false });
    }
  };

  // Modify handleCardContentKeyDown
  const handleCardContentKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>, cardId: number) => {
    // Allow all default behavior when cmd/ctrl is pressed
    if (e.metaKey || e.ctrlKey) {
      e.stopPropagation(); // Stop propagation but allow default behavior
      return;
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleCardBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingCardId(null);
      setTempCardContent('');
    }
    
    e.stopPropagation();
  };

  // First, let's add the formatText function
  const formatText = (type: 'bold' | 'italic' | 'strikethrough' | 'highlight') => {
    if (!textareaRef.current) return;
    
    const textarea = textareaRef.current;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = textarea.value.substring(start, end);
    
    let marker = '';
    if (type === 'bold') marker = '**';
    else if (type === 'italic') marker = '_';
    else if (type === 'strikethrough') marker = '~~';
    else if (type === 'highlight') {
      const newText = textarea.value.substring(0, start) + 
                     '<mark>' + selectedText + '</mark>' + 
                     textarea.value.substring(end);
      setTempCardContent(newText);
      return;
    }
    
    const newText = textarea.value.substring(0, start) + 
                   marker + selectedText + marker + 
                   textarea.value.substring(end);
    
    setTempCardContent(newText);
  };

  // Update handleCardContentChange
  const handleCardContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setTempCardContent(newContent);
    
    if (editingCardId) {
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === editingCardId
            ? { ...card, content: newContent }
            : card
        )
      );
      
      // Update rendered content
      // Force marked to return a string synchronously
      const renderedContent = marked.parse(newContent, { async: false }) as string;
      setRenderedContents(prev => ({
        ...prev,
        [editingCardId]: renderedContent
      }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    console.log("Input changed:", e.target.value);
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleGenerate(inputValue); // Pass inputValue here
    }
  };

  const handleCardClick = (e: React.MouseEvent, cardId: number) => {
    e.stopPropagation();
    if (!isDragging) {
      // Don't reset selection if we're currently editing
      if (editingCardId !== cardId) {
        setSelectedCardId(cardId);
        setEditingCardId(null);
        setSelectedImageId(null);
      }
    }
  };

  const handleCardDoubleClick = (e: React.MouseEvent, cardId: number) => {
    e.stopPropagation();
    e.preventDefault();
    
    // If we're already editing this card, don't reset the content
    if (editingCardId === cardId) {
      return;
    }

    setEditingCardId(cardId);
    const card = cards.find(c => c.id === cardId);
    setTempCardContent(card?.content || '');
  };

  const saveCardContent = (cardId: number) => {
    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId ? { ...card, content: tempCardContent } : card
      )
    );
    setEditingCardId(null);
    setTempCardContent('');
  };

  // Add this new function near the top of the component
  const isTextSelected = () => {
    const selection = window.getSelection();
    return selection && selection.toString().length > 0;
  };

  // Modify the handleCardBlur function
  const handleCardBlur = (e?: React.FocusEvent) => {
    // Add a small delay to check for text selection
    setTimeout(() => {
      const selection = window.getSelection();
      const isTextCurrentlySelected = selection && selection.toString().length > 0;
      
      // If text is selected, refocus the textarea
      if (isTextCurrentlySelected && textareaRef.current) {
        textareaRef.current.focus();
        return;
      }

      // Only proceed with blur if no text is selected
      if (!isTextCurrentlySelected && editingCardId !== null) {
        setCards(prevCards => prevCards.map(card => 
          card.id === editingCardId ? { ...card, content: tempCardContent } : card
        ));
        setEditingCardId(null);
        setTempCardContent('');
      }
    }, 0);
  };

  const handleCanvasDoubleClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      const cardWidth = 250;
      const cardHeight = 100;

      const newCard: Card = {
        id: Date.now(),
        content: '',
        position: {
          x: (e.clientX - pan.x) / scale - cardWidth / 2,
          y: (e.clientY - pan.y) / scale
        },
        conversationHistory: [],
        model: selectedModel, // Use the currently selected model
      };
      
      setCards(prevCards => [...prevCards, newCard]);
      setTempCardContent('');
      setEditingCardId(newCard.id);
      setNewCardId(newCard.id);
      
      setTimeout(() => setNewCardId(null), 100);
    }
  };

  // Modify the handleCanvasClick function to clear selection
  const handleCanvasClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !selectionBox.isActive) {
      handleCardBlur();
      if (!e.shiftKey) {
        setSelectedItems(new Set());
      }
      setEditingCardId(null);
    }
  };

  // Add new function to handle vision-based generation
  const generateVisionResponse = async (imageUrl: string, prompt: string = "What's in this image?") => {
    if (!openAIKey) {
      return "Please set up your OpenAI API key first to use vision features.";
    }

    try {
      // Convert data URL to base64 string
      const base64Image = imageUrl.split(',')[1];

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAIKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: "gpt-4o-mini",  // Updated to correct model name
          messages: [
            {
              role: "user",
              content: [
                { type: "text", text: prompt },
                {
                  type: "image_url",
                  image_url: {
                    url: `data:image/jpeg;base64,${base64Image}`
                  }
                }
              ]
            }
          ],
          max_tokens: 300
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('OpenAI API error:', errorData);
        throw new Error(`HTTP error! status: ${response.status}, message: ${JSON.stringify(errorData)}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error generating vision response:', error);
      return `Error analyzing image: ${error instanceof Error ? error.message : 'Unknown error occurred'}`;
    }
  };

  // Modify handleImageUpload to create a link
  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const imageUrl = event.target?.result as string;
        const newImage = {
          id: Date.now(),
          url: imageUrl,
          position: {
            x: (inputPosition.x - pan.x) / scale,
            y: (inputPosition.y - pan.y) / scale,
          },
        };
        
        // Add the image first
        setImages(prevImages => [...prevImages, newImage]);

        // Generate vision response
        const description = await generateVisionResponse(imageUrl);
        
        // Create a new card with the vision response
        const responseCard: Card = {
          id: Date.now() + 1,
          content: description,
          position: {
            x: newImage.position.x + 260,
            y: newImage.position.y,
          },
          conversationHistory: [],
          width: 250,
          height: 200,
        };

        setCards(prevCards => [...prevCards, responseCard]);
        
        // Create a link between the image and the response card
        setLinks(prevLinks => [...prevLinks, {
          fromId: newImage.id,
          toId: responseCard.id
        }]);
      };
      reader.readAsDataURL(file);
    }
  };

  // Modify analyzeExistingImage to create a link
  const analyzeExistingImage = async (imageId: number) => {
    const image = images.find(img => img.id === imageId);
    if (!image) return;

    setInterpretingImageId(imageId); // Set loading state
    
    try {
      const description = await generateVisionResponse(image.url);
      
      const responseCard: Card = {
        id: Date.now(),
        content: description,
        position: {
          x: image.position.x + 260,
          y: image.position.y,
        },
        conversationHistory: [],
        width: 250,
        height: 200,
      };

      setCards(prevCards => [...prevCards, responseCard]);
      
      // Create a link between the image and the response card
      setLinks(prevLinks => [...prevLinks, {
        fromId: imageId,
        toId: responseCard.id
      }]);
    } finally {
      setInterpretingImageId(null); // Clear loading state
    }
  };

  const triggerImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleCardImageUpload = (cardId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const uploadImage = (file: File) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const imageUrl = event.target?.result as string;
        const card = cards.find(c => c.id === cardId);
        if (card) {
          const newImage: Image = {
            id: Date.now(),
            url: imageUrl,
            position: {
              x: card.position.x,
              y: card.position.y,
            },
          };
          setImages(prevImages => [...prevImages, newImage]);
          setCards(prevCards => prevCards.filter(c => c.id !== cardId));
          setEditingCardId(null);
        }
      };
      reader.readAsDataURL(file);
    };

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        uploadImage(file);
      }
    };
    input.click();
  };

  const handleCardPdfUpload = (cardId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const uploadPdf = (file: File) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        const pdfUrl = event.target?.result as string;
        const card = cards.find(c => c.id === cardId);
        if (card) {
          const newPdf: PDF = {
            id: Date.now(),
            url: pdfUrl,
            position: {
              x: card.position.x + 260,
              y: card.position.y,
            },
            width: 320,  // Initial width
            height: 450, // Initial height
          };
          setPdfs(prevPdfs => [...prevPdfs, newPdf]);
        }
      };
      reader.readAsDataURL(file);
    };

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        uploadPdf(file);
      }
    };
    input.click();
  };

  // Add PDF parsing upload handler
  const handlePdfParseUpload = (cardId: number, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'application/pdf';
    input.onchange = async (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (file) {
        setGeneratingCardId(cardId);
        
        // Create a new card immediately with a loading message
        const responseCardId = Date.now();
        const newCard: Card = {
          id: responseCardId,
          title: `Parsing: ${file.name}`,
          content: "Extracting text from PDF...",
          position: {
            x: cards.find(c => c.id === cardId)?.position.x! + 260,
            y: cards.find(c => c.id === cardId)?.position.y!,
          },
          conversationHistory: [file.name],
          width: 400,
          height: 500,
        };
        
        // Add the card immediately
        setCards(prevCards => [...prevCards, newCard]);
        setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: responseCardId }]);
        
        try {
          // Parse the PDF content
          const parsedContent = await parsePDF(file);
          
          // Update the card with the parsed content
          setCards(prevCards => 
            prevCards.map(c => 
              c.id === responseCardId 
                ? { 
                    ...c, 
                    content: parsedContent,
                    conversationHistory: [file.name, parsedContent],
                  }
                : c
            )
          );
        } catch (error) {
          console.error("Error parsing PDF:", error);
          // Update the card with the error message
          setCards(prevCards => 
            prevCards.map(c => 
              c.id === responseCardId 
                ? { ...c, content: `Error parsing PDF: ${error instanceof Error ? error.message : 'Unknown error'}` }
                : c
            )
          );
        } finally {
          setGeneratingCardId(null);
        }
      }
    };
    input.click();
  };

  const finalizeCardCreation = () => {
    if (tempCard) {
      if (tempCard.content.trim() !== '') {
        setCards(prevCards => [...prevCards, tempCard]);
      }
      setTempCard(null);
      setEditingCardId(null);
    }
  };

  const handleImageClick = (e: React.MouseEvent, imageId: number) => {
    e.stopPropagation();
    if (!isDragging) {
      setSelectedImageId(imageId);
      setSelectedCardId(null);
      setEditingCardId(null);
    }
  };

  const handleDeleteImage = () => {
    if (selectedImageId !== null) {
      const imageToDelete = images.find(image => image.id === selectedImageId);
      if (imageToDelete) {
        const deleteImageCommand: Command = {
          undo: () => setImages(prevImages => [...prevImages, imageToDelete]),
          redo: () => setImages(prevImages => prevImages.filter(image => image.id !== selectedImageId))
        };
        addCommand(deleteImageCommand);
        deleteImageCommand.redo();
      }
      setSelectedImageId(null);
    }
  };

  const handleDeleteCard = () => {
    if (selectedCardId !== null) {
      setCards(prevCards => prevCards.filter(card => card.id !== selectedCardId));
      setSelectedCardId(null);
    }
  };

  const wiggleStyle = {
    animation: 'subtleWiggle 0.s ease-in-out infinite',
    };

  const handleCopy = (e: React.ClipboardEvent) => {
    // Only handle structured card/image copying when no text is actively selected
    if (window.getSelection()?.toString()) {
      return; // Let the default copy behavior handle selected text
    }

    e.preventDefault();
    if (selectedCardId !== null) {
      const cardToCopy = cards.find(card => card.id === selectedCardId);
      if (cardToCopy) {
        e.clipboardData.setData('text/plain', JSON.stringify({ type: 'card', data: cardToCopy }));
      }
    } else if (selectedImageId !== null) {
      const imageToCopy = images.find(image => image.id === selectedImageId);
      if (imageToCopy) {
        e.clipboardData.setData('text/plain', JSON.stringify({ type: 'image', data: imageToCopy }));
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const pastedData = e.clipboardData.getData('text');
    
    // Try to parse as JSON first
    try {
      const parsedData = JSON.parse(pastedData);
      if (parsedData.type === 'card' || parsedData.type === 'image') {
        e.preventDefault();
        if (parsedData.type === 'card') {
          const newCard: Card = {
            ...parsedData.data,
            id: Date.now(),
            position: {
              x: parsedData.data.position.x + 20,
              y: parsedData.data.position.y + 20,
            },
          };
          setCards(prevCards => [...prevCards, newCard]);
        } else if (parsedData.type === 'image') {
          const newImage: Image = {
            ...parsedData.data,
            id: Date.now(),
            position: {
              x: parsedData.data.position.x + 20,
              y: parsedData.data.position.y + 20,
            },
          };
          setImages(prevImages => [...prevImages, newImage]);
        }
      }
    } catch (error) {
      // If it's not valid JSON and we're not editing a card, create a new card with the pasted text
      if (pastedData && activeInputCardId === null && editingCardId === null) {
        e.preventDefault();
        
        // Calculate position - use mouse position if available, otherwise center of viewport
        let x, y;
        if (mousePosition) {
          x = mousePosition.x;
          y = mousePosition.y;
        } else {
          // Center of viewport in canvas coordinates
          x = (window.innerWidth / 2 - pan.x) / scale;
          y = (window.innerHeight / 2 - pan.y) / scale;
        }
        
        const newCard: Card = {
          id: Date.now(),
          content: pastedData,
          position: { x, y },
          conversationHistory: [],
          model: selectedModel,
          width: 250,
          height: Math.max(100, Math.min(300, pastedData.length * 0.5)), // Dynamic height based on content
        };
        
        setCards(prevCards => [...prevCards, newCard]);
        
        // Optionally, select the new card
        setSelectedCardId(newCard.id);
        setSelectedItems(new Set([newCard.id]));
      }
    }
  };

  const handleCardContentEdit = (cardId: number, newContent: string) => {
    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId ? { ...card, content: newContent } : card
      )
    );
  };

  const handleCardPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>, cardId: number) => {
    e.stopPropagation(); // Prevent the event from bubbling up
    
    // Check if clipboard contains image data
    const items = Array.from(e.clipboardData.items);
    const imageItem = items.find(item => item.type.startsWith('image/'));
    
    if (imageItem) {
      e.preventDefault(); // Prevent default paste for images
      
      const file = imageItem.getAsFile();
      if (file) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageData = event.target?.result as string;
          
          // Update the card with the image data and switch to OpenAI model for vision support
          setCards(prevCards =>
            prevCards.map(card =>
              card.id === cardId
                ? { ...card, imageData, model: 'openai' }
                : card
            )
          );
        };
        reader.readAsDataURL(file);
      }
    }
    // If no image, allow default text paste behavior
  };

  // Add this function near the top of your component
  const preventDefault = (e: Event) => e.preventDefault();

  // Add this new constant for resize handles
  const RESIZE_HANDLES: { [key: string]: ResizeHandle } = {
    'top-left': { cursor: 'nw-resize', position: { top: '0', left: '0' } },
    'top-right': { cursor: 'ne-resize', position: { top: '0', right: '0' } },
    'bottom-left': { cursor: 'sw-resize', position: { bottom: '0', left: '0' } },
    'bottom-right': { cursor: 'se-resize', position: { bottom: '0', right: '0' } },
    'top': { cursor: 'n-resize', position: { top: '0', left: '50%' } },
    'bottom': { cursor: 's-resize', position: { bottom: '0', left: '50%' } },
    'left': { cursor: 'w-resize', position: { left: '0', top: '50%' } },
    'right': { cursor: 'e-resize', position: { right: '0', top: '50%' } },
  };

  // Update handleResizeStart
  const handleResizeStart = (e: React.MouseEvent, id: number, handle: string) => {
    e.stopPropagation();
    const item = cards.find(c => c.id === id) || pdfs.find(p => p.id === id);
    if (item) {
      setResizingCardId(id);
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: ('width' in item && typeof item.width === 'number') ? item.width : 250,
        height: ('height' in item && typeof item.height === 'number') ? item.height : 100,
        handle,
        originalPosition: { x: item.position.x, y: item.position.y }
      });
      setIsResizing(true);
      document.addEventListener('selectstart', preventDefault);
      document.body.style.userSelect = 'none';
    }
  };

  // Update handleResize
  const handleResize = (e: React.MouseEvent) => {
    if (resizingCardId !== null && resizeStart) {
      const dx = (e.clientX - resizeStart.x) / scale;
      const dy = (e.clientY - resizeStart.y) / scale;
      
      let newWidth = resizeStart.width;
      let newHeight = resizeStart.height;
      let newX = resizeStart.originalPosition.x;
      let newY = resizeStart.originalPosition.y;

      // Handle width changes
      if (resizeStart.handle.includes('left')) {
        newWidth = Math.max(150, resizeStart.width - dx);
        newX = resizeStart.originalPosition.x + resizeStart.width - newWidth;
      } else if (resizeStart.handle.includes('right')) {
        newWidth = Math.max(150, resizeStart.width + dx);
      }

      // Handle height changes
      if (resizeStart.handle.includes('top')) {
        newHeight = Math.max(100, resizeStart.height - dy);
        newY = resizeStart.originalPosition.y + resizeStart.height - newHeight;
      } else if (resizeStart.handle.includes('bottom')) {
        newHeight = Math.max(100, resizeStart.height + dy);
      }

      // Update cards
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === resizingCardId
            ? {
                ...card,
                width: newWidth,
                height: newHeight,
                position: { x: newX, y: newY }
              }
            : card
        )
      );

      // Update PDFs
      setPdfs(prevPdfs =>
        prevPdfs.map(pdf =>
          pdf.id === resizingCardId
            ? {
                ...pdf,
                width: newWidth,
                height: newHeight,
                position: { x: newX, y: newY }
              }
            : pdf
        )
      );
    }
  };

  const handleResizeEnd = () => {
    setResizingCardId(null);
    setResizeStart(null);
    setIsResizing(false);
    document.removeEventListener('selectstart', preventDefault);
    document.body.style.userSelect = '';
  };

  // Add this new function to find the parent card
  const findParentCard = (cardId: number): Card | null => {
    const parentLink = links.find(link => link.toId === cardId);
    if (parentLink) {
      return cards.find(card => card.id === parentLink.fromId) || null;
    }
    return null;
  };

  // Add this function to check if a card is a child
  const isChildCard = (cardId: number): boolean => {
    return links.some(link => link.toId === cardId);
  };

  const truncateTitle = (content: string, maxLength: number = 50): string => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength - 3) + '...';
  };

  // Add this new function near your other generate functions
  const generateImage = async (prompt: string): Promise<string | null> => {
    if (!openAIKey) {
      console.log('No OpenAI API key found');
      return null;
    }

    try {
      const response = await fetch('https://api.openai.com/v1/images/generations', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAIKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: "dall-e-3",
          prompt: prompt,
          n: 1,
          size: "1024x1024",
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.data[0].url;
    } catch (error) {
      console.error('Error generating image:', error);
      return null;
    }
  };

  // Load PDF.js library dynamically
  const loadPDFJS = async () => {
    if (window.pdfjsLib) return window.pdfjsLib;
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = () => {
        if (window.pdfjsLib) {
          window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
          resolve(window.pdfjsLib);
        } else {
          reject(new Error('PDF.js failed to load'));
        }
      };
      script.onerror = () => reject(new Error('Failed to load PDF.js'));
      document.head.appendChild(script);
    });
  };

  // Add PDF parsing function
  const parsePDF = async (file: File): Promise<string> => {
    try {
      const pdfjsLib = await loadPDFJS();
      
      // Convert file to array buffer
      const arrayBuffer = await file.arrayBuffer();
      
      // Load the PDF document
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      
      let fullText = '';
      
      // Extract text from each page
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        
        // Combine text items from the page
        const pageText = textContent.items
          .map((item: any) => item.str)
          .join(' ')
          .trim();
        
        if (pageText) {
          fullText += `\n\n**Page ${pageNum}**\n\n${pageText}`;
        }
      }
      
      if (!fullText.trim()) {
        return `**PDF Parsing Complete**\n\nNo readable text found in this PDF. The document may contain:\n- Scanned images without OCR text\n- Protected/encrypted content\n- Graphics or charts without text\n- Non-standard text encoding\n\n**File:** ${file.name}\n**Pages:** ${pdf.numPages}\n**Size:** ${(file.size / 1024 / 1024).toFixed(2)} MB`;
      }
      
      // Format the extracted content
      const formattedContent = `# PDF Document: ${file.name}

**Document Information:**
- **File Size:** ${(file.size / 1024 / 1024).toFixed(2)} MB
- **Total Pages:** ${pdf.numPages}
- **Extracted:** ${new Date().toLocaleString()}

---

${fullText.trim()}

---

*Text extracted from PDF document*`;
      
      return formattedContent;
      
    } catch (error) {
      console.error('Error parsing PDF:', error);
      return `**PDF Parsing Error**

Failed to extract text from PDF: ${error instanceof Error ? error.message : 'Unknown error'}

**File:** ${file.name}
**Size:** ${(file.size / 1024 / 1024).toFixed(2)} MB

**Possible solutions:**
1. Try a different PDF file
2. Ensure the PDF contains readable text (not just scanned images)
3. Check if the PDF is password protected
4. Verify the file is not corrupted

*Note: This parser works best with text-based PDFs. Scanned documents may require OCR processing.*`;
    }
  };

  // Add webpage parsing function
  const parseWebpage = async (url: string): Promise<string> => {
    try {
      // Use a CORS proxy service to fetch the webpage content
      const proxyUrl = `https://api.allorigins.win/get?url=${encodeURIComponent(url)}`;
      const response = await fetch(proxyUrl);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      const htmlContent = data.contents;

      // Create a temporary DOM element to parse the HTML
      const parser = new DOMParser();
      const doc = parser.parseFromString(htmlContent, 'text/html');

      // Remove script and style elements
      const scripts = doc.querySelectorAll('script, style, nav, footer, header, .navigation, .nav, .menu, .sidebar, .advertisement, .ad');
      scripts.forEach(el => el.remove());

      // Try to find the main content area
      let contentElement = doc.querySelector('main') || 
                          doc.querySelector('article') || 
                          doc.querySelector('.content') || 
                          doc.querySelector('.post-content') || 
                          doc.querySelector('.entry-content') || 
                          doc.querySelector('#content') || 
                          doc.querySelector('.main-content') || 
                          doc.body;

      if (!contentElement) {
        contentElement = doc.body;
      }

      // Convert HTML to better formatted text while preserving structure
      let textContent = '';
      
      // Process each element to preserve paragraph structure
      const processElement = (element: Element): string => {
        let result = '';
        
        for (const node of Array.from(element.childNodes)) {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent?.trim();
            if (text) {
              result += text + ' ';
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const el = node as Element;
            const tagName = el.tagName.toLowerCase();
            
            switch (tagName) {
              case 'p':
              case 'div':
                const pContent = processElement(el).trim();
                if (pContent) {
                  result += '\n\n' + pContent + '\n\n';
                }
                break;
              case 'h1':
              case 'h2':
              case 'h3':
              case 'h4':
              case 'h5':
              case 'h6':
                const hContent = processElement(el).trim();
                if (hContent) {
                  const level = parseInt(tagName[1]);
                  const prefix = '#'.repeat(Math.min(level + 1, 6)); // Add 1 since we already have main title as #
                  result += '\n\n' + prefix + ' ' + hContent + '\n\n';
                }
                break;
              case 'br':
                result += '\n';
                break;
              case 'li':
                const liContent = processElement(el).trim();
                if (liContent) {
                  result += '\n• ' + liContent;
                }
                break;
              case 'ul':
              case 'ol':
                const listContent = processElement(el);
                if (listContent.trim()) {
                  result += '\n' + listContent + '\n';
                }
                break;
              case 'blockquote':
                const quoteContent = processElement(el).trim();
                if (quoteContent) {
                  result += '\n\n> ' + quoteContent.replace(/\n/g, '\n> ') + '\n\n';
                }
                break;
              case 'strong':
              case 'b':
                const strongContent = processElement(el).trim();
                if (strongContent) {
                  result += '**' + strongContent + '**';
                }
                break;
              case 'em':
              case 'i':
                const emContent = processElement(el).trim();
                if (emContent) {
                  result += '*' + emContent + '*';
                }
                break;
              case 'a':
                const linkContent = processElement(el).trim();
                const href = el.getAttribute('href');
                if (linkContent && href) {
                  result += '[' + linkContent + '](' + href + ')';
                } else if (linkContent) {
                  result += linkContent;
                }
                break;
              default:
                // For other elements, just process their content
                result += processElement(el);
                break;
            }
          }
        }
        
        return result;
      };

      textContent = processElement(contentElement);

      // Clean up the text
      textContent = textContent
        .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newlines
        .replace(/[ \t]+/g, ' ') // Replace multiple spaces/tabs with single space, but preserve newlines
        .replace(/\n /g, '\n') // Remove spaces after newlines
        .replace(/ \n/g, '\n') // Remove spaces before newlines
        .replace(/^\n+|\n+$/g, '') // Remove leading/trailing newlines
        .trim();

      // Get the page title
      const titleElement = doc.querySelector('title');
      const pageTitle = titleElement ? titleElement.textContent?.trim() : 'Parsed Article';

      // Format as an article
      const formattedContent = `# ${pageTitle}

**Source:** [${url}](${url})

---

${textContent}

---

*Article parsed from webpage content*`;

      return formattedContent;

    } catch (error) {
      console.error('Error parsing webpage:', error);
      return `Error parsing webpage: ${error instanceof Error ? error.message : 'Unknown error'}

The webpage might not be accessible due to CORS restrictions or other issues.

**URL:** ${url}

You can try:
1. Checking if the URL is accessible
2. Using a different URL
3. Copying the content manually`;
    }
  };

  // Update the web search function to be more direct with OpenAI
  const performWebSearch = async (query: string): Promise<WebSearchResult[]> => {
    console.log('Performing web search for:', query);
    setIsSearching(true);
    
    try {
      // Alternative 1: Try to use a more reliable free search API
      try {
        const response = await fetch(`https://api.duckduckgo.com/?q=${encodeURIComponent(query)}&format=json&no_html=1&skip_disambig=1`);
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.RelatedTopics && data.RelatedTopics.length > 0) {
            return data.RelatedTopics.slice(0, 5).map((result: any) => ({
              title: result.Text || "Search Result",
              url: result.FirstURL || "#",
              snippet: result.Text || ""
            }));
          }
        }
      } catch (err) {
        console.log('Primary search API failed, trying fallback');
      }
      
      // Alternative 2: Try another free API
      try {
        const response = await fetch(`https://en.wikipedia.org/w/api.php?action=opensearch&search=${encodeURIComponent(query)}&limit=5&namespace=0&format=json&origin=*`);
        
        if (response.ok) {
          const data = await response.json();
          if (data && data.length > 1) {
            const titles = data[1];
            const urls = data[3];
            
            return titles.map((title: string, index: number) => ({
              title: title,
              url: urls[index] || "#",
              snippet: `Wikipedia result for ${title}`
            }));
          }
        }
      } catch (err) {
        console.log('Secondary search API failed');
      }
      
      // If all searches fail, return an empty array
      console.log('All external search APIs failed');
      return [];
    } catch (error) {
      console.error('Error during web search:', error);
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Update the response generation to work better with or without search results
  const generateWebSearchResponse = async (cardId: number, searchQuery: string) => {
    if (!openAIKey) {
      return `Please set up your OpenAI API key to use the web search feature.`;
    }
    
    setGeneratingCardId(cardId);
    
    // Get connected cards for additional context
    const connectedCards = getConnectedCards(cardId);
    
    // Create a new card immediately with a loading message
    const responseCardId = Date.now();
    const card = cards.find(c => c.id === cardId);
    const newCard: Card = {
      id: responseCardId,
      title: `Search: ${truncateTitle(searchQuery)}`,
      content: "Searching the web...",
      position: {
        x: card ? card.position.x + 260 : 100,
        y: card ? card.position.y : 100,
      },
      conversationHistory: [searchQuery],
      width: 300,
      height: 350,
      model: selectedModel,
    };
    
    // Add the card immediately
    setCards(prevCards => [...prevCards, newCard]);
    setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: responseCardId }]);
    
    try {
      // First update - searching...
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: "Searching the web for results..." }
            : card
        )
      );
      
      // Try to perform the search
      const searchResults = await performWebSearch(searchQuery);
      const hasSearchResults = searchResults.length > 0;
      
      // Second update - found results
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { 
                ...card, 
                content: hasSearchResults 
                  ? `Found ${searchResults.length} results for "${searchQuery}".\n\nGenerating summary...` 
                  : `No direct search results found for "${searchQuery}".\n\nGenerating response from AI knowledge...`
              }
            : card
        )
      );
      
      // Prepare system message with clear instructions
      const systemMessage = `You are a helpful assistant that helps users with web searches. 
When responding to the user:
1. If search results are provided, use them to inform your response
2. Include relevant URLs from search results when available
3. If no search results are provided, acknowledge that you don't have real-time web access
4. Provide your best response based on your training data
5. Be clear about what you know and don't know`;
      
      // Prepare user message differently based on whether we have search results
      let userMessage = '';
      
      // Add connected cards context if available
      const connectedContext = connectedCards.length > 0 ? 
        `\n\nAdditional Context from Connected Cards:\n${connectedCards.map((connectedCard, index) => 
          `Connected Card ${index + 1}: ${connectedCard.content}`
        ).join('\n\n')}\n\n` : '';
      
      if (hasSearchResults) {
        // Format search results nicely
        const formattedResults = searchResults.map((result, index) => 
          `[${index + 1}] ${result.title}\nURL: ${result.url}\n${result.snippet}`
        ).join('\n\n');
        
        userMessage = `I searched for: "${searchQuery}"${connectedContext}Here are the search results:

${formattedResults}

Please provide a helpful response based on these search results and any additional context provided. Include relevant URLs when appropriate.`;
      } else {
        // No search results, so just ask directly
        userMessage = `I'm looking for information about: "${searchQuery}"${connectedContext}Please provide the best information you have based on your knowledge and any additional context provided. Note that I understand you don't have live web browsing capability, so please let me know if this is something that would require more recent information than you might have.`;
      }
      
      // Set up streaming for the response
      setStreamingCardId(responseCardId);
      setStreamingContent('');
      setStreamingDone(false);
      
      // Call OpenAI API with streaming
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAIKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [
            { role: 'system', content: systemMessage },
            { role: 'user', content: userMessage }
          ],
          temperature: 0.7,
          stream: true
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI API error ${response.status}: ${errorData.error?.message || 'Unknown error'}`);
      }

      // Process the stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n').filter(line => line.trim() !== '');
          
          for (const line of lines) {
            if (line.includes('[DONE]')) continue;
            if (line.startsWith('data: ')) {
              const jsonString = line.slice(6);
              if (jsonString === '[DONE]') continue;
              
              try {
                const json = JSON.parse(jsonString);
                const delta = json.choices[0]?.delta?.content || '';
                if (delta) {
                  fullContent += delta;
                  setStreamingContent(fullContent);
                  
                  // Update the card's content in real-time
                  setCards(prevCards => 
                    prevCards.map(card => 
                      card.id === responseCardId 
                        ? { ...card, content: fullContent }
                        : card
                    )
                  );
                }
              } catch (e) {
                console.error('Error parsing streaming JSON:', e);
              }
            }
          }
        }
      }
      
      // Final content update
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: fullContent, conversationHistory: [searchQuery, fullContent] }
            : card
        )
      );
      
      setStreamingDone(true);
      setGeneratedCardIds(prev => new Set(prev).add(responseCardId));
      
      return fullContent;
    } catch (error) {
      console.error('Error generating web search response:', error);
      
      // Update the card with the error message
      const errorMessage = `Error during web search: ${error instanceof Error ? error.message : 'Unknown error occurred'}
      
To use the search feature:
1. Make sure you have a valid OpenAI API key in the configuration
2. Check that your API key has enough usage credits
3. Try again with a different search query`;

      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: errorMessage }
            : card
        )
      );
      
      return errorMessage;
    } finally {
      setGeneratingCardId(null);
      setStreamingCardId(null);
    }
  };

  // Add new function for generating interactive elements
  const generateInteractiveElement = async (cardId: number, prompt: string) => {
    const card = cards.find(c => c.id === cardId);
    const modelToUse = card?.model || selectedModel;
    
    // Check if we have the necessary API key for the selected model
    if (modelToUse === 'openai' && !openAIKey) {
      return `Please set up your OpenAI API key to generate interactive elements.`;
    } else if (modelToUse === 'deepseek' && !deepSeekKey) {
      return `Please set up your DeepSeek API key to generate interactive elements.`;
    } else if (modelToUse === 'claude-sonnet-4-20250514' && !claudeKey) {
      return `Please set up your Claude API key to generate interactive elements.`;
    }
    
    setGeneratingCardId(cardId);
    
    // Create the interactive element card
    const interactiveCardId = Date.now();
    const interactiveCard: Card = {
      id: interactiveCardId,
      title: `Interactive: ${truncateTitle(prompt)}`,
      content: "Generating interactive element...",
      position: {
        x: card ? card.position.x + 260 : 100,
        y: card ? card.position.y : 100,
      },
      conversationHistory: [prompt],
      width: 400,
      height: 300,
      model: modelToUse,
      isInteractive: false, // Will be set to true once generated
    };
    
    // Add the interactive card
    setCards(prevCards => [...prevCards, interactiveCard]);
    setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: interactiveCardId }]);
    
    try {
      // Create a comprehensive prompt for generating interactive HTML/CSS/JS
      const interactivePrompt = `Create a complete, self-contained HTML page with embedded CSS and JavaScript for: ${prompt}

Requirements:
1. Write a complete HTML document with <!DOCTYPE html>, <html>, <head>, and <body> tags
2. Include all CSS in a <style> tag in the <head>
3. Include all JavaScript in a <script> tag before closing </body>
4. Make it interactive and engaging
5. Use modern CSS features like flexbox/grid for layout
6. Add smooth animations and transitions
7. Make it responsive and mobile-friendly
8. Use semantic HTML elements
9. Include comments in the code explaining key parts
10. Ensure the element is fully functional and complete

The HTML should be ready to render directly in a browser. Make it visually appealing with good UX design principles.

Example format:
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Interactive Element</title>
    <style>
        /* CSS goes here */
    </style>
</head>
<body>
    <!-- HTML content goes here -->
    <script>
        // JavaScript goes here
    </script>
</body>
</html>

Now create the interactive element for: ${prompt}`;
      
      // Set up streaming for the response
      setStreamingCardId(interactiveCardId);
      setStreamingContent('');
      setStreamingDone(false);
      
      // Generate the interactive element using the selected model
      let htmlContent: string;
      if (modelToUse === 'deepseek') {
        htmlContent = await generateDeepSeekResponse(cardId, interactivePrompt, false, interactiveCardId);
      } else if (modelToUse === 'claude-sonnet-4-20250514') {
        htmlContent = await generateClaudeResponse(cardId, interactivePrompt, false, interactiveCardId);
      } else {
        htmlContent = await generateChatGPTResponse(cardId, interactivePrompt, false, interactiveCardId);
      }
      
      // Extract HTML content from the response (remove markdown code blocks if present)
      let cleanHtml = htmlContent;
      const htmlMatch = htmlContent.match(/```html\s*([\s\S]*?)\s*```/) || htmlContent.match(/```\s*(<!DOCTYPE[\s\S]*?)\s*```/);
      if (htmlMatch) {
        cleanHtml = htmlMatch[1];
      } else if (htmlContent.includes('<!DOCTYPE html>')) {
        // If no code blocks but contains DOCTYPE, use as is
        const startIndex = htmlContent.indexOf('<!DOCTYPE html>');
        const endIndex = htmlContent.lastIndexOf('</html>') + 7;
        if (endIndex > startIndex) {
          cleanHtml = htmlContent.substring(startIndex, endIndex);
        }
      }
      
      // Final update with the interactive content
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === interactiveCardId
            ? { 
                ...card, 
                content: `Interactive element: ${prompt}`,
                htmlContent: cleanHtml,
                isInteractive: true,
                conversationHistory: [prompt, htmlContent],
                height: 400, // Make it larger for interactive content
                width: 500
              }
            : card
        )
      );
      
      setGeneratedCardIds(prev => new Set(prev).add(interactiveCardId));
      
      return htmlContent;
    } catch (error) {
      console.error('Error during interactive element generation:', error);
      
      const errorMessage = `**Interactive Element Generation Error**
      
Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}

Interactive element generation requires:
1. A valid API key for the selected model (${modelToUse})
2. Sufficient API credits/usage allowance
3. A clear description of what you want to create

Please check your configuration and try again with a more specific prompt.

Examples:
- "/interactive a calculator with buttons"
- "/interactive a todo list with add/remove functionality"
- "/interactive a color picker with preview"`;

      setCards(prevCards =>
        prevCards.map(card =>
          card.id === interactiveCardId
            ? { ...card, content: errorMessage }
            : card
        )
      );
      
      return errorMessage;
    } finally {
      setGeneratingCardId(null);
      setStreamingCardId(null);
    }
  };

  // Add new function for deep research
  const generateDeepResearch = async (cardId: number, researchTopic: string) => {
    const card = cards.find(c => c.id === cardId);
    const modelToUse = card?.model || selectedModel;
    
    // Check if we have the necessary API key for the selected model
    if (modelToUse === 'openai' && !openAIKey) {
      return `Please set up your OpenAI API key to use deep research.`;
    } else if (modelToUse === 'deepseek' && !deepSeekKey) {
      return `Please set up your DeepSeek API key to use deep research.`;
    } else if (modelToUse === 'claude-sonnet-4-20250514' && !claudeKey) {
      return `Please set up your Claude API key to use deep research.`;
    }
    
    setGeneratingCardId(cardId);
    
    // Get connected cards for additional context
    const connectedCards = getConnectedCards(cardId);
    
    // Create the main research report card
    const reportCardId = Date.now();
    const reportCard: Card = {
      id: reportCardId,
      title: `Deep Research: ${truncateTitle(researchTopic)}`,
      content: "Initializing deep research...",
      position: {
        x: card ? card.position.x + 260 : 100,
        y: card ? card.position.y : 100,
      },
      conversationHistory: [researchTopic],
      width: 400,
      height: 500,
      model: modelToUse,
    };
    
    // Add the main research card
    setCards(prevCards => [...prevCards, reportCard]);
    setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: reportCardId }]);
    
    try {
      // Step 1: Generate research subtopics and questions
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === reportCardId
            ? { ...card, content: "**Phase 1: Generating Research Framework**\n\nAnalyzing the topic and creating comprehensive research questions..." }
            : card
        )
      );
      
      // Build context from connected cards
      const connectedContext = connectedCards.length > 0 ? 
        `\n\nAdditional Context from Connected Cards:\n${connectedCards.map((connectedCard, index) => 
          `Connected Card ${index + 1}: ${connectedCard.content}`
        ).join('\n\n')}\n\n` : '';

      const subtopicsPrompt = `You are a research assistant. For the topic "${researchTopic}", generate 4-5 specific research questions or subtopics that would provide comprehensive coverage of this subject.${connectedContext}Format your response as a simple numbered list:
1. [First research question/subtopic]
2. [Second research question/subtopic]
3. [Third research question/subtopic]
4. [Fourth research question/subtopic]
5. [Fifth research question/subtopic]

Keep each question focused and specific enough to search for online. Consider any additional context provided above when formulating the research questions.`;

      // Generate research questions using the selected model
      let subtopicsResponse: string;
      if (modelToUse === 'deepseek') {
        subtopicsResponse = await generateDeepSeekResponse(cardId, subtopicsPrompt, false, reportCardId);
      } else if (modelToUse === 'claude-sonnet-4-20250514') {
        subtopicsResponse = await generateClaudeResponse(cardId, subtopicsPrompt, false, reportCardId);
      } else {
        subtopicsResponse = await generateChatGPTResponse(cardId, subtopicsPrompt, false, reportCardId);
      }
      
      // Extract research questions from the response
      const researchQuestions = subtopicsResponse
        .split('\n')
        .filter(line => line.match(/^\d+\./))
        .map(line => line.replace(/^\d+\.\s*/, '').trim())
        .filter(q => q.length > 0)
        .slice(0, 5); // Limit to 5 questions max

      // Step 2: Perform web searches for each research question
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === reportCardId
            ? { ...card, content: `**Phase 1: Research Framework Complete**\n\nGenerated ${researchQuestions.length} research questions:\n${researchQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n')}\n\n**Phase 2: Gathering Information**\n\nSearching for comprehensive information...` }
            : card
        )
      );
      
      // Perform searches for each research question (only if OpenAI is available for search)
      const allSearchResults: { question: string; results: WebSearchResult[] }[] = [];
      
      if (modelToUse === 'openai' && openAIKey) {
        for (let i = 0; i < researchQuestions.length; i++) {
          const question = researchQuestions[i];
          setCards(prevCards =>
            prevCards.map(card =>
              card.id === reportCardId
                ? { ...card, content: `**Phase 1: Research Framework Complete**\n\nGenerated ${researchQuestions.length} research questions:\n${researchQuestions.map((q, j) => `${j + 1}. ${q}`).join('\n')}\n\n**Phase 2: Gathering Information**\n\nSearching question ${i + 1} of ${researchQuestions.length}: "${question.substring(0, 50)}..."` }
                : card
            )
          );
          
          const searchResults = await performWebSearch(question);
          allSearchResults.push({ question, results: searchResults });
          
          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }
      
      // Step 3: Compile comprehensive research report
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === reportCardId
            ? { ...card, content: `**Phase 1: Research Framework Complete**\n\n**Phase 2: Information Gathering Complete**\n\n**Phase 3: Synthesizing Research Report**\n\nAnalyzing findings and creating comprehensive report...` }
            : card
        )
      );
      
      // Create comprehensive synthesis prompt
      let synthesisPrompt = `You are conducting a deep research analysis on: "${researchTopic}"${connectedContext}Research Questions Investigated:
${researchQuestions.map((q, i) => `${i + 1}. ${q}`).join('\n')}
`;

      if (allSearchResults.length > 0) {
        synthesisPrompt += `\nWeb Search Results:\n`;
        allSearchResults.forEach(({ question, results }, index) => {
          synthesisPrompt += `\n--- Research Question ${index + 1}: ${question} ---\n`;
          if (results.length > 0) {
            results.forEach((result, i) => {
              synthesisPrompt += `Source ${i + 1}: ${result.title}\nURL: ${result.url}\nSummary: ${result.snippet}\n\n`;
            });
          } else {
            synthesisPrompt += `No specific web results found for this question.\n\n`;
          }
        });
        
        synthesisPrompt += `\nPlease create a comprehensive research report that:
1. Provides an executive summary
2. Addresses each research question with detailed analysis
3. Synthesizes information from multiple sources
4. Includes relevant URLs where available
5. Identifies key insights and conclusions
6. Suggests areas for further research

Format the report in clear markdown with proper headings and structure.`;
      } else {
        synthesisPrompt += `\nSince web search is not available, please create a comprehensive research report based on your knowledge that:
1. Provides an executive summary of the topic
2. Addresses each research question with detailed analysis
3. Covers the main aspects, theories, and current understanding
4. Identifies key insights and important considerations
5. Suggests areas where current/live data would be beneficial
6. Notes any limitations due to knowledge cutoff

Format the report in clear markdown with proper headings and structure.`;
      }
      
      // Set up streaming for the final report
      setStreamingCardId(reportCardId);
      setStreamingContent('');
      setStreamingDone(false);
      
      // Generate the comprehensive report using the selected model
      let finalReport: string;
      if (modelToUse === 'deepseek') {
        finalReport = await generateDeepSeekResponse(cardId, synthesisPrompt, false, reportCardId);
      } else if (modelToUse === 'claude-sonnet-4-20250514') {
        finalReport = await generateClaudeResponse(cardId, synthesisPrompt, false, reportCardId);
      } else {
        finalReport = await generateChatGPTResponse(cardId, synthesisPrompt, false, reportCardId);
      }
      
      // Final update with the complete report
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === reportCardId
            ? { 
                ...card, 
                content: finalReport,
                conversationHistory: [researchTopic, finalReport],
                height: 600 // Make it larger for the comprehensive report
              }
            : card
        )
      );
      
      setGeneratedCardIds(prev => new Set(prev).add(reportCardId));
      
      return finalReport;
    } catch (error) {
      console.error('Error during deep research:', error);
      
      const errorMessage = `**Deep Research Error**
      
Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}

Deep research requires:
1. A valid API key for the selected model (${modelToUse})
2. Sufficient API credits/usage allowance
3. Stable internet connection for web searches (when using OpenAI)

Please check your configuration and try again.`;

      setCards(prevCards =>
        prevCards.map(card =>
          card.id === reportCardId
            ? { ...card, content: errorMessage }
            : card
        )
      );
      
      return errorMessage;
    } finally {
      setGeneratingCardId(null);
      setStreamingCardId(null);
    }
  };

  // Modify the handleAddClick function
  const handleAddClick = (cardId: number) => {
    const card = cards.find(c => c.id === cardId);
    
    if (card) {
      const newCard: Card = {
        id: Date.now(),
        title: truncateTitle(card.content), // Use the parent card's content as the title
        content: "",
        position: {
          x: card.position.x + 260,
          y: card.position.y,
        },
        conversationHistory: [...card.conversationHistory, card.content],
      };
      setCards(prevCards => [...prevCards, newCard]);
      setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: newCard.id }]);
      setPendingInputCards(prev => new Set(prev).add(newCard.id));
      setEditingCardId(newCard.id);
    }
  };

  // Add this new function to get the full conversation history
  const getFullConversationHistory = (cardId: number): string[] => {
    const history: string[] = [];
    let currentCardId: number | null = cardId;

    while (currentCardId !== null) {
      const currentCard = cards.find(c => c.id === currentCardId);
      if (currentCard) {
        history.unshift(currentCard.content);
        const parentLink = links.find(link => link.toId === currentCardId);
        currentCardId = parentLink ? parentLink.fromId : null;
      } else {
        break;
      }
    }

    return history;
  };

  // Modify the renderLinks function for culling
  const renderLinks = () => {
    return links.map((link, index) => {
      const fromCard = cards.find(c => c.id === link.fromId);
      const fromImage = images.find(i => i.id === link.fromId);
      const toCard = cards.find(c => c.id === link.toId);
      const toImage = images.find(i => i.id === link.toId);
      
      if ((!fromCard && !fromImage) || (!toCard && !toImage)) return null;

      // Get source and destination positions
      let fromPos, toPos, fromSize, toSize;
      
      if (fromCard) {
        fromPos = fromCard.position;
        fromSize = { width: fromCard.width || 250, height: fromCard.height || 100 };
      } else if (fromImage) {
        fromPos = fromImage.position;
        const imageElement = document.querySelector(`img[src="${fromImage.url}"]`) as HTMLImageElement;
        fromSize = { width: imageElement?.width || 300, height: imageElement?.height || 300 };
      } else return null;

      if (toCard) {
        toPos = toCard.position;
        toSize = { width: toCard.width || 250, height: toCard.height || 100 };
      } else if (toImage) {
        toPos = toImage.position;
        const imageElement = document.querySelector(`img[src="${toImage.url}"]`) as HTMLImageElement;
        toSize = { width: imageElement?.width || 300, height: imageElement?.height || 300 };
      } else return null;

      // Cull links that are completely outside viewport
      if (!isElementInViewport(fromPos, fromSize.width, fromSize.height) &&
          !isElementInViewport(toPos, toSize.width, toSize.height)) {
        return null;
      }

      const linkKey = `${link.fromId}-${link.toId}`;

      // Get source coordinates
      let fromX: number, fromY: number;
      if (fromCard) {
        fromX = fromCard.position.x + (fromCard.width || 250) / 2;
        fromY = fromCard.position.y + (fromCard.height || 100) / 2;
      } else if (fromImage) {
        const imageElement = document.querySelector(`img[src="${fromImage.url}"]`) as HTMLImageElement;
        const width = imageElement?.width || 300;
        const height = imageElement?.height || 300;
        fromX = fromImage.position.x + width / 2;
        fromY = fromImage.position.y + height / 2;
      } else {
        return null;
      }

      // Get destination coordinates
      let toX: number, toY: number;
      if (toCard) {
        toX = toCard.position.x + (toCard.width || 250) / 2;
        toY = toCard.position.y + (toCard.height || 100) / 2;
      } else if (toImage) {
        const imageElement = document.querySelector(`img[src="${toImage.url}"]`) as HTMLImageElement;
        const width = imageElement?.width || 300;
        const height = imageElement?.height || 300;
        toX = toImage.position.x + width / 2;
        toY = toImage.position.y + height / 2;
      } else {
        return null;
      }

      if (!staticRectangles[linkKey]) {
        const distance = Math.sqrt(Math.pow(toX - fromX, 2) + Math.pow(toY - fromY, 2));
        const squareCount = Math.max(Math.floor(distance / 10), 10);

        const squares = Array.from({ length: squareCount }, (_, i) => {
          const t = i / (squareCount - 1);
          return {
            t,
            size: Math.random() * 4 + 2,
            color: `hsl(${Math.random() * 360}, 70%, 70%)`,
            rotation: Math.random() * 45,
          };
        });

        setStaticRectangles(prev => ({...prev, [linkKey]: squares}));
      }

      const squares = staticRectangles[linkKey] || [];

      return (
        <svg key={index} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
          {squares.map((square, sqIndex) => {
            const x = fromX + (toX - fromX) * square.t;
            const y = fromY + (toY - fromY) * square.t;
            return (
              <rect
                key={sqIndex}
                x={x - square.size / 2}
                y={y - square.size / 2}
                width={square.size}
                height={square.size}
                fill={square.color}
                transform={`rotate(${square.rotation} ${x} ${y})`}
              />
            );
          })}
        </svg>
      );
    });
  };

  // Add this helper function to get connected cards
  const getConnectedCards = (cardId: number): Card[] => {
    // Get cards connected by links (both directions)
    const connectedLinks = links.filter(link => 
      link.fromId === cardId || link.toId === cardId
    );
    
    return connectedLinks.map(link => {
      const connectedId = link.fromId === cardId ? link.toId : link.fromId;
      return cards.find(card => card.id === connectedId);
    }).filter((card): card is Card => card !== undefined);
  };

  // Modify handleGenClick to create a single response card for syntheses
  const handleGenClick = async (cardId: number) => {
    console.log(`handleGenClick called for cardId: ${cardId} using ${selectedModel}`);
    const card = cards.find(c => c.id === cardId);
    if (!card) return;

    // Special handling for interactive cards - regenerate the interactive element
    if (card.isInteractive && card.conversationHistory.length > 0) {
      const originalPrompt = card.conversationHistory[0];
      console.log("Regenerating interactive card with prompt:", originalPrompt);
      await generateInteractiveElement(cardId, originalPrompt);
      return;
    }

    // Get connected cards
    const connectedCards = getConnectedCards(cardId);
    
    setGeneratingCardId(cardId);
    setStreamingCardId(cardId);
    setStreamingContent('');
    setStreamingDone(false);
    
    // Build prompt combining content from connected cards
    let prompt = card.content;
    if (connectedCards.length > 0) {
      const connectedContext = connectedCards.map((connectedCard, index) => 
        `Connected Card ${index + 1}: ${connectedCard.content}`
      ).join('\n\n');
      prompt = `Main Card Content:\n${card.content}\n\nConnected Cards Context:\n${connectedContext}\n\nPlease synthesize this information and provide a comprehensive response that considers all the connected content.`;
    }
    
    // Create new synthesis card with "Generating..." placeholder
    const responseCardId = Date.now();
    const newCard: Card = {
      id: responseCardId,
      title: 'Synthesis',
      content: "Generating synthesis...",
      position: {
        x: card.position.x + 260,
        y: card.position.y,
      },
      conversationHistory: [prompt],
      width: 300,
      height: 200,
      model: card.model || selectedModel,
    };
    
    // Add the new card immediately
    setCards(prevCards => [...prevCards, newCard]);
    
    // Create links from all source cards to the new card
    setLinks(prevLinks => [
      ...prevLinks,
      { fromId: cardId, toId: newCard.id },
      ...connectedCards.map(c => ({ fromId: c.id, toId: newCard.id }))
    ]);
    
    try {
      // Get the model to use
      const modelToUse = card.model || selectedModel;
      
      // Generate the response
      let generatedContent: string;
      if (modelToUse === 'deepseek') {
        console.log("Explicitly calling DeepSeek API for synthesis");
        generatedContent = await generateDeepSeekResponse(cardId, prompt, false, responseCardId);
      } else if (modelToUse === 'claude-sonnet-4-20250514') {
        console.log("Explicitly calling Claude API for synthesis");
        generatedContent = await generateClaudeResponse(cardId, prompt, false, responseCardId);
      } else {
        console.log("Explicitly calling OpenAI API for synthesis");
        generatedContent = await generateChatGPTResponse(cardId, prompt, false, responseCardId);
      }
      
      // Final update to the response card (though it should already be updated via streaming)
      setCards(prevCards => 
        prevCards.map(c => 
          c.id === responseCardId 
            ? { 
                ...c, 
                content: generatedContent,
                conversationHistory: [prompt, generatedContent],
              }
            : c
        )
      );
      setGeneratedCardIds(prev => new Set(prev).add(responseCardId));
    } catch (error) {
      console.error("Error generating synthesis:", error);
      // If generation failed, update the new card with the error
      setCards(prevCards => 
        prevCards.map(c => 
          c.id === responseCardId 
            ? { ...c, content: `Error generating synthesis: ${error instanceof Error ? error.message : 'Unknown error'}` }
            : c
        )
      );
    } finally {
      setGeneratingCardId(null);
      setStreamingCardId(null);
    }
  };

  // Add a helper function to handle API selection
  const generateResponse = async (cardId: number, prompt: string): Promise<string> => {
    const card = cards.find(c => c.id === cardId);
    const modelToUse = card?.model || selectedModel;
    console.log(`Generating response for cardId: ${cardId} using ${modelToUse}`);
    
    if (modelToUse === 'deepseek') {
      return generateDeepSeekResponse(cardId, prompt, false);
    } else if (modelToUse === 'claude-sonnet-4-20250514') {
      return generateClaudeResponse(cardId, prompt, false);
    } else {
      return generateChatGPTResponse(cardId, prompt, false);
    }
  };

  // Add this new function to handle burst generation
  const handleBurst = async (cardId: number) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) return;

    const prompt = card.content;
    if (!prompt.trim()) return;

    const models = ['openai', 'deepseek', 'claude-sonnet-4-20250514'];
    const modelNames = ['OpenAI', 'DeepSeek', 'Claude'];
    
    // Create cards for each model
    const burstCards: Card[] = [];
    const burstLinks: Link[] = [];
    
    models.forEach((model, index) => {
      const responseCardId = Date.now() + index;
      const newCard: Card = {
        id: responseCardId,
        title: `${modelNames[index]} Response`,
        content: "Generating...",
        position: {
          x: card.position.x + 300 + (index * 260),
          y: card.position.y + (index * 50),
        },
        conversationHistory: [prompt],
        width: 280,
        height: 200,
        model: model,
      };
      
      burstCards.push(newCard);
      burstLinks.push({ fromId: cardId, toId: responseCardId });
    });
    
    // Add all cards and links immediately
    setCards(prevCards => [...prevCards, ...burstCards]);
    setLinks(prevLinks => [...prevLinks, ...burstLinks]);
    
    // Generate responses for each model
    burstCards.forEach(async (burstCard, index) => {
      const model = models[index];
      
      try {
        let generatedContent: string;
        if (model === 'deepseek') {
          generatedContent = await generateDeepSeekResponse(cardId, prompt, false, burstCard.id);
        } else if (model === 'claude-sonnet-4-20250514') {
          generatedContent = await generateClaudeResponse(cardId, prompt, false, burstCard.id);
        } else {
          generatedContent = await generateChatGPTResponse(cardId, prompt, false, burstCard.id);
        }
        
        // Update the card with generated content
        setCards(prevCards =>
          prevCards.map(c =>
            c.id === burstCard.id
              ? { ...c, content: generatedContent, conversationHistory: [prompt, generatedContent] }
              : c
          )
        );
        
        setGeneratedCardIds(prev => new Set(prev).add(burstCard.id));
      } catch (error) {
        console.error(`Error generating ${model} response:`, error);
        setCards(prevCards =>
          prevCards.map(c =>
            c.id === burstCard.id
              ? { ...c, content: `Error generating ${modelNames[index]} response: ${error instanceof Error ? error.message : 'Unknown error'}` }
              : c
          )
        );
      }
    });
  };

  // Modify the API functions to accept a target card ID
  const generateDeepSeekResponse = async (cardId: number, prompt: string, updateExisting: boolean = false, targetCardId?: number): Promise<string> => {
    console.log('generateDeepSeekResponse called for cardId:', cardId);
    console.log('Prompt:', prompt);
    
    // Check if the source card has image data
    const sourceCard = cards.find(c => c.id === cardId);
    if (sourceCard?.imageData) {
      return `**Image Vision Not Supported**

DeepSeek models don't currently support image analysis. To analyze this image, please:

1. Switch to OpenAI model (click the model button on the card)
2. Click Generate again

The image will be analyzed using OpenAI's vision capabilities.`;
    }
    
    if (!deepSeekKey) {
      console.log('No DeepSeek API key found');
      return `Please set up your DeepSeek API key first:
1. Click the Config button in the bottom left
2. Enter your DeepSeek API key
3. Click Save
4. Try generating again

You can get an API key at: https://platform.deepseek.com/api_keys`;
    }

    setGeneratingCardId(cardId);
    setStreamingCardId(targetCardId || cardId);
    setStreamingContent('');
    setStreamingDone(false); // Make sure this is false at the start
    
    // Determine which card to update
    let responseCardId = updateExisting ? cardId : (targetCardId || Date.now());
    
    // If we need to create a new card and there's no targetCardId
    if (!updateExisting && !targetCardId) {
      // Create a new card first with empty content
      const card = cards.find(c => c.id === cardId);
      const newCard: Card = {
        id: responseCardId,
        content: '',
        position: {
          x: card ? card.position.x + 260 : 100,
          y: card ? card.position.y : 100,
        },
        conversationHistory: [prompt],
        width: 250,
        height: 200,
        model: selectedModel,
      };
      
      // Add the empty card immediately
      setCards(prevCards => [...prevCards, newCard]);
      // Create the link
      setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: responseCardId }]);
    }
    
    try {
      // First try the direct DeepSeek API
      try {
        console.log('Sending request to DeepSeek API...');
        const response = await fetch('https://api.deepseek.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${deepSeekKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: 'deepseek-chat',
            messages: [
              { role: 'system', content: 'You are DeepSeek Chat, an AI assistant by DeepSeek. Always identify yourself as DeepSeek Chat when asked about your identity.' },
              { role: 'user', content: prompt }
            ],
            temperature: 0.7,
            stream: true // Enable streaming
          })
        });

        if (!response.ok) {
          console.error('DeepSeek API response not OK:', response.status, response.statusText);
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        console.log('DeepSeek API streaming response started');
        
        // Process the stream
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();
        let fullContent = '';
        
        if (reader) {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            
            const chunk = decoder.decode(value);
            const lines = chunk.split('\n').filter(line => line.trim() !== '');
            
            for (const line of lines) {
              if (line.includes('[DONE]')) continue;
              if (line.startsWith('data: ')) {
                const jsonString = line.slice(6);
                if (jsonString === '[DONE]') continue;
                
                try {
                  const json = JSON.parse(jsonString);
                  const delta = json.choices[0]?.delta?.content || '';
                  if (delta) {
                    fullContent += delta;
                    setStreamingContent(fullContent);
                    
                    // Update the card's content in real-time
                    setCards(prevCards => 
                      prevCards.map(card => 
                        card.id === responseCardId 
                          ? { ...card, content: fullContent }
                          : card
                      )
                    );
                  }
                } catch (e) {
                  console.error('Error parsing streaming JSON:', e);
                }
              }
            }
          }
        }
        
        // Final content update
        setCards(prevCards =>
          prevCards.map(card =>
            card.id === responseCardId
              ? { ...card, content: fullContent, conversationHistory: [...(card.conversationHistory || []), fullContent] }
              : card
          )
        );
        
        setStreamingDone(true); // Set to true when completed
        setGeneratedCardIds(prev => new Set(prev).add(responseCardId));
        console.log('Streaming completed');
        return fullContent;
      } catch (directApiError) {
        // If the direct API fails, try DeepInfra as a fallback with non-streaming
        console.error('Error with direct DeepSeek API streaming call:', directApiError);
        console.log('Attempting fallback to DeepInfra (non-streaming)...');
        
        const deepInfraResponse = await fetch('https://api.deepinfra.com/v1/openai/chat/completions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${deepSeekKey}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: 'deepseek-ai/DeepSeek-V3',
            messages: [
              { role: 'system', content: 'You are DeepSeek Chat, an AI assistant by DeepSeek. Always identify yourself as DeepSeek Chat when asked about your identity.' },
              { role: 'user', content: prompt }
            ],
            temperature: 0.7
          })
        });
        
        if (!deepInfraResponse.ok) {
          throw new Error(`DeepInfra fallback failed with status: ${deepInfraResponse.status}`);
        }
        
        const fallbackData = await deepInfraResponse.json();
        const content = fallbackData.choices[0].message.content;
        
        // Update the card with the final content
        setCards(prevCards =>
          prevCards.map(card =>
            card.id === responseCardId
              ? { ...card, content: content, conversationHistory: [...(card.conversationHistory || []), content] }
              : card
          )
        );
        
        setStreamingDone(true);
        setGeneratedCardIds(prev => new Set(prev).add(responseCardId));
        return content;
      }
    } catch (error) {
      console.error('All DeepSeek API attempts failed:', error);
      
      // Update the card with the error message
      const errorMessage = `Error: Failed to generate response from DeepSeek.

This might be due to CORS restrictions. To use DeepSeek in a browser app, you need to either:

1. Use a different model like OpenAI which supports browser requests
2. Set up a server-side proxy for DeepSeek API calls

For now, please try clicking the model button to switch to OpenAI.`;

      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: errorMessage }
            : card
        )
      );
      
      return errorMessage;
    } finally {
      setGeneratingCardId(null);
      setStreamingCardId(null);
    }
  };

  // Modify the generateChatGPTResponse function to accept a target card ID
  const generateChatGPTResponse = async (cardId: number, prompt: string, updateExisting: boolean = false, targetCardId?: number): Promise<string> => {
    console.log('generateChatGPTResponse called for cardId:', cardId);
    console.log('Prompt:', prompt);
    
    if (!openAIKey) {
      console.log('No OpenAI API key found');
      return `Please set up your OpenAI API key first:
1. Click the Config button in the bottom left
2. Enter your OpenAI API key
3. Click Save
4. Try generating again

You can get an API key at: https://platform.openai.com/api-keys`;
    }

    setGeneratingCardId(cardId);
    setStreamingCardId(targetCardId || cardId);
    setStreamingContent('');
    setStreamingDone(false); // Make sure this is false at the start
    
    // Determine which card to update
    let responseCardId = updateExisting ? cardId : (targetCardId || Date.now());
    
    // If we need to create a new card and there's no targetCardId
    if (!updateExisting && !targetCardId) {
      // Create a new card first with empty content
      const card = cards.find(c => c.id === cardId);
      const newCard: Card = {
        id: responseCardId,
        content: '',
        position: {
          x: card ? card.position.x + 260 : 100,
          y: card ? card.position.y : 100,
        },
        conversationHistory: [prompt],
        width: 250,
        height: 200,
        model: selectedModel,
      };
      
      // Add the empty card immediately
      setCards(prevCards => [...prevCards, newCard]);
      // Create the link
      setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: responseCardId }]);
    }
    
    try {
      console.log('Sending request to OpenAI API...');
      
      // Check if the source card has image data for vision
      const sourceCard = cards.find(c => c.id === cardId);
      const hasImage = sourceCard?.imageData;
      
      let messages;
      let model = 'gpt-4o-mini';
      
      if (hasImage) {
        // Use vision model and format for image + text
        model = 'gpt-4o-mini';
        console.log('Using vision model with image data');
        
        // Preserve the original image format from the data URL
        const imageDataUrl = sourceCard.imageData!;
        messages = [
          {
            role: 'user',
            content: [
              { type: 'text', text: prompt },
              {
                type: 'image_url',
                image_url: {
                  url: imageDataUrl
                }
              }
            ]
          }
        ];
        console.log('Vision messages prepared:', messages);
      } else {
        // Regular text-only conversation
        messages = [
          { role: 'system', content: 'You are OpenAI\'s GPT-4o model. Always identify yourself as GPT-4o when asked about your identity.' },
          { role: 'user', content: prompt }
        ];
      }
      
      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAIKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model,
          messages,
          temperature: 0.7,
          stream: true,
          max_tokens: hasImage ? 300 : undefined // Set token limit for vision requests
        })
      });

      if (!response.ok) {
        console.error('OpenAI API response not OK:', response.status, response.statusText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('OpenAI API streaming response started');
      
      // Process the stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n').filter(line => line.trim() !== '');
          
          for (const line of lines) {
            if (line.includes('[DONE]')) continue;
            if (line.startsWith('data: ')) {
              const jsonString = line.slice(6);
              if (jsonString === '[DONE]') continue;
              
              try {
                const json = JSON.parse(jsonString);
                const delta = json.choices[0]?.delta?.content || '';
                if (delta) {
                  fullContent += delta;
                  setStreamingContent(fullContent);
                  
                  // Update the card's content in real-time
                  setCards(prevCards => 
                    prevCards.map(card => 
                      card.id === responseCardId 
                        ? { ...card, content: fullContent }
                        : card
                    )
                  );
                }
              } catch (e) {
                console.error('Error parsing streaming JSON:', e);
              }
            }
          }
        }
      }
      
      // Final content update
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: fullContent, conversationHistory: [...(card.conversationHistory || []), fullContent] }
            : card
        )
      );
      
      setStreamingDone(true); // Set to true when completed
      setGeneratedCardIds(prev => new Set(prev).add(responseCardId));
      console.log('Streaming completed');
      return fullContent;
    } catch (error) {
      console.error('Error calling OpenAI:', error);
      if (error instanceof Error) {
        console.error('Error stack:', error.stack);
      }
      
      // Update the card with the error message
      const errorMessage = `Error: Failed to generate response. ${error instanceof Error ? error.message : 'Unknown error occurred'}`;
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: errorMessage }
            : card
        )
      );
      
      return errorMessage;
    } finally {
      console.log('Clearing generatingCardId and streamingCardId');
      setGeneratingCardId(null);
      setStreamingCardId(null);
    }
  };

  // Add this new function near your other generate functions
  const generateImageQuestion = async (imageId: number, question: string) => {
    const image = images.find(img => img.id === imageId);
    if (!image) return;

    try {
      // Convert data URL to base64 string
      const base64Image = image.url.split(',')[1];
      
      const messages: VisionMessage[] = [
        {
          role: "user",
          content: [
            { type: "text", text: question },
            {
              type: "image_url",
              image_url: {
                url: `data:image/jpeg;base64,${base64Image}`
              }
            }
          ]
        }
      ];

      const response = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAIKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: "gpt-4-vision-preview",
          messages,
          max_tokens: 300
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error generating vision response:', error);
      return `Error analyzing image: ${error instanceof Error ? error.message : 'Unknown error occurred'}`;
    }
  };

  // Add this new function to handle starting a line draw
  const handleStartDrawing = (e: React.MouseEvent, cardId: number) => {
    if (e.metaKey || e.ctrlKey) {  // Check for cmd/ctrl instead of alt
      e.stopPropagation();
      setDrawingState({
        isDrawing: true,
        startCardId: cardId,
        endPosition: {
          x: (e.clientX - pan.x) / scale,
          y: (e.clientY - pan.y) / scale
        }
      });
    }
  };

  // Add this function to handle completing the line drawing
  const handleCardDrawEnd = (e: React.MouseEvent, endCardId: number) => {
    if (drawingState.isDrawing && drawingState.startCardId !== null) {
      e.stopPropagation();
      // Don't create self-referential links
      if (drawingState.startCardId !== endCardId) {
        setLinks(prevLinks => [...prevLinks, {
          fromId: drawingState.startCardId!,
          toId: endCardId
        }]);
      }
      setDrawingState({
        isDrawing: false,
        startCardId: null,
        endPosition: null
      });
    }
  };

  // Add this new component to render the temporary drawing line
  const DrawingLine = () => {
    if (!drawingState.isDrawing || !drawingState.startCardId || !drawingState.endPosition) {
      return null;
    }

    const startCard = cards.find(card => card.id === drawingState.startCardId);
    if (!startCard) return null;

    const startX = startCard.position.x + (startCard.width || 250) / 2;
    const startY = startCard.position.y + (startCard.height || 100) / 2;

    return (
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
        }}
      >
        <line
          x1={startX}
          y1={startY}
          x2={drawingState.endPosition.x}
          y2={drawingState.endPosition.y}
          stroke="#ffafcc"
          strokeWidth="2"
          strokeDasharray="5,5"
        />
      </svg>
    );
  };

  // Update this CSS in the style tag within the return statement
  const gridStyles = `
    .canvas {
      background-image: 
        /* Cross marks at 18x18 intersections */
        radial-gradient(circle at center, rgba(0, 0, 0, 0.15) 2px, transparent 2px),
        /* Plus marks at 9x9 intersections */
        radial-gradient(circle at center, rgba(0, 0, 0, 0.1) 1px, transparent 1px),
        /* Regular grid lines */
        linear-gradient(to right, rgba(0, 0, 0, 0.05) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
      background-size: 
        360px 360px,  /* Cross marks (18 * 20px) */
        180px 180px,  /* Plus marks (9 * 20px) */
        20px 20px,    /* Regular grid */
        20px 20px;    /* Regular grid */
      background-position:
        0 0,          /* Cross marks */
        0 0,          /* Plus marks */
        0 0,          /* Regular grid */
        0 0;          /* Regular grid */
    }
    
    ${mousePosition ? `
    .canvas::after {
      content: '';
      position: absolute;
      width: 50px;
      height: 50px;
      left: ${mousePosition.x - 25}px;
      top: ${mousePosition.y - 25}px;
      pointer-events: none;
      z-index: -1;
      background-image: 
        /* Enhanced grid lines in cursor area */
        linear-gradient(to right, rgba(0, 0, 0, 0.15) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(0, 0, 0, 0.15) 1px, transparent 1px);
      background-size: 
        20px 20px,
        20px 20px;
      background-position:
        ${-((mousePosition.x - 25) % 20)}px 0,
        0 ${-((mousePosition.y - 25) % 20)}px;
      -webkit-mask-image: radial-gradient(circle at center,
        black 0px,
        black 12px,
        transparent 25px
      );
      mask-image: radial-gradient(circle at center,
        black 0px,
        black 12px,
        transparent 25px
      );
    }
    ` : ''}
  `;

  // Add this new function to handle marker drag start
  const handleMarkerMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDraggingMarker(true);
    // Calculate offset between mouse and marker's top-left
    const rect = (e.target as HTMLElement).getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const offsetY = e.clientY - rect.top;
    setMarkerDragOffset({ x: offsetX, y: offsetY });
    setDragStart({
      x: e.clientX,
      y: e.clientY
    });
  };

  // Replace the ConfigModal component with this updated version
  const ConfigModal = ({ setShowConfigModal }: { setShowConfigModal: React.Dispatch<React.SetStateAction<boolean>> }) => {
    const [tempOpenAIKey, setTempOpenAIKey] = useState(openAIKey);
    const [tempDeepSeekKey, setTempDeepSeekKey] = useState(deepSeekKey);
    const [tempClaudeKey, setTempClaudeKey] = useState(claudeKey);
    const [tempModel, setTempModel] = useState(selectedModel);

    useEffect(() => {
      if (tempOpenAIKey) {
        localStorage.setItem('openAIKey', tempOpenAIKey);
      }
      if (tempDeepSeekKey) {
        localStorage.setItem('deepSeekKey', tempDeepSeekKey);
      }
      if (tempClaudeKey) {
        localStorage.setItem('claudeKey', tempClaudeKey);
      }
    }, [tempOpenAIKey, tempDeepSeekKey, tempClaudeKey]);

    useEffect(() => {
      localStorage.setItem('selectedModel', tempModel);
    }, [tempModel]);

    const handleSave = () => {
      setOpenAIKey(tempOpenAIKey);
      setDeepSeekKey(tempDeepSeekKey);
      setClaudeKey(tempClaudeKey);
      setSelectedModel(tempModel);
      setShowConfigModal(false);
    };

    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        pointerEvents: 'auto',
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      onMouseUp={(e) => e.stopPropagation()}
      >
        <div style={{
          backgroundColor: 'white',
          padding: '20px',
          borderRadius: '8px',
          width: '400px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          position: 'relative',
          pointerEvents: 'auto',
          zIndex: 10000,
        }}
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onMouseUp={(e) => e.stopPropagation()}
        >
          {/* X Close Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              console.log('Close X clicked');
              setShowConfigModal(false);
            }}
            style={{
              position: 'absolute',
              top: '10px',
              right: '10px',
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#888',
              zIndex: 1001,
              lineHeight: 1,
            }}
            aria-label="Close"
          >
            ×
          </button>
          {/* Model Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ 
              display: 'block', 
              marginBottom: '8px',
              fontFamily: "Times New Roman, Times, serif",
            }}>
              Model:
            </label>
            <div style={{ display: 'flex', gap: '10px', flexDirection: 'column' }}>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setTempModel('openai');
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: '1px solid black',
                    backgroundColor: tempModel === 'openai' ? '#f0f0f0' : 'white',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    flex: 1,
                    color: 'black',
                    minWidth: '100px',
                  }}
                >
                  OpenAI
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setTempModel('deepseek');
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: '1px solid black',
                    backgroundColor: tempModel === 'deepseek' ? '#f0f0f0' : 'white',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    flex: 1,
                    color: 'black',
                    minWidth: '100px',
                  }}
                >
                  DeepSeek
                </button>
              </div>
              {/* Claude models row */}
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setTempModel('claude-sonnet-4-20250514');
                  }}
                  style={{
                    padding: '8px 16px',
                    borderRadius: '4px',
                    border: '1px solid black',
                    backgroundColor: tempModel === 'claude-sonnet-4-20250514' ? '#f0f0f0' : 'white',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    flex: 1,
                    color: 'black',
                    minWidth: '100px',
                  }}
                >
                  Claude Sonnet
                </button>
              </div>
              {tempModel === 'deepseek' && (
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  marginTop: '4px',
                }}>
                  Browser-compatible API similar to OpenAI
                </div>
              )}
              {tempModel === 'claude-sonnet-4-20250514' && (
                <div style={{
                  fontSize: '12px',
                  color: '#666',
                  fontStyle: 'italic',
                  textAlign: 'center',
                  marginTop: '4px',
                }}>
                  Anthropic Claude models via API
                </div>
              )}
            </div>
          </div>

          {/* OpenAI Key Input (only show if OpenAI is selected) */}
          {tempModel === 'openai' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontFamily: "Times New Roman, Times, serif",
              }}>
                OpenAI API Key:
              </label>
              <input
                type="password"
                value={tempOpenAIKey}
                onChange={(e) => {
                  e.stopPropagation();
                  setTempOpenAIKey(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Stop propagation of keyboard events to prevent interference
                  e.stopPropagation();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onMouseMove={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onPaste={(e) => {
                  // Stop propagation of paste event
                  e.stopPropagation();
                }}
                style={{
                  width: '95%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: "Times New Roman, Times, serif",
                }}
              />
              <div style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #a5d6a7',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#2e7d32',
              }}>
                <strong>Special Commands:</strong><br/>
              • <strong>/search</strong> - Search the web: "/search current weather in New York"<br/>
              • <strong>/deep</strong> - Deep research: "/deep climate change impacts on agriculture"<br/>
              • <strong>/image</strong> - Generate images: "/image a sunset over mountains"<br/>
              • <strong>/parse</strong> - Parse webpages or PDFs: "/parse https://example.com/article" or "/parse" then upload PDF<br/>
              • <strong>/interactive</strong> - Create interactive HTML/CSS/JS: "/interactive a calculator"<br/>
              • <strong>/embed</strong> - Embed a browser: "/embed" (for DuckDuckGo) or "/embed wikipedia.org" (Note: Many sites block embedding)
              </div>
            </div>
          )}

          {/* DeepSeek Key Input (only show if DeepSeek is selected) */}
          {tempModel === 'deepseek' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontFamily: "Times New Roman, Times, serif",
              }}>
                DeepSeek API Key:
              </label>
              <input
                type="password"
                value={tempDeepSeekKey}
                onChange={(e) => {
                  e.stopPropagation();
                  setTempDeepSeekKey(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Stop propagation of keyboard events to prevent interference
                  e.stopPropagation();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onMouseMove={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onPaste={(e) => {
                  // Stop propagation of paste event
                  e.stopPropagation();
                }}
                style={{
                  width: '95%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: "Times New Roman, Times, serif",
                }}
              />
              <div style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #a5d6a7',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#2e7d32',
              }}>
                <strong>Info:</strong> DeepSeek API is compatible with OpenAI's format and works with browser-based requests.
                Get your API key from <a href="https://platform.deepseek.com/api_keys" target="_blank" rel="noopener noreferrer" style={{color: '#2e7d32'}}>DeepSeek Platform</a>.
              </div>
            </div>
          )}

          {/* Claude Key Input (only show if Claude is selected) */}
          {tempModel === 'claude-sonnet-4-20250514' && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{ 
                display: 'block', 
                marginBottom: '8px',
                fontFamily: "Times New Roman, Times, serif",
              }}>
                API Key (Anthropic or OpenRouter):
              </label>
              <input
                type="password"
                value={tempClaudeKey}
                onChange={(e) => {
                  e.stopPropagation();
                  setTempClaudeKey(e.target.value);
                }}
                onKeyDown={(e) => {
                  // Stop propagation of keyboard events to prevent interference
                  e.stopPropagation();
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
                onMouseMove={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
                onPaste={(e) => {
                  // Stop propagation of paste event
                  e.stopPropagation();
                }}
                style={{
                  width: '95%',
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  fontFamily: "Times New Roman, Times, serif",
                }}
              />
              <div style={{
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#e8f5e9',
                border: '1px solid #a5d6a7',
                borderRadius: '4px',
                fontSize: '12px',
                color: '#2e7d32',
              }}>
                <strong>You have two options:</strong>
                <br/><br/>
                <strong>Option 1:</strong> Use your Anthropic API key directly
                <br/>
                • Get from <a href="https://console.anthropic.com/account/keys" target="_blank" rel="noopener noreferrer" style={{color: '#2e7d32'}}>Anthropic Console</a>
                <br/>
                • Starts with "sk-ant-"
                <br/>
                • Uses CORS proxy (may need to enable)
                <br/><br/>
                <strong>Option 2:</strong> Use OpenRouter (more reliable)
                <br/>
                • Get from <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" style={{color: '#2e7d32'}}>OpenRouter</a>
                <br/>
                • Requires adding credits to account
                <br/>
                • Works directly from browser
              </div>
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowConfigModal(false);
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: '1px solid #ccc',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontFamily: "Times New Roman, Times, serif",
                color: 'black',
              }}
            >
              Cancel
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleSave();
              }}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: '1px solid black',
                backgroundColor: 'white',
                cursor: 'pointer',
                fontFamily: "Times New Roman, Times, serif",
                color: 'black',
              }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Add this new function to set card expiration
  const setCardExpiration = (cardId: number, shouldExpire: boolean) => {
    setCards(prevCards =>
      prevCards.map(card =>
        card.id === cardId
          ? {
              ...card,
              expiresAt: shouldExpire ? Date.now() + 24 * 60 * 60 * 1000 : undefined
            }
          : card
      )
    );
  };

  // Add function to cycle through background colors
  const cycleBackgroundColor = () => {
    const colors = ['#ffffff', '#faedcd', '#ccd5ae', '#000000'];
    const currentIndex = colors.indexOf(backgroundColor);
    const nextIndex = (currentIndex + 1) % colors.length;
    setBackgroundColor(colors[nextIndex]);
  };

  // Get RGB values for the current background color for gradients
  const getBackgroundRGB = (): string => {
    switch (backgroundColor) {
      case '#ffffff':
        return '255,255,255';
      case '#faedcd':
        return '250,237,205';
      case '#ccd5ae':
        return '204,213,174';
      case '#000000':
        return '0,0,0';
      default:
        return '250,237,205'; // Default #faedcd
    }
  };

  // Get appropriate text color based on background
  const getTextColor = (): string => {
    return backgroundColor === '#000000' ? '#ffffff' : '#333333';
  };

  // Get appropriate button colors based on background
  const getButtonColors = () => {
    if (backgroundColor === '#000000') {
      return {
        background: '#333333',
        color: '#ffffff',
        border: '#666666'
      };
    }
    return {
      background: 'white',
      color: 'black',
      border: 'black'
    };
  };

  // Handle background image upload
  const handleBackgroundImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const imageUrl = event.target?.result as string;
          setBackgroundImage(imageUrl);
        };
        reader.readAsDataURL(file);
      } else {
        alert('Please select a valid image file.');
      }
    }
  };

  // Trigger background image upload
  const triggerBackgroundImageUpload = () => {
    backgroundImageInputRef.current?.click();
  };

  // Clear background image
  const clearBackgroundImage = () => {
    setBackgroundImage(null);
  };

  // Add a function to get selected item count
  const getSelectedItemsCount = (): number => {
    return selectedItems.size;
  };

  // Voice recording functions
  const startRecording = async (cardId: number) => {
    try {
      // Request high-quality audio
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          channelCount: 1, // Mono audio
          sampleRate: 16000, // 16kHz is optimal for Whisper
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        } 
      });

      // Check what audio formats are supported and prefer webm/opus
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/mp4',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/wav'
      ];

      let selectedMimeType = 'audio/wav'; // fallback
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }

      console.log('Using audio format:', selectedMimeType);

      const recorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType,
        audioBitsPerSecond: 128000 // 128kbps for good quality
      });
      
      const audioChunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
        }
      };

      recorder.onstop = async () => {
        const audioBlob = new Blob(audioChunks, { type: selectedMimeType });
        stream.getTracks().forEach(track => track.stop()); // Stop all tracks
        await transcribeAudio(cardId, audioBlob, selectedMimeType);
      };

      // Record in chunks to ensure we get data
      recorder.start(1000); // Record in 1-second chunks
      setMediaRecorder(recorder);
      setRecordingCardId(cardId);
      setRecordingTime(0);

      // Start timer
      const startTime = Date.now();
      const timer = setInterval(() => {
        setRecordingTime(Math.floor((Date.now() - startTime) / 1000));
      }, 1000);

      // Store timer reference on the recorder for cleanup
      (recorder as any).timer = timer;

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Error accessing microphone. Please check permissions and ensure your browser supports audio recording.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state === 'recording') {
      mediaRecorder.stop();
      
      // Clear timer
      if ((mediaRecorder as any).timer) {
        clearInterval((mediaRecorder as any).timer);
      }
      
      setMediaRecorder(null);
      setRecordingCardId(null);
      setRecordingTime(0);
    }
  };

  const transcribeAudio = async (cardId: number, audioBlob: Blob, mimeType: string = 'audio/wav') => {
    if (!openAIKey) {
      alert('OpenAI API key is required for transcription. Please configure it in the Models settings.');
      return;
    }

    setIsTranscribing(true);

    try {
      console.log('Transcribing audio blob:', {
        size: audioBlob.size,
        type: audioBlob.type,
        mimeType: mimeType
      });

      // Determine file extension based on MIME type
      let fileExtension = 'wav';
      if (mimeType.includes('webm')) fileExtension = 'webm';
      else if (mimeType.includes('mp4')) fileExtension = 'mp4';
      else if (mimeType.includes('ogg')) fileExtension = 'ogg';

      const formData = new FormData();
      formData.append('file', audioBlob, `recording.${fileExtension}`);
      formData.append('model', 'whisper-1');
      
      // Enhanced Whisper parameters for better accuracy
      formData.append('response_format', 'verbose_json'); // Get more detailed response
      formData.append('temperature', '0'); // More deterministic output
      
      // Auto-detect language instead of forcing English
      // formData.append('language', 'en'); // Commented out to let Whisper auto-detect
      
      // Add prompt to improve accuracy for certain contexts
      formData.append('prompt', 'This is a voice memo or note. Please transcribe accurately with proper punctuation.');

      const response = await fetch('https://api.openai.com/v1/audio/transcriptions', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAIKey}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Whisper API error:', errorData);
        throw new Error(`Transcription failed: ${errorData.error?.message || 'Unknown error'}`);
      }

      const data = await response.json();
      console.log('Whisper response:', data);
      
      // Use the text from the response
      const transcribedText = data.text;
      
      if (!transcribedText || transcribedText.trim().length === 0) {
        throw new Error('No speech detected in the recording. Please try again and speak more clearly.');
      }

      // Add the transcribed text to the card's content with a clean separator
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === cardId
            ? {
                ...card,
                content: card.content 
                  ? card.content + (card.content.endsWith('\n') ? '\n' : '\n\n') + transcribedText
                  : transcribedText
              }
            : card
        )
      );

      console.log('Transcription successful:', transcribedText);

    } catch (error) {
      console.error('Error transcribing audio:', error);
      
      let errorMessage = 'Transcription failed. ';
      if (error instanceof Error) {
        errorMessage += error.message;
      } else {
        errorMessage += 'Unknown error occurred.';
      }
      
      // Add troubleshooting tips
      errorMessage += '\n\nTips for better transcription:\n• Speak clearly and at normal pace\n• Ensure good microphone quality\n• Record in a quiet environment\n• Try recording for at least 1-2 seconds';
      
      alert(errorMessage);
    } finally {
      setIsTranscribing(false);
    }
  };

  // Add a helper function to check if a card's content is likely a question
  const isLikelyQuestion = (content: string): boolean => {
    if (!content) return false;
    
    // Check for common question patterns
    const questionPatterns = [
      /\?$/, // Ends with question mark
      /^(what|who|where|when|why|how|is|are|can|could|will|would|should|do|does|did)/i, // Starts with question word
      /(tell me about|i want to know|looking for information|search for)/i, // Research intent phrases
    ];
    
    return questionPatterns.some(pattern => pattern.test(content.trim()));
  };

  // Add this new function for Claude responses
  const generateClaudeResponse = async (cardId: number, prompt: string, updateExisting: boolean = false, targetCardId?: number): Promise<string> => {
    console.log('generateClaudeResponse called for cardId:', cardId);
    console.log('Prompt:', prompt);
    
    // Check if the source card has image data
    const sourceCard = cards.find(c => c.id === cardId);
    if (sourceCard?.imageData) {
      return `**Image Vision Not Fully Supported**

Claude vision support is not yet implemented in this interface. To analyze this image, please:

1. Switch to OpenAI model (click the model button on the card)
2. Click Generate again

The image will be analyzed using OpenAI's vision capabilities.`;
    }
    
    if (!claudeKey) {
      console.log('No Claude API key found');
      return `Please set up your API key for Claude:

Option 1: Use OpenRouter (Recommended)
1. Get an API key from https://openrouter.ai/keys
2. Add credits to your OpenRouter account
3. Enter the OpenRouter API key below

Option 2: Use Anthropic API key directly
1. Get an API key from https://console.anthropic.com/account/keys
2. Enter your Anthropic API key below
3. The app will use a CORS proxy automatically`;
    }

    setGeneratingCardId(cardId);
    setStreamingCardId(targetCardId || cardId);
    setStreamingContent('');
    setStreamingDone(false);
    
    // Determine which card to update
    let responseCardId = updateExisting ? cardId : (targetCardId || Date.now());
    
    // If we need to create a new card and there's no targetCardId
    if (!updateExisting && !targetCardId) {
      // Create a new card first with empty content
      const card = cards.find(c => c.id === cardId);
      const newCard: Card = {
        id: responseCardId,
        content: '',
        position: {
          x: card ? card.position.x + 260 : 100,
          y: card ? card.position.y : 100,
        },
        conversationHistory: [prompt],
        width: 250,
        height: 200,
        model: selectedModel,
      };
      
      // Add the empty card immediately
      setCards(prevCards => [...prevCards, newCard]);
      // Create the link
      setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: responseCardId }]);
    }
    
    // Check if this is an Anthropic key (starts with 'sk-ant-') or OpenRouter key
    const isAnthropicKey = claudeKey.startsWith('sk-ant-');
    
    try {
      let response;
      
      if (isAnthropicKey) {
        console.log('Using Anthropic API with CORS proxy...');
        
        // Use a CORS proxy for Anthropic API
        const proxyUrl = 'https://cors-anywhere.herokuapp.com/';
        const anthropicUrl = 'https://api.anthropic.com/v1/messages';
        
        // Determine the correct system message based on model
        const systemMessage = selectedModel === 'claude-sonnet-4-20250514' 
          ? 'You are Claude 3.5 Sonnet by Anthropic. Always identify yourself as Claude 3.5 Sonnet when asked about your identity.'
          : 'You are Claude 3 Opus by Anthropic. Always identify yourself as Claude 3 Opus when asked about your identity.';
        
        response = await fetch(proxyUrl + anthropicUrl, {
          method: 'POST',
          headers: {
            'x-api-key': claudeKey,
            'anthropic-version': '2023-06-01',
            'Content-Type': 'application/json',
            'anthropic-dangerous-direct-browser-access': 'true'
          },
          body: JSON.stringify({
            model: selectedModel,
            max_tokens: 1024,
            system: systemMessage,
            messages: [
              { role: 'user', content: prompt }
            ],
            stream: true
          })
        });
      } else {
        console.log('Using OpenRouter API...');
        
        // Determine the correct system message based on model
        const systemMessage = selectedModel === 'claude-sonnet-4-20250514' 
          ? 'You are Claude 3.5 Sonnet by Anthropic. Always identify yourself as Claude 3.5 Sonnet when asked about your identity.'
          : 'You are Claude 3 Opus by Anthropic. Always identify yourself as Claude 3 Opus when asked about your identity.';
        
        // Use OpenRouter API
        response = await fetch('https://openrouter.ai/api/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${claudeKey}`,
            'HTTP-Referer': window.location.origin,
            'X-Title': 'Canvas App',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            model: selectedModel === 'claude-sonnet-4-20250514' 
              ? 'anthropic/claude-3.5-sonnet:beta' 
              : 'anthropic/claude-3-opus:beta',
            messages: [
              { role: 'system', content: systemMessage },
              { role: 'user', content: prompt }
            ],
            stream: true,
            temperature: 0.7
          })
        });
      }

      if (!response.ok) {
        console.error('API response not OK:', response.status, response.statusText);
        const errorData = await response.text();
        console.error('Error details:', errorData);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('Claude API streaming response started');
      
      // Process the stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullContent = '';
      
      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          
          const chunk = decoder.decode(value);
          const lines = chunk.split('\n').filter(line => line.trim() !== '');
          
          for (const line of lines) {
            if (line.includes('[DONE]')) continue;
            if (line.startsWith('data: ')) {
              const jsonString = line.slice(6);
              if (jsonString === '[DONE]') continue;
              
              try {
                const json = JSON.parse(jsonString);
                let delta = '';
                
                if (isAnthropicKey) {
                  // Anthropic API format
                  if (json.type === 'content_block_delta') {
                    delta = json.delta?.text || '';
                  }
                } else {
                  // OpenRouter format
                  delta = json.choices?.[0]?.delta?.content || '';
                }
                
                if (delta) {
                  fullContent += delta;
                  setStreamingContent(fullContent);
                  
                  // Update the card's content in real-time
                  setCards(prevCards => 
                    prevCards.map(card => 
                      card.id === responseCardId 
                        ? { ...card, content: fullContent }
                        : card
                    )
                  );
                }
              } catch (e) {
                console.error('Error parsing streaming JSON:', e);
              }
            }
          }
        }
      }
      
      // Final content update
      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: fullContent, conversationHistory: [...(card.conversationHistory || []), fullContent] }
            : card
        )
      );
      
      setStreamingDone(true);
      setGeneratedCardIds(prev => new Set(prev).add(responseCardId));
      console.log('Streaming completed');
      return fullContent;
    } catch (error) {
      console.error('Error calling Claude:', error);
      
      // Update the card with the error message
      const errorMessage = `Error: Failed to generate response from Claude.

${isAnthropicKey ? 
`Using Anthropic API directly:
- The CORS proxy might be down. Try visiting https://cors-anywhere.herokuapp.com/corsdemo and clicking "Request temporary access"
- Or switch to using an OpenRouter API key instead` :
`Using OpenRouter:
- Check if your API key is valid
- Ensure you have sufficient credits
- Check for rate limits`}

To fix this:
1. Try the other option (Anthropic key vs OpenRouter key)
2. Or switch to OpenAI/DeepSeek models which work directly`;

      setCards(prevCards =>
        prevCards.map(card =>
          card.id === responseCardId
            ? { ...card, content: errorMessage }
            : card
        )
      );
      
      return errorMessage;
    } finally {
      setGeneratingCardId(null);
      setStreamingCardId(null);
    }
  };

  // Add the DocumentView component
  const DocumentView = ({ cardId }: { cardId: number }) => {
    const [docContent, setDocContent] = useState('');
    const [isDocEditing, setIsDocEditing] = useState(false);
    const [isReading, setIsReading] = useState(false);
    const [isLoadingAudio, setIsLoadingAudio] = useState(false);
    const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);
    const [audioQueue, setAudioQueue] = useState<string[]>([]);
    const [chunkProgress, setChunkProgress] = useState<{ current: number; total: number } | null>(null);
    const docTextareaRef = useRef<HTMLTextAreaElement>(null);
    const isEditingRef = useRef(false);
    
    // Memoize the card to prevent unnecessary re-renders
    const card = useMemo(() => cards.find(c => c.id === cardId), [cards, cardId]);
    
    // Keep ref in sync with state
    useEffect(() => {
      audioQueueRef.current = audioQueue;
    }, [audioQueue]);

          // Sync ref with state
    useEffect(() => {
      isEditingRef.current = isDocEditing;
    }, [isDocEditing]);

    // Initialize docContent when card is found, but don't override when editing
    useEffect(() => {
      if (card && !isEditingRef.current) {
        setDocContent(card.content);
      }
    }, [card?.content]);

    // Clean up audio when component unmounts or document closes
    useEffect(() => {
      return () => {
        if (audioElement) {
          audioElement.pause();
          audioElement.src = '';
        }
        // Clean up any queued audio URLs
        const currentQueue = audioQueueRef.current;
        currentQueue.forEach(url => URL.revokeObjectURL(url));
      };
    }, [audioElement]);

    // Handle sequential playback of audio chunks
    const playNextChunk = () => {
      const currentQueue = audioQueueRef.current;
      if (currentQueue.length > 0) {
        const nextAudioUrl = currentQueue[0];
        const remainingQueue = currentQueue.slice(1);
        
        // Update both state and ref
        setAudioQueue(remainingQueue);
        audioQueueRef.current = remainingQueue;
        
        const audio = new Audio(nextAudioUrl);
        
        audio.onended = () => {
          URL.revokeObjectURL(nextAudioUrl);
          const currentQueue = audioQueueRef.current;
          if (currentQueue.length > 0) {
            // More chunks to play
            playNextChunk();
          } else {
            // All chunks finished
            setIsReading(false);
            setAudioElement(null);
          }
        };

        audio.onerror = (e) => {
          console.error('Audio playback error:', e);
          setIsReading(false);
          setAudioElement(null);
          URL.revokeObjectURL(nextAudioUrl);
          // Clean up remaining queue
          const currentQueue = audioQueueRef.current;
          currentQueue.forEach(url => URL.revokeObjectURL(url));
          setAudioQueue([]);
          audioQueueRef.current = [];
        };

        audio.onpause = () => {
          // Audio is paused but we're still in reading mode
        };

        audio.onplay = () => {
          // Audio is playing
        };

        setAudioElement(audio);
        audio.play().catch(error => {
          console.error('Error playing next chunk:', error);
          setIsReading(false);
          setAudioElement(null);
          URL.revokeObjectURL(nextAudioUrl);
        });
      } else {
        // No more chunks, end playback
        setIsReading(false);
        setAudioElement(null);
      }
    };

    if (!card) return null;

    // Function to convert markdown to plain text for reading
    const markdownToPlainText = (markdown: string): string => {
      return markdown
        // Remove code blocks
        .replace(/```[\s\S]*?```/g, '')
        .replace(/`[^`]*`/g, '')
        // Remove headers
        .replace(/^#{1,6}\s+/gm, '')
        // Remove bold/italic
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/__([^_]+)__/g, '$1')
        .replace(/_([^_]+)_/g, '$1')
        // Remove strikethrough
        .replace(/~~([^~]+)~~/g, '$1')
        // Remove links but keep text
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        // Remove images
        .replace(/!\[[^\]]*\]\([^)]+\)/g, '')
        // Remove list markers
        .replace(/^[\s]*[-*+]\s+/gm, '')
        // Remove blockquotes
        .replace(/^>\s+/gm, '')
        // Clean up extra whitespace
        .replace(/\n\s*\n/g, '\n\n')
        .trim();
    };

    // Function to generate audio using OpenAI TTS API
    const generateAudio = async (text: string): Promise<string | null> => {
      if (!openAIKey) {
        alert('OpenAI API key is required for text-to-speech. Please configure it in the Models settings.');
        return null;
      }

      try {
        setIsLoadingAudio(true);
        
        // If text is within limit, process normally
        if (text.length <= 4000) { // Leave some buffer under 4096
          return await generateSingleAudio(text);
        }
        
        // If text is too long, chunk it
        const chunks = chunkText(text, 4000);
        console.log(`Text too long (${text.length} chars), splitting into ${chunks.length} chunks`);
        
        // Generate audio for all chunks
        const audioUrls: string[] = [];
        setChunkProgress({ current: 0, total: chunks.length });
        
        for (let i = 0; i < chunks.length; i++) {
          setChunkProgress({ current: i + 1, total: chunks.length });
          console.log(`Generating audio for chunk ${i + 1}/${chunks.length}`);
          const audioUrl = await generateSingleAudio(chunks[i]);
          if (audioUrl) {
            audioUrls.push(audioUrl);
          } else {
            // If any chunk fails, clean up and return null
            audioUrls.forEach(url => URL.revokeObjectURL(url));
            setChunkProgress(null);
            setAudioQueue([]);
            audioQueueRef.current = [];
            return null;
          }
        }
        
        setChunkProgress(null);
        
        // Return the first audio URL and store the rest for sequential playback
        if (audioUrls.length > 0) {
          // Store the remaining URLs for sequential playback
          const remainingUrls = audioUrls.slice(1);
          setAudioQueue(remainingUrls);
          audioQueueRef.current = remainingUrls;
          return audioUrls[0];
        }
        
        return null;
      } catch (error) {
        console.error('Error generating audio:', error);
        alert(`Error generating audio: ${error instanceof Error ? error.message : 'Unknown error'}`);
        return null;
      } finally {
        setIsLoadingAudio(false);
      }
    };

    // Helper function to generate audio for a single text chunk
    const generateSingleAudio = async (text: string): Promise<string | null> => {
      const response = await fetch('https://api.openai.com/v1/audio/speech', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${openAIKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'tts-1',
          input: text,
          voice: 'nova', // You can change this to: alloy, echo, fable, onyx, nova, shimmer
          response_format: 'mp3',
          speed: 0.9
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(`OpenAI TTS API error: ${errorData.error?.message || 'Unknown error'}`);
      }

      // Convert response to blob and create object URL
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      return audioUrl;
    };

    // Helper function to chunk text intelligently at sentence boundaries
    const chunkText = (text: string, maxLength: number): string[] => {
      if (text.length <= maxLength) {
        return [text];
      }

      const chunks: string[] = [];
      let currentChunk = '';
      
      // Split by sentences first
      const sentences = text.split(/(?<=[.!?])\s+/);
      
      for (const sentence of sentences) {
        // If a single sentence is too long, split it by other punctuation
        if (sentence.length > maxLength) {
          const subSentences = sentence.split(/(?<=[,;:])\s+/);
          for (const subSentence of subSentences) {
            if (subSentence.length > maxLength) {
              // If still too long, split by words
              const words = subSentence.split(' ');
              let wordChunk = '';
              for (const word of words) {
                if ((wordChunk + ' ' + word).length > maxLength) {
                  if (wordChunk) {
                    chunks.push(wordChunk.trim());
                    wordChunk = word;
                  } else {
                    // Single word longer than limit, force split
                    chunks.push(word.substring(0, maxLength));
                    wordChunk = word.substring(maxLength);
                  }
                } else {
                  wordChunk += (wordChunk ? ' ' : '') + word;
                }
              }
              if (wordChunk) {
                currentChunk += (currentChunk ? ' ' : '') + wordChunk;
              }
            } else {
              if ((currentChunk + ' ' + subSentence).length > maxLength) {
                if (currentChunk) {
                  chunks.push(currentChunk.trim());
                  currentChunk = subSentence;
                } else {
                  chunks.push(subSentence);
                }
              } else {
                currentChunk += (currentChunk ? ' ' : '') + subSentence;
              }
            }
          }
        } else {
          // Check if adding this sentence would exceed the limit
          if ((currentChunk + ' ' + sentence).length > maxLength) {
            if (currentChunk) {
              chunks.push(currentChunk.trim());
              currentChunk = sentence;
            } else {
              chunks.push(sentence);
            }
          } else {
            currentChunk += (currentChunk ? ' ' : '') + sentence;
          }
        }
      }
      
      // Add the last chunk if it exists
      if (currentChunk) {
        chunks.push(currentChunk.trim());
      }
      
      return chunks.filter(chunk => chunk.length > 0);
    };

    // Function to handle text-to-speech
    const handlePlayPause = async () => {
      if (isLoadingAudio) return;

      if (isReading && audioElement) {
        // Currently reading - pause or resume
        if (audioElement.paused) {
          audioElement.play();
        } else {
          audioElement.pause();
        }
      } else {
        // Not reading - start reading
        const plainText = markdownToPlainText(docContent);
        
        if (!plainText.trim()) {
          alert('No text content to read.');
          return;
        }

        // Generate audio from text
        const audioUrl = await generateAudio(plainText);
        if (!audioUrl) return;

        // Create and configure audio element
        const audio = new Audio(audioUrl);
        
        audio.onloadstart = () => {
          setIsReading(true);
        };

        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);
          const currentQueue = audioQueueRef.current;
          if (currentQueue.length > 0) {
            // More chunks to play
            playNextChunk();
          } else {
            // All chunks finished
            setIsReading(false);
            setAudioElement(null);
          }
        };

        audio.onerror = () => {
          setIsReading(false);
          setAudioElement(null);
          alert('An error occurred while playing the audio.');
          URL.revokeObjectURL(audioUrl);
          // Clean up remaining queue
          const currentQueue = audioQueueRef.current;
          currentQueue.forEach(url => URL.revokeObjectURL(url));
          setAudioQueue([]);
          audioQueueRef.current = [];
        };

        audio.onpause = () => {
          // Audio is paused but we're still in reading mode
        };

        audio.onplay = () => {
          // Audio is playing
        };

        setAudioElement(audio);
        
        // Start playing
        try {
          await audio.play();
        } catch (error) {
          console.error('Error playing audio:', error);
          setIsReading(false);
          setAudioElement(null);
          alert('Could not play audio. Please try again.');
        }
      }
    };

    // Function to stop reading
    const handleStop = () => {
      if (audioElement) {
        audioElement.pause();
        audioElement.src = '';
        setAudioElement(null);
      }
      // Clean up any remaining audio queue
      const currentQueue = audioQueueRef.current;
      currentQueue.forEach(url => URL.revokeObjectURL(url));
      setAudioQueue([]);
      setChunkProgress(null);
      setIsReading(false);
    };

    // Function to get current audio state
    const getAudioState = () => {
      if (isLoadingAudio) return 'loading';
      if (!isReading) return 'stopped';
      if (audioElement?.paused) return 'paused';
      if (audioElement && !audioElement.paused) return 'playing';
      return 'stopped';
    };

    // Add formatting function for document mode
    const formatDocumentText = (type: 'bold' | 'italic' | 'strikethrough' | 'highlight' | 'h1' | 'h2' | 'h3') => {
      if (!docTextareaRef.current) return;
      
      const textarea = docTextareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = textarea.value.substring(start, end);
      
      let newText = '';
      let newCursorPos = start;
      
      switch (type) {
        case 'bold':
          newText = textarea.value.substring(0, start) + 
                   '**' + selectedText + '**' + 
                   textarea.value.substring(end);
          newCursorPos = selectedText ? end + 4 : start + 2;
          break;
        case 'italic':
          newText = textarea.value.substring(0, start) + 
                   '_' + selectedText + '_' + 
                   textarea.value.substring(end);
          newCursorPos = selectedText ? end + 2 : start + 1;
          break;
        case 'strikethrough':
          newText = textarea.value.substring(0, start) + 
                   '~~' + selectedText + '~~' + 
                   textarea.value.substring(end);
          newCursorPos = selectedText ? end + 4 : start + 2;
          break;
        case 'highlight':
          newText = textarea.value.substring(0, start) + 
                   '<mark>' + selectedText + '</mark>' + 
                   textarea.value.substring(end);
          newCursorPos = selectedText ? end + 13 : start + 6;
          break;
        case 'h1':
        case 'h2':
        case 'h3':
          // Find the beginning of the line containing the cursor/selection
          const textBeforeCursor = textarea.value.substring(0, start);
          const lastNewlineIndex = textBeforeCursor.lastIndexOf('\n');
          const lineStart = lastNewlineIndex === -1 ? 0 : lastNewlineIndex + 1;
          
          // Find the end of the current line
          const textAfterCursor = textarea.value.substring(end);
          const nextNewlineIndex = textAfterCursor.indexOf('\n');
          const lineEnd = nextNewlineIndex === -1 ? textarea.value.length : end + nextNewlineIndex;
          
          // Get the current line content
          const currentLine = textarea.value.substring(lineStart, lineEnd);
          
          // Remove existing heading markers if any
          const cleanLine = currentLine.replace(/^#{1,6}\s*/, '');
          
          // Add new heading markers
          const headingMarkers = type === 'h1' ? '# ' : type === 'h2' ? '## ' : '### ';
          const newLine = headingMarkers + cleanLine;
          
          // Reconstruct the full text
          newText = textarea.value.substring(0, lineStart) + 
                   newLine + 
                   textarea.value.substring(lineEnd);
          
          // Calculate new cursor position
          const markerLength = headingMarkers.length;
          const originalLineLength = currentLine.length;
          const newLineLength = newLine.length;
          const lengthDifference = newLineLength - originalLineLength;
          
          if (selectedText) {
            // If there was a selection, maintain relative position
            newCursorPos = start + lengthDifference;
          } else {
            // If no selection, place cursor after the heading markers
            newCursorPos = lineStart + markerLength;
          }
          break;
      }
      
      setDocContent(newText);
      
      // Reset cursor position after state update
      setTimeout(() => {
        if (docTextareaRef.current) {
          docTextareaRef.current.setSelectionRange(newCursorPos, newCursorPos);
          docTextareaRef.current.focus();
        }
      }, 0);
    };

    const handleDocSave = () => {
      setCards(prevCards =>
        prevCards.map(c =>
          c.id === cardId ? { ...c, content: docContent } : c
        )
      );
      setIsDocEditing(false);
    };

    const handleDocKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        // Stop reading when closing
        handleStop();
        setExpandedCardId(null);
        setDocumentMode(false);
      } else if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleDocSave();
              } else if (isDocEditing) {
          // Handle formatting shortcuts in document mode
          if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
            e.preventDefault();
            formatDocumentText('bold');
          } else if ((e.metaKey || e.ctrlKey) && e.key === 'i') {
            e.preventDefault();
            formatDocumentText('italic');
          } else if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'X') {
            e.preventDefault();
            formatDocumentText('strikethrough');
          } else if ((e.metaKey || e.ctrlKey) && e.key === 'h') {
            e.preventDefault();
            formatDocumentText('highlight');
          }
        }
    };

    const handleDocPaste = (e: React.ClipboardEvent) => {
      // Check if clipboard contains image data
      const items = Array.from(e.clipboardData.items);
      const imageItem = items.find(item => item.type.startsWith('image/'));
      
      if (imageItem) {
        e.preventDefault(); // Prevent default paste for images
        
        const file = imageItem.getAsFile();
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            const imageData = event.target?.result as string;
            
            // Update the card with the image data and switch to OpenAI model for vision support
            setCards(prevCards =>
              prevCards.map(c =>
                c.id === cardId ? { ...c, imageData, model: 'openai' } : c
              )
            );
          };
          reader.readAsDataURL(file);
        }
      } else {
        // Allow default text paste behavior
        e.stopPropagation();
      }
    };

    return (
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10000,
        }}
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            handleStop(); // Stop reading when closing
            setExpandedCardId(null);
            setDocumentMode(false);
          }
        }}
      >
        <div
          style={{
            backgroundColor: backgroundColor,
            width: '90%',
            maxWidth: '800px',
            height: '90%',
            maxHeight: '90vh',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            animation: 'documentFadeIn 0.3s ease-out',
          }}
        >
          {/* Document Header */}
          <div
            style={{
              padding: '20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              backgroundColor: '#fafafa',
            }}
          >
            <div style={{ flex: 1 }}>
              {card.title && (
                <div
                  style={{
                    fontSize: '14px',
                    color: '#6d6875',
                    fontStyle: 'italic',
                    marginBottom: '4px',
                  }}
                >
                  {card.title}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {/* Voice Recording Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (recordingCardId === cardId) {
                    stopRecording();
                  } else if (recordingCardId === null) {
                    startRecording(cardId);
                  }
                }}
                disabled={recordingCardId !== null && recordingCardId !== cardId}
                style={{
                  backgroundColor: recordingCardId === cardId 
                    ? '#ff6b6b' 
                    : isTranscribing 
                    ? '#ff9800' 
                    : '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  cursor: (recordingCardId !== null && recordingCardId !== cardId) ? 'not-allowed' : 'pointer',
                  fontFamily: "Times New Roman, Times, serif",
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: (recordingCardId !== null && recordingCardId !== cardId) ? 0.5 : 1,
                }}
                title={
                  recordingCardId === cardId 
                    ? `Recording... ${recordingTime}s (click to stop)` 
                    : isTranscribing 
                    ? 'Transcribing audio...'
                    : 'Record voice memo'
                }
              >
                {recordingCardId === cardId ? (
                  <>
                    <div style={{
                      width: '14px',
                      height: '14px',
                      backgroundColor: 'white',
                      borderRadius: '50%',
                      animation: 'pulsingOutline 1s ease-in-out infinite',
                    }} />
                    <span>Recording {recordingTime}s</span>
                  </>
                ) : isTranscribing ? (
                  <>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                      <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity="0.25"/>
                      <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z">
                        <animateTransform attributeName="transform" type="rotate" dur="0.75s" values="0 12 12;360 12 12" repeatCount="indefinite"/>
                      </path>
                    </svg>
                    <span>Transcribing</span>
                  </>
                ) : (
                  <>
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="white">
                      <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/>
                      <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z"/>
                    </svg>
                    <span>Record</span>
                  </>
                )}
              </button>

              {/* Play/Pause Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handlePlayPause();
                }}
                disabled={isLoadingAudio}
                style={{
                  backgroundColor: getAudioState() === 'stopped' ? '#4CAF50' : getAudioState() === 'paused' ? '#2196F3' : getAudioState() === 'loading' ? '#ff9800' : '#ff6b6b',
                  color: 'white',
                  border: 'none',
                  padding: '8px 12px',
                  borderRadius: '4px',
                  cursor: isLoadingAudio ? 'wait' : 'pointer',
                  fontFamily: "Times New Roman, Times, serif",
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  opacity: isLoadingAudio ? 0.7 : 1,
                }}
                title={getAudioState() === 'stopped' 
                  ? "Read text aloud with AI voice" 
                  : getAudioState() === 'paused' 
                  ? "Resume reading" 
                  : getAudioState() === 'loading'
                  ? "Generating audio..."
                  : "Pause reading"
                }
              >
                {getAudioState() === 'loading' ? (
                  // Loading spinner
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                    <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity="0.25"/>
                    <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z">
                      <animateTransform attributeName="transform" type="rotate" dur="0.75s" values="0 12 12;360 12 12" repeatCount="indefinite"/>
                    </path>
                  </svg>
                ) : getAudioState() === 'stopped' ? (
                  // Play icon
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="white">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M6.271 5.055a.5.5 0 0 1 .52.024L11 7.055a.5.5 0 0 1 0 .89L6.791 9.921A.5.5 0 0 1 6 9.5V6a.5.5 0 0 1 .271-.945z"/>
                  </svg>
                ) : getAudioState() === 'paused' ? (
                  // Resume icon
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="white">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M6.271 5.055a.5.5 0 0 1 .52.024L11 7.055a.5.5 0 0 1 0 .89L6.791 9.921A.5.5 0 0 1 6 9.5V6a.5.5 0 0 1 .271-.945z"/>
                  </svg>
                ) : (
                  // Pause icon
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="white">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M5 6.25a1.25 1.25 0 1 1 2.5 0v3.5a1.25 1.25 0 1 1-2.5 0v-3.5zm3.5 0a1.25 1.25 0 1 1 2.5 0v3.5a1.25 1.25 0 1 1-2.5 0v-3.5z"/>
                  </svg>
                )}
                {getAudioState() === 'loading' 
                  ? chunkProgress 
                    ? `Processing ${chunkProgress.current}/${chunkProgress.total}...`
                    : "Generating..." 
                  : getAudioState() === 'stopped' 
                  ? "Play" 
                  : getAudioState() === 'paused' 
                  ? "Resume" 
                  : "Pause"
                }
              </button>
              
              {/* Stop Button (only show when reading) */}
              {isReading && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStop();
                  }}
                  style={{
                    backgroundColor: '#666',
                    color: 'white',
                    border: 'none',
                    padding: '8px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                  }}
                  title="Stop reading"
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="white">
                    <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
                    <path d="M5 5h6v6H5z"/>
                  </svg>
                  Stop
                </button>
              )}
              
              {/* Download buttons */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadCardAsTxt(card);
                }}
                style={{
                  backgroundColor: 'white',
                  color: 'black',
                  border: '1px solid #ccc',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontFamily: "Times New Roman, Times, serif",
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                title="Download as text file"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 1V11M8 11L4 7M8 11L12 7M2 14H14" stroke="black" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                TXT
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  downloadCardAsPdf(card);
                }}
                style={{
                  backgroundColor: '#ff6b6b',
                  color: 'white',
                  border: '1px solid #ff6b6b',
                  padding: '6px 12px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontFamily: "Times New Roman, Times, serif",
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
                title="Download as PDF file"
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 14v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4z" fill="white"/>
                  <path d="M6 2v6l-1.5-1.5L3 8l5 5 5-5-1.5-1.5L10 8V2H6z" fill="white"/>
                </svg>
                PDF
              </button>
              {!isDocEditing && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setIsDocEditing(true);
                  }}
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                  }}
                >
                  Edit
                </button>
              )}
              {isDocEditing && (
                <button
                  onClick={handleDocSave}
                  style={{
                    backgroundColor: '#4CAF50',
                    color: 'white',
                    border: '1px solid #4CAF50',
                    padding: '6px 12px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                  }}
                >
                  Save
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleStop(); // Stop reading when closing
                  setExpandedCardId(null);
                  setDocumentMode(false);
                }}
                style={{
                  backgroundColor: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#888',
                  padding: '0 6px',
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Formatting Toolbar - Only show when editing */}
          {isDocEditing && (
            <div
              style={{
                padding: '12px 20px',
                borderBottom: '1px solid #eee',
                backgroundColor: '#f8f9fa',
                display: 'flex',
                gap: '8px',
                alignItems: 'center',
                flexWrap: 'wrap',
              }}
            >
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => formatDocumentText('bold')}
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                  title="Bold (Ctrl+B)"
                >
                  B
                </button>
                <button
                  onClick={() => formatDocumentText('italic')}
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                    fontStyle: 'italic',
                  }}
                  title="Italic (Ctrl+I)"
                >
                  I
                </button>
                <button
                  onClick={() => formatDocumentText('strikethrough')}
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                    textDecoration: 'line-through',
                  }}
                  title="Strikethrough (Ctrl+Shift+X)"
                >
                  S
                </button>
                <button
                  onClick={() => formatDocumentText('highlight')}
                  style={{
                    backgroundColor: '#ffeb3b',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                  title="Highlight (Ctrl+H)"
                >
                  H
                </button>
              </div>
              
              <div style={{ width: '1px', height: '24px', backgroundColor: '#ddd', margin: '0 4px' }} />
              
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  onClick={() => formatDocumentText('h1')}
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '16px',
                    fontWeight: 'bold',
                  }}
                  title="Heading 1"
                >
                  H1
                </button>
                <button
                  onClick={() => formatDocumentText('h2')}
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  onClick={() => formatDocumentText('h3')}
                  style={{
                    backgroundColor: 'white',
                    color: 'black',
                    border: '1px solid #ccc',
                    padding: '6px 10px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontFamily: "Times New Roman, Times, serif",
                    fontSize: '12px',
                    fontWeight: 'bold',
                  }}
                  title="Heading 3"
                >
                  H3
                </button>
              </div>
              
              <div style={{ marginLeft: 'auto', fontSize: '12px', color: getTextColor() }}>
                Ctrl+B bold, Ctrl+I italic, Ctrl+Shift+X strikethrough, Ctrl+H highlight
              </div>
            </div>
          )}

          {/* Document Body */}
          <div
            style={{
              flex: 1,
              padding: '40px',
              overflow: 'auto',
              backgroundColor: backgroundColor,
            }}
          >
            {/* Display pasted image in document view */}
            {card.imageData && (
              <div
                style={{
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={card.imageData}
                  alt="Pasted content"
                  style={{
                    maxWidth: '100%',
                    maxHeight: '400px',
                    borderRadius: '8px',
                    border: '1px solid #ccc',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  }}
                />
              </div>
            )}
            {isDocEditing ? (
              <textarea
                ref={docTextareaRef}
                value={docContent}
                onChange={(e) => setDocContent(e.target.value)}
                onKeyDown={handleDocKeyDown}
                onPaste={handleDocPaste}
                onMouseDown={(e) => e.stopPropagation()}
                style={{
                  width: '100%',
                  height: '100%',
                  padding: '8px',
                  border: 'none',
                  resize: 'none',
                  outline: 'none',
                  fontFamily: "Times New Roman, Times, serif",
                  fontSize: '16px',
                  lineHeight: '1.6',
                  color: getTextColor(),
                  userSelect: 'text',
                  WebkitUserSelect: 'text',
                  MozUserSelect: 'text',
                  msUserSelect: 'text',
                  cursor: 'text',
                  backgroundColor: backgroundColor,
                }}
                placeholder="Start typing..."
                autoFocus
              />
            ) : (
              <div
                style={{
                  fontFamily: "Times New Roman, Times, serif",
                  fontSize: '16px',
                  lineHeight: '1.8',
                  color: getTextColor(),
                  maxWidth: '700px',
                  margin: '0 auto',
                }}
                dangerouslySetInnerHTML={{ __html: renderedContents[card.id] || '' }}
              />
            )}
          </div>

          {/* Document Footer */}
          <div
            style={{
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ fontSize: '12px', color: getTextColor() }}>
              {isDocEditing ? 'Press Cmd/Ctrl+S to save, Esc to exit' : ''}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              {/* Removed model selector and generate buttons for cleaner document view */}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Add this new function to handle downloading a card as text
  const downloadCardAsTxt = (card: Card) => {
    // Get the plain text content (remove markdown formatting for cleaner text file)
    const plainTextContent = card.content;
    
    // Create the file content with optional title
    let fileContent = '';
    if (card.title) {
      fileContent += `Title: ${card.title}\n\n`;
    }
    fileContent += plainTextContent;
    
    // Add conversation history if it exists
    if (card.conversationHistory && card.conversationHistory.length > 0) {
      fileContent += '\n\n---\nConversation History:\n';
      card.conversationHistory.forEach((msg, index) => {
        fileContent += `\n[${index + 1}] ${msg}\n`;
      });
    }
    
    // Create a blob with the text content
    const blob = new Blob([fileContent], { type: 'text/plain' });
    
    // Create a temporary download link
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    
    // Generate filename based on title or first few words of content
    const filename = card.title 
      ? `${card.title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`
      : `card_${card.content.slice(0, 30).replace(/[^a-z0-9]/gi, '_').toLowerCase()}.txt`;
    
    a.download = filename;
    
    // Trigger the download
    document.body.appendChild(a);
    a.click();
    
    // Cleanup
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // Add this new function to handle downloading a card as PDF
  const downloadCardAsPdf = (card: Card) => {
    // Create a new window/tab for PDF generation
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      alert('Please allow popups to download PDF files.');
      return;
    }

    // Generate the PDF content as HTML
    const title = card.title || 'Canvas Document';
    const content = card.content;
    
    // Convert markdown to HTML for better PDF formatting
    const htmlContent = marked.parse(content) as string;
    
    // Generate conversation history HTML
    let historyHtml = '';
    if (card.conversationHistory && card.conversationHistory.length > 0) {
      historyHtml = `
        <div class="page-break">
          <h2>Conversation History</h2>
          <hr />
          ${card.conversationHistory.map((msg, index) => 
            `<div class="history-item">
              <strong>[${index + 1}]</strong> ${msg.replace(/\n/g, '<br/>')}
            </div>`
          ).join('')}
        </div>
      `;
    }

    // Create the full HTML document
    const htmlDocument = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${title}</title>
        <style>
          @page {
            margin: 1in;
            size: letter;
          }
          
          body {
            font-family: "Times New Roman", Times, serif;
            font-size: 12pt;
            line-height: 1.6;
            color: #333;
            max-width: none;
            margin: 0;
            padding: 0;
          }
          
          .document-title {
            font-size: 18pt;
            font-weight: bold;
            margin-bottom: 20pt;
            text-align: center;
            border-bottom: 2pt solid #333;
            padding-bottom: 10pt;
          }
          
          .content {
            margin-bottom: 30pt;
          }
          
          .page-break {
            page-break-before: always;
            margin-top: 30pt;
          }
          
          .history-item {
            margin-bottom: 15pt;
            padding: 10pt;
            border-left: 3pt solid #ccc;
            padding-left: 15pt;
          }
          
          h1, h2, h3, h4, h5, h6 {
            color: #333;
            margin-top: 20pt;
            margin-bottom: 10pt;
          }
          
          h1 { font-size: 16pt; }
          h2 { font-size: 14pt; }
          h3 { font-size: 13pt; }
          h4, h5, h6 { font-size: 12pt; }
          
          p {
            margin-bottom: 10pt;
            text-align: justify;
          }
          
          ul, ol {
            margin-bottom: 10pt;
            padding-left: 20pt;
          }
          
          li {
            margin-bottom: 5pt;
          }
          
          blockquote {
            margin: 15pt 20pt;
            padding: 10pt;
            border-left: 3pt solid #ccc;
            font-style: italic;
            background-color: #f9f9f9;
          }
          
          code {
            font-family: "Courier New", monospace;
            background-color: #f5f5f5;
            padding: 2pt 4pt;
            border-radius: 2pt;
          }
          
          pre {
            font-family: "Courier New", monospace;
            background-color: #f5f5f5;
            padding: 10pt;
            border-radius: 4pt;
            overflow-wrap: break-word;
            white-space: pre-wrap;
          }
          
          strong, b {
            font-weight: bold;
          }
          
          em, i {
            font-style: italic;
          }
          
          mark {
            background-color: #ffeb3b;
            padding: 1pt 2pt;
          }
          
          a {
            color: #0066cc;
            text-decoration: underline;
          }
          
          hr {
            border: none;
            border-top: 1pt solid #ccc;
            margin: 20pt 0;
          }
          
          @media print {
            body {
              print-color-adjust: exact;
              -webkit-print-color-adjust: exact;
            }
          }
        </style>
      </head>
      <body>
        ${card.title ? `<div class="document-title">${card.title}</div>` : ''}
        
        <div class="content">
          ${htmlContent}
        </div>
        
        ${historyHtml}
        
        <script>
          // Auto-print when the page loads
          window.onload = function() {
            // Small delay to ensure content is fully rendered
            setTimeout(function() {
              window.print();
              // Close the window after printing (optional)
              // window.close();
            }, 500);
          };
        </script>
      </body>
      </html>
    `;

    // Write the HTML to the new window
    printWindow.document.write(htmlDocument);
    printWindow.document.close();
    
    // Focus the new window
    printWindow.focus();
  };

  // Modify handleGenerateClick to create a single response card
  const handleGenerateClick = async (cardId: number) => {
    console.log(`handleGenerateClick called for cardId: ${cardId}`);
    const card = cards.find(c => c.id === cardId);
    if (!card) return;
    
    // Log the model that will be used
    console.log(`Card model: ${card.model || 'not set'}, selectedModel: ${selectedModel}`);
    const modelToUse = card.model || selectedModel;
    console.log(`Will use model: ${modelToUse}`);

    // Handle "All Models" option by calling burst functionality
    if (modelToUse === 'all-models') {
      console.log('All Models selected, calling burst functionality');
      await handleBurst(cardId);
      return;
    }

    // Check if the content starts with /image
    if (card.content.trim().startsWith('/image')) {
      setGeneratingCardId(cardId);
      const prompt = card.content.replace('/image', '').trim();
      const imageUrl = await generateImage(prompt);
      setGeneratingCardId(null);

      if (imageUrl) {
        // Create a new image
        const newImage: Image = {
          id: Date.now(),
          url: imageUrl,
          position: {
            x: card.position.x + 260,
            y: card.position.y,
          },
          description: prompt
        };
        setImages(prevImages => [...prevImages, newImage]);
        
        // Create a link between the prompt card and the generated image
        setLinks(prevLinks => [...prevLinks, {
          fromId: cardId,
          toId: newImage.id
        }]);
      }
    } 
    // Handle /search command directly with generateWebSearchResponse
    else if (card.content.trim().startsWith('/search')) {
      const searchQuery = card.content.replace('/search', '').trim();
      if (searchQuery.length === 0) {
        return; // Skip empty searches
      }
      
      // Call generateWebSearchResponse directly - it will create the card
      await generateWebSearchResponse(cardId, searchQuery);
    }
    // Handle /deep command for deep research
    else if (card.content.trim().startsWith('/deep')) {
      const researchTopic = card.content.replace('/deep', '').trim();
      if (researchTopic.length === 0) {
        return; // Skip empty research topics
      }
      
      // Call generateDeepResearch directly - it will create the card
      await generateDeepResearch(cardId, researchTopic);
    }
    // Handle /parse command for webpage and PDF parsing
    else if (card.content.trim().startsWith('/parse')) {
      const input = card.content.replace('/parse', '').trim();
      
      // If no input provided, show help and file upload option
      if (input.length === 0) {
        setGeneratingCardId(cardId);
        
        // Create a new card with parse options
        const optionsCardId = Date.now();
        const optionsCard: Card = {
          id: optionsCardId,
          title: `Parse Options`,
          content: `**Choose what to parse:**

**Option 1: Parse Webpage**
Type: \`/parse https://example.com/article\`

**Option 2: Parse PDF**
Click the "Upload PDF" button below to select a PDF file for text extraction.

**Examples:**
- \`/parse https://en.wikipedia.org/wiki/Artificial_intelligence\`
- \`/parse https://arxiv.org/abs/2301.00001\` (research papers)
- \`/parse https://news.example.com/article\` (news articles)

Upload a PDF using the button below to extract and analyze its text content.`,
          position: {
            x: card.position.x + 260,
            y: card.position.y,
          },
          conversationHistory: [],
          width: 400,
          height: 300,
        };
        
        // Add the options card immediately
        setCards(prevCards => [...prevCards, optionsCard]);
        setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: optionsCardId }]);
        setGeneratingCardId(null);
        return;
      }
      
      // Check if input looks like a URL
      const isUrl = input.startsWith('http://') || input.startsWith('https://') || input.includes('.');
      
      if (isUrl) {
        // Handle URL parsing
        setGeneratingCardId(cardId);
        
        // Create a new card immediately with a loading message
        const responseCardId = Date.now();
        const newCard: Card = {
          id: responseCardId,
          title: `Parsed: ${input.length > 50 ? input.substring(0, 47) + '...' : input}`,
          content: "Parsing webpage content...",
          position: {
            x: card.position.x + 260,
            y: card.position.y,
          },
          conversationHistory: [input],
          width: 400,
          height: 500,
        };
        
        // Add the card immediately
        setCards(prevCards => [...prevCards, newCard]);
        setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: responseCardId }]);
        
        try {
          // Parse the webpage content
          const parsedContent = await parseWebpage(input);
          
          // Update the card with the parsed content
          setCards(prevCards => 
            prevCards.map(c => 
              c.id === responseCardId 
                ? { 
                    ...c, 
                    content: parsedContent,
                    conversationHistory: [input, parsedContent],
                  }
                : c
            )
          );
        } catch (error) {
          console.error("Error parsing webpage:", error);
          // Update the card with the error message
          setCards(prevCards => 
            prevCards.map(c => 
              c.id === responseCardId 
                ? { ...c, content: `Error parsing webpage: ${error instanceof Error ? error.message : 'Unknown error'}` }
                : c
            )
          );
        } finally {
          setGeneratingCardId(null);
        }
      } else {
        // Show error for unrecognized input
        const errorCardId = Date.now();
        const errorCard: Card = {
          id: errorCardId,
          title: `Parse Error`,
          content: `**Invalid Input for /parse**

The input "${input}" doesn't appear to be a valid URL.

**Valid formats:**
- \`/parse https://example.com\`
- \`/parse http://example.com\`
- \`/parse example.com\`

**For PDF parsing:**
Use \`/parse\` without any text, then use the "Upload PDF" button.`,
          position: {
            x: card.position.x + 260,
            y: card.position.y,
          },
          conversationHistory: [],
          width: 300,
          height: 200,
        };
        
        setCards(prevCards => [...prevCards, errorCard]);
        setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: errorCardId }]);
      }
    }
    // Handle /interactive command for interactive HTML/CSS/JS elements
    else if (card.content.trim().startsWith('/interactive')) {
      const prompt = card.content.replace('/interactive', '').trim();
      if (prompt.length === 0) {
        return; // Skip empty prompts
      }
      
      // Call generateInteractiveElement directly - it will create the card
      await generateInteractiveElement(cardId, prompt);
    }
    // Handle /embed command for embedded browser with DuckDuckGo or specified URL
    else if (card.content.trim().startsWith('/embed')) {
      const urlInput = card.content.replace('/embed', '').trim();
      let url = 'https://duckduckgo.com';
      
      // If a URL is specified, use it (add https:// if not present)
      if (urlInput.length > 0) {
        if (urlInput.startsWith('http://') || urlInput.startsWith('https://')) {
          url = urlInput;
        } else {
          url = 'https://' + urlInput;
        }
      }
      
      setGeneratingCardId(cardId);
      
      // Create a new card immediately with the embedded browser
      const browserCardId = Date.now();
      const newCard: Card = {
        id: browserCardId,
        title: `Browser: ${url === 'https://duckduckgo.com' ? 'DuckDuckGo' : url}`,
        content: `**Embedded Browser**\n\nLoaded: ${url}`,
        position: {
          x: card.position.x + 260,
          y: card.position.y,
        },
        conversationHistory: [card.content],
        width: 600,
        height: 500,
        isInteractive: true,
                 htmlContent: `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Embedded Browser</title>
    <style>
        body, html {
            margin: 0;
            padding: 0;
            height: 100%;
            overflow: hidden;
            font-family: Arial, sans-serif;
        }
        iframe {
            width: 100%;
            height: calc(100% - 50px);
            border: none;
            display: block;
        }
        .browser-header {
            background: #f0f0f0;
            padding: 8px;
            font-size: 12px;
            border-bottom: 1px solid #ccc;
            display: flex;
            align-items: center;
            gap: 10px;
            height: 34px;
        }
        .url-bar {
            flex: 1;
            padding: 4px 8px;
            border: 1px solid #ccc;
            border-radius: 4px;
            background: white;
            font-size: 12px;
        }
        .nav-btn {
            background: #e0e0e0;
            border: 1px solid #ccc;
            padding: 4px 8px;
            cursor: pointer;
            border-radius: 3px;
            font-size: 12px;
        }
        .nav-btn:hover {
            background: #d0d0d0;
        }
        .error-message {
            display: none;
            padding: 20px;
            text-align: center;
            background: #fff3cd;
            border: 1px solid #ffeaa7;
            margin: 20px;
            border-radius: 8px;
        }
        .error-message.show {
            display: block;
        }
        .suggestions {
            margin-top: 15px;
            text-align: left;
        }
        .suggestion-link {
            display: inline-block;
            margin: 5px;
            padding: 5px 10px;
            background: #007bff;
            color: white;
            text-decoration: none;
            border-radius: 4px;
            font-size: 12px;
        }
        .suggestion-link:hover {
            background: #0056b3;
        }
        .open-tab-btn {
            background: #28a745;
            color: white;
            border: none;
            padding: 8px 16px;
            border-radius: 4px;
            cursor: pointer;
            margin: 10px 5px;
        }
        .open-tab-btn:hover {
            background: #1e7e34;
        }
    </style>
</head>
<body>
    <div class="browser-header">
        <button class="nav-btn" onclick="goBack()">◀</button>
        <button class="nav-btn" onclick="goForward()">▶</button>
        <button class="nav-btn" onclick="reload()">↻</button>
        <input type="text" class="url-bar" id="urlBar" value="${url}" onkeypress="if(event.key==='Enter') navigate()">
        <button class="nav-btn" onclick="navigate()">Go</button>
        <button class="nav-btn" onclick="openInNewTab()">📤</button>
    </div>
    
    <div id="errorMessage" class="error-message">
        <h3>⚠️ Site Cannot Be Embedded</h3>
        <p>This website blocks embedding for security reasons. Many popular sites like Google, Facebook, YouTube, Twitter, and news sites have this restriction.</p>
        <button class="open-tab-btn" onclick="openInNewTab()">Open in New Tab Instead</button>
        
        <div class="suggestions">
            <strong>Try these embed-friendly alternatives:</strong><br>
            <a href="#" class="suggestion-link" onclick="loadUrl('https://duckduckgo.com')">DuckDuckGo Search</a>
            <a href="#" class="suggestion-link" onclick="loadUrl('https://archive.org')">Internet Archive</a>
            <a href="#" class="suggestion-link" onclick="loadUrl('https://wikipedia.org')">Wikipedia</a>
            <a href="#" class="suggestion-link" onclick="loadUrl('https://github.com')">GitHub</a>
            <a href="#" class="suggestion-link" onclick="loadUrl('https://codepen.io')">CodePen</a>
            <a href="#" class="suggestion-link" onclick="loadUrl('https://jsfiddle.net')">JSFiddle</a>
        </div>
    </div>
    
    <iframe id="browserFrame" src="${url}" sandbox="allow-same-origin allow-scripts allow-popups allow-forms allow-top-navigation allow-top-navigation-by-user-activation"></iframe>
    
    <script>
        const iframe = document.getElementById('browserFrame');
        const urlBar = document.getElementById('urlBar');
        const errorMessage = document.getElementById('errorMessage');
        let loadTimeout;
        
        function showError() {
            errorMessage.classList.add('show');
            iframe.style.display = 'none';
        }
        
        function hideError() {
            errorMessage.classList.remove('show');
            iframe.style.display = 'block';
        }
        
        function loadUrl(url) {
            hideError();
            urlBar.value = url;
            iframe.src = url;
            checkLoad();
        }
        
        function navigate() {
            const url = urlBar.value;
            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                urlBar.value = 'https://' + url;
            }
            loadUrl(urlBar.value);
        }
        
        function openInNewTab() {
            window.open(urlBar.value, '_blank');
        }
        
        function checkLoad() {
            clearTimeout(loadTimeout);
            loadTimeout = setTimeout(() => {
                // Check if iframe loaded properly by testing if we can access its document
                try {
                    // This will throw an error if the iframe content is blocked
                    const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                    if (!iframeDoc || iframeDoc.body.children.length === 0) {
                        showError();
                    }
                } catch(e) {
                    // If we get a cross-origin error, the page might have loaded fine
                    // But if it's a refused connection, we should show the error
                    showError();
                }
            }, 3000); // Give it 3 seconds to load
        }
        
        function goBack() {
            try {
                iframe.contentWindow.history.back();
            } catch(e) {
                console.log('Cannot navigate back due to cross-origin restrictions');
            }
        }
        
        function goForward() {
            try {
                iframe.contentWindow.history.forward();
            } catch(e) {
                console.log('Cannot navigate forward due to cross-origin restrictions');
            }
        }
        
        function reload() {
            hideError();
            iframe.src = iframe.src;
            checkLoad();
        }
        
        // Initial load check
        checkLoad();
        
        // Handle iframe load events
        iframe.addEventListener('load', function() {
            clearTimeout(loadTimeout);
            try {
                const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
                if (iframeDoc && iframeDoc.title) {
                    hideError();
                }
            } catch(e) {
                // Cross-origin, but probably loaded fine
                hideError();
            }
        });
        
        iframe.addEventListener('error', function() {
            showError();
        });
    </script>
</body>
</html>`,
      };
      
      // Add the card immediately
      setCards(prevCards => [...prevCards, newCard]);
      setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: browserCardId }]);
      
      setGeneratingCardId(null);
    }
    else {
      // Handle regular text generation - create a single new response card
      // Get connected cards for context
      const connectedCards = getConnectedCards(cardId);
      
      let prompt: string;
      let fullHistory: string[];
      
      if (card.imageData) {
        // For image cards, use just the current card's content to maintain image context
        prompt = card.content;
        fullHistory = [card.content];
        
        // Add connected cards context for image cards
        if (connectedCards.length > 0) {
          const connectedContext = connectedCards.map((connectedCard, index) => 
            `Connected Card ${index + 1}: ${connectedCard.content}`
          ).join('\n\n');
          prompt = `${card.content}\n\nAdditional Context from Connected Cards:\n${connectedContext}`;
        }
      } else {
        // For text-only cards, use full conversation history
        fullHistory = getFullConversationHistory(cardId);
        prompt = fullHistory.join("\n\n");
        
        // Add connected cards context
        if (connectedCards.length > 0) {
          const connectedContext = connectedCards.map((connectedCard, index) => 
            `Connected Card ${index + 1}: ${connectedCard.content}`
          ).join('\n\n');
          prompt = `Main Content:\n${prompt}\n\nAdditional Context from Connected Cards:\n${connectedContext}\n\nPlease consider all the above information when responding.`;
        }
      }
      
      setGeneratingCardId(cardId);
      setStreamingCardId(cardId);
      setStreamingContent('');
      setStreamingDone(false);
      
      // Create a new response card immediately with a "Generating..." placeholder
      const responseCardId = Date.now();
      const newCard: Card = {
        id: responseCardId,
        content: "Generating...",
        position: {
          x: card.position.x + 260,
          y: card.position.y,
        },
        conversationHistory: [...fullHistory],
        width: 300,
        height: 200,
        model: modelToUse,
      };
      
      // Add the new card immediately
      setCards(prevCards => [...prevCards, newCard]);
      // Create the link
      setLinks(prevLinks => [...prevLinks, { fromId: cardId, toId: responseCardId }]);
      
      try {
        // Generate response based explicitly on card's model
        let generatedContent: string;
        if (modelToUse === 'deepseek') {
          console.log("Explicitly calling DeepSeek API for synthesis");
          generatedContent = await generateDeepSeekResponse(cardId, prompt, false, responseCardId);
        } else if (modelToUse === 'claude-sonnet-4-20250514') {
          console.log("Explicitly calling Claude API for synthesis");
          generatedContent = await generateClaudeResponse(cardId, prompt, false, responseCardId);
        } else {
          console.log("Explicitly calling OpenAI API for synthesis");
          generatedContent = await generateChatGPTResponse(cardId, prompt, false, responseCardId);
        }
        
        // Final update to the response card (though it should already be updated via streaming)
        setCards(prevCards => 
          prevCards.map(c => 
            c.id === responseCardId 
              ? { 
                  ...c, 
                  content: generatedContent,
                  conversationHistory: [...fullHistory, generatedContent],
                }
              : c
          )
        );
        setGeneratedCardIds(prev => new Set(prev).add(responseCardId));
      } catch (error) {
        console.error("Error generating content:", error);
        // If generation failed, update the new card with the error
        setCards(prevCards => 
          prevCards.map(c => 
            c.id === responseCardId 
              ? { ...c, content: `Error generating response: ${error instanceof Error ? error.message : 'Unknown error'}` }
              : c
          )
        );
      } finally {
        setGeneratingCardId(null);
        setStreamingCardId(null);
      }
    }
  };

  return (
    <>
      <GoogleAnalytics />
      <div 
        ref={appRef}
        className="App" 
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onKeyDown={handleKeyDown}
        onCopy={handleCopy}
        onPaste={handlePaste}
        onContextMenu={handleContextMenu}
        onClick={handleClickOutsideContextMenu}
        tabIndex={0}
      >
        <style>
          {`
            @keyframes cardAppear {
              from {
                opacity: 0;
                transform: scale(0.9);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }

            @keyframes wiggle {
              0% { transform: rotate(0deg); }
              25% { transform: rotate(-1deg); }
              75% { transform: rotate(1deg); }
              100% { transform: rotate(0deg); }
            }

            @keyframes pulsingOutline {
              0% { box-shadow: 0 0 0 2px rgba(255, 175, 204, 0.3); }
              50% { box-shadow: 0 0 0 3px rgba(255, 175, 204, 0.8); }
              100% { box-shadow: 0 0 0 2px rgba(255, 175, 204, 0.3); }
            }

            @keyframes blurToFocus {
              0% { filter: blur(6px); opacity: 0.7; }
              50% { filter: blur(3px); opacity: 0.85; }
              100% { filter: blur(0); opacity: 1; }
            }

            @keyframes documentFadeIn {
              from {
                opacity: 0;
                transform: scale(0.95);
              }
              to {
                opacity: 1;
                transform: scale(1);
              }
            }

            .card {
              transition: box-shadow 0.3s ease-in-out;
            }
            .card:hover {
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            }

            .selected-card {
              box-shadow: 0 0 0 1pt #ffafcc;
            }
            
            .streaming-text {
              animation: blurToFocus 3s ease-out forwards;
            }

            mark {
              background-color: #ffeb3b;
              color: black;
              padding: 2px 4px;
              border-radius: 3px;
              box-decoration-break: clone;
              -webkit-box-decoration-break: clone;
            }

            /* Ensure all text elements in cards inherit the correct color */
            .card h1, .card h2, .card h3, .card h4, .card h5, .card h6,
            .card p, .card li, .card blockquote, .card div, .card span,
            .card strong, .card em, .card b, .card i, .card a, .card code,
            .card pre, .card ul, .card ol, .card table, .card td, .card th {
              color: inherit !important;
            }
            
            /* Universal selector to force all elements in cards to inherit color */
            .card * {
              color: inherit !important;
            }
            
            /* More specific targeting for rendered markdown content */
            .card [dangerouslySetInnerHTML] h1, 
            .card [dangerouslySetInnerHTML] h2, 
            .card [dangerouslySetInnerHTML] h3, 
            .card [dangerouslySetInnerHTML] h4, 
            .card [dangerouslySetInnerHTML] h5, 
            .card [dangerouslySetInnerHTML] h6,
            .card [dangerouslySetInnerHTML] p, 
            .card [dangerouslySetInnerHTML] li, 
            .card [dangerouslySetInnerHTML] div, 
            .card [dangerouslySetInnerHTML] span,
            .card [dangerouslySetInnerHTML] strong, 
            .card [dangerouslySetInnerHTML] em, 
            .card [dangerouslySetInnerHTML] b, 
            .card [dangerouslySetInnerHTML] i,
            .card [dangerouslySetInnerHTML] a,
            .card [dangerouslySetInnerHTML] code,
            .card [dangerouslySetInnerHTML] pre {
              color: inherit !important;
            }

            ${gridStyles}
          `}
        </style>

        {/* Context Menu */}
        {contextMenu.visible && (
          <div style={{
            position: 'fixed',
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
            backgroundColor: 'white',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)',
            zIndex: 9999,
            fontFamily: "Times New Roman, Times, serif",
            fontSize: '14px',
          }}>
            {/* Update the duplicate option with a keyboard shortcut indicator */}
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid #eee',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f8f8f8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateSelectedItems();
                setContextMenu({ ...contextMenu, visible: false });
              }}
            >
              <span>Duplicate ({selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''})</span>
              <span style={{ opacity: 0.6, fontSize: '12px', marginLeft: '10px' }}>Ctrl+D</span>
            </div>
            {/* With keyboard indicator */}
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                color: '#ff5252',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#fff8f8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'white';
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSelectedItems();
                setContextMenu({ ...contextMenu, visible: false });
              }}
            >
              Delete ({selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''}) <span style={{ opacity: 0.6, fontSize: '12px' }}>Del</span>
            </div>
          </div>
        )}

        {/* Recording/Transcription Status */}
        {(recordingCardId !== null || isTranscribing) && (
          <div style={{
            position: 'fixed',
            top: '20px',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: recordingCardId !== null ? 'rgba(255, 107, 107, 0.95)' : 'rgba(255, 152, 0, 0.95)',
            padding: '10px 20px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            zIndex: 2000,
            fontFamily: "Times New Roman, Times, serif",
            fontSize: '16px',
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            animation: 'pulsingOutline 1.5s ease-in-out infinite',
          }}>
            {recordingCardId !== null ? (
              <>
                <div style={{
                  width: '12px',
                  height: '12px',
                  backgroundColor: 'white',
                  borderRadius: '50%',
                  animation: 'pulsingOutline 1s ease-in-out infinite',
                }} />
                <span>Recording... {recordingTime}s</span>
                <button
                  onClick={stopRecording}
                  style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                  }}
                >
                  Stop
                </button>
              </>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                  <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity="0.25"/>
                  <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z">
                    <animateTransform attributeName="transform" type="rotate" dur="0.75s" values="0 12 12;360 12 12" repeatCount="indefinite"/>
                  </path>
                </svg>
                <span>Transcribing audio...</span>
              </>
            )}
          </div>
        )}

        {selectedItems.size > 0 && (
          <div style={{
            position: 'fixed',
            bottom: '20px',
            left: '50%',
            transform: 'translateX(-320px)', // Moved further left to create more space
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '6px 12px',
            borderRadius: '4px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 1000,
            fontFamily: "Times New Roman, Times, serif",
            fontSize: '14px',
            color: getTextColor(),
            border: '1px solid #ffafcc',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}>
            <span 
              style={{ position: 'relative', cursor: 'help' }}
              onMouseEnter={(e) => {
                const tooltip = document.createElement('div');
                tooltip.className = 'selection-tooltip';
                tooltip.style.position = 'absolute';
                tooltip.style.bottom = '24px';
                tooltip.style.left = '0';
                tooltip.style.backgroundColor = 'rgba(0, 0, 0, 0.8)';
                tooltip.style.color = 'white';
                tooltip.style.padding = '6px 10px';
                tooltip.style.borderRadius = '4px';
                tooltip.style.fontSize = '12px';
                tooltip.style.whiteSpace = 'nowrap';
                tooltip.style.zIndex = '1001';
                tooltip.style.pointerEvents = 'none';
                tooltip.innerText = 'Shift+Click to add/remove items\nDrag to select multiple items';
                e.currentTarget.appendChild(tooltip);
              }}
              onMouseLeave={(e) => {
                const tooltip = e.currentTarget.querySelector('.selection-tooltip');
                if (tooltip) {
                  e.currentTarget.removeChild(tooltip);
                }
              }}
            >
              {selectedItems.size} item{selectedItems.size !== 1 ? 's' : ''} selected
            </span>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDuplicateSelectedItems();
              }}
              style={{
                backgroundColor: getButtonColors().background,
                color: getButtonColors().color,
                border: `1px solid ${getButtonColors().border}`,
                padding: '3px 6px',
                borderRadius: '3px',
                cursor: 'pointer',
                fontFamily: "Times New Roman, Times, serif",
                fontSize: '12px',
              }}
            >
              Duplicate
            </button>
            
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteSelectedItems();
              }}
              style={{
                backgroundColor: getButtonColors().background,
                color: '#ff5252',
                border: '1px solid #ff5252',
                padding: '3px 6px',
                borderRadius: '3px',
                cursor: 'pointer',
                fontFamily: "Times New Roman, Times, serif",
                fontSize: '12px',
              }}
            >
              Delete
            </button>
          </div>
        )}

        <div className="canvas-container" style={{
          position: 'absolute',
          top: '0',
          left: '0',
          right: '0',
          bottom: '0',
          overflow: 'hidden',
          backgroundColor: 'black',
        }}>
          <div 
            className="canvas"
            style={{
              transform: `translate(${pan.x}px, ${pan.y}px) scale(${scale})`,
              transformOrigin: '0 0',
              border: '2px dotted #e5e5e5',
              borderRadius: '24px',
              position: 'absolute',
              top: '0',
              left: '0',
              width: '5000px',
              height: '5000px',
              minWidth: '5000px',
              minHeight: '5000px',
              padding: '24px',
              backgroundColor: backgroundColor,
              ...(backgroundImage ? {
                backgroundImage: `url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
              } : {}),
            }}
            onClick={handleCanvasClick}
            onDoubleClick={handleCanvasDoubleClick}
            onMouseDown={(e) => handleMouseDown(e)}
            onMouseLeave={() => setMousePosition(null)}
          >
            {/* Render selection box */}
            {selectionBox.isActive && selectionBox.start && selectionBox.current && (
              <div
                style={{
                  position: 'absolute',
                  left: `${Math.min(selectionBox.start.x, selectionBox.current.x)}px`,
                  top: `${Math.min(selectionBox.start.y, selectionBox.current.y)}px`,
                  width: `${Math.abs(selectionBox.current.x - selectionBox.start.x)}px`,
                  height: `${Math.abs(selectionBox.current.y - selectionBox.start.y)}px`,
                  backgroundColor: 'rgba(255, 175, 204, 0.1)',
                  border: '1px dashed #ffafcc',
                  pointerEvents: 'none',
                  zIndex: 999,
                }}
              />
            )}
            
            {/* Add the marker component */}
            <img
              src="/face.png"
              alt="Marker"
              style={{
                position: 'absolute',
                left: `${markerPosition.x}px`,
                top: `${markerPosition.y}px`,
                width: '80px',
                height: '80px',
                cursor: 'move',
                zIndex: 1000,
                userSelect: 'none',
                pointerEvents: 'auto',
                animation: isDraggingMarker ? 'wiggle 0.3s infinite' : undefined,
              }}
              draggable={false}
              onMouseDown={handleMarkerMouseDown}
            />
            {renderLinks()}
            <DrawingLine />
            {cards.filter(card => isElementInViewport(card.position, card.width || 250, card.height || 100))
              .map(card => (
                <div 
                  key={card.id}
                  id={`card-${card.id}`}
                  className={`card ${selectedItems.has(card.id) ? 'selected-card' : ''}`}
                  style={{
                    position: 'absolute',
                    left: `${card.position.x}px`,
                    top: `${card.position.y}px`,
                    cursor: selectedItems.has(card.id) && isActuallyDragging ? 'grabbing' : 'grab',
                    padding: '10px',
                    backgroundColor: backgroundColor,
                    color: getTextColor(),
                    border: selectedItems.has(card.id) ? '1pt solid #ffafcc' : card.isInteractive ? '2px solid #4CAF50' : '1px solid #ccc',
                    borderRadius: '12px',
                    width: card.width ? `${card.width}px` : '250px',
                    height: card.height ? `${card.height}px` : '100px',
                    minHeight: '80px',
                    transformOrigin: 'center center',
                    zIndex: 1,
                    ...(selectedItems.has(card.id) && isActuallyDragging ? {
                      animation: 'subtleWiggle 0.3s ease-in-out infinite'
                    } : {}),
                    display: 'flex',
                    flexDirection: 'column',
                    userSelect: 'none',
                    animation: (draggedCardId === card.id && isActuallyDragging)
                      ? 'wiggle 0.3s ease-in-out infinite' 
                      : newCardId === card.id 
                        ? 'cardAppear 0.1s ease-out' 
                        : generatingCardId === card.id
                          ? 'pulsingOutline 1.5s ease-in-out infinite'
                          : undefined,
                    boxShadow: selectedItems.has(card.id) 
                      ? '0 0 0 1pt #ffafcc'
                      : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'none',
                    overflow: 'hidden',
                    // Add a subtle indicator for cards with expiration set
                    borderColor: card.expiresAt ? '#ff7675' : selectedItems.has(card.id) ? '#ffafcc' : '#ccc',
                  }}
                  onMouseDown={(e: React.MouseEvent) => {
                    handleMouseDown(e, card.id);
                    handleStartDrawing(e, card.id);
                  }}
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setSelectedCardId(card.id);
                    setFocusedCardId(card.id);
                    // Removed automatic expansion on click
                  }}
                  onDoubleClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    setEditingCardId(card.id);
                    setTempCardContent(card.content);
                  }}
                  onMouseEnter={(e) => {
                    setHoveredCardId(card.id);
                    handleCardDrawEnd(e, card.id);
                  }}
                  onMouseLeave={() => setHoveredCardId(null)}
                  onFocus={() => setFocusedCardId(card.id)}
                  onBlur={() => setFocusedCardId(null)}
                  tabIndex={0}
                >
                  {card.expiresAt && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        fontSize: '11px',
                        color: '#ff7675',
                        padding: '2px 4px',
                        borderRadius: '3px',
                        backgroundColor: 'rgba(255, 118, 117, 0.1)',
                      }}
                    >
                      Expires in {Math.max(0, Math.floor((card.expiresAt - Date.now()) / (60 * 60 * 1000)))} hours
                    </div>
                  )}
                  
                  {card.isInteractive && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '5px',
                        left: '5px',
                        fontSize: '11px',
                        color: '#4CAF50',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        fontWeight: 'bold',
                      }}
                    >
                      INTERACTIVE
                    </div>
                  )}
                  
                  {card.imageData && (
                    <div
                      style={{
                        position: 'absolute',
                        top: card.isInteractive ? '25px' : '5px',
                        left: '5px',
                        fontSize: '11px',
                        color: '#2196F3',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        backgroundColor: 'rgba(33, 150, 243, 0.1)',
                        border: '1px solid rgba(33, 150, 243, 0.3)',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 16 16" fill="#2196F3">
                        <path d="M1 3.5A1.5 1.5 0 0 1 2.5 2h11A1.5 1.5 0 0 1 15 3.5v8a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 11.5v-8zM2.5 3a.5.5 0 0 0-.5.5v7l2.086-4.172a.5.5 0 0 1 .894 0L6.879 9.5l1.414-1.414a.5.5 0 0 1 .707 0L12 11V3.5a.5.5 0 0 0-.5-.5h-9zM4.5 5.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z"/>
                      </svg>
                      IMAGE
                    </div>
                  )}
                  
                  {card.title && (
                    <div
                      style={{
                        fontStyle: 'italic',
                        fontSize: '12px',
                        color: backgroundColor === '#000000' ? '#ffafcc' : '#6d6875', // Use pink on black, dark pink on light
                        marginBottom: '8px',
                        marginLeft: '12px',
                        marginRight: '12px',
                        marginTop: '8px',
                        cursor: 'pointer',
                        textDecoration: 'none',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const parentCard = findParentCard(card.id);
                        if (parentCard) {
                          const parentElement = document.getElementById(`card-${parentCard.id}`);
                          if (parentElement) {
                            parentElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                          setSelectedCardId(parentCard.id);
                        }
                      }}
                    >
                      {card.title}
                    </div>
                  )}
                  {editingCardId === card.id ? (
                    <div 
                      onClick={(e) => e.stopPropagation()} 
                      onMouseDown={(e) => e.stopPropagation()}
                      style={{ 
                        flex: 1, 
                        position: 'relative',
                        userSelect: 'text',
                        height: 'calc(100% - 40px)',
                        display: 'flex',
                        backgroundColor: backgroundColor,
                      }}
                    >
                      <textarea
                        ref={textareaRef}
                        value={tempCardContent}
                        onChange={handleCardContentChange}
                        onKeyDown={(e) => handleCardContentKeyDown(e, card.id)}
                        onPaste={(e) => {
                          e.stopPropagation();
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onFocus={() => {
                          setActiveInputCardId(card.id);
                          setEditingCardId(card.id);
                        }}
                        onBlur={(e) => {
                          setActiveInputCardId(null);
                          handleCardBlur(e);
                        }}
                        placeholder="Type..."
                        style={{ 
                          width: '100%',
                          height: '100%',
                          padding: '8px',
                          border: 'none',
                          resize: 'none',
                          outline: 'none',
                          fontFamily: "Times New Roman, Times, serif",
                          fontSize: '14px',
                          userSelect: 'text',
                          WebkitUserSelect: 'text',
                          MozUserSelect: 'text',
                          msUserSelect: 'text',
                          cursor: 'text',
                          backgroundColor: backgroundColor,
                          color: getTextColor(),
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div style={{ position: 'relative', flex: 1, overflowY: 'auto' }}>
                      {generatingCardId === card.id && isSearching && (
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          padding: '10px',
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(4px)',
                          borderRadius: '8px',
                          zIndex: 10,
                          textAlign: 'center',
                          fontSize: '14px',
                          boxShadow: '0 2px 6px rgba(0, 0, 0, 0.1)',
                          border: '1px solid #ffafcc',
                          animation: 'pulsingOutline 1.5s ease-in-out infinite',
                          color: getTextColor(),
                        }}>
                          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Searching the web...</div>
                          <div style={{ fontSize: '12px', opacity: 0.8 }}>Gathering information from online sources</div>
                        </div>
                      )}
                      
                      {/* Render interactive content if this is an interactive card */}
                      {card.isInteractive && card.htmlContent ? (
                        <iframe
                          srcDoc={card.htmlContent}
                          style={{
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            borderRadius: '8px',
                            margin: '8px 0',
                          }}
                          sandbox="allow-scripts allow-forms allow-modals allow-popups allow-same-origin"
                          title="Interactive Element"
                        />
                      ) : (
                        <>
                          {/* Display pasted image if it exists */}
                          {card.imageData && (
                            <div
                              style={{
                                marginTop: '8px',
                                marginBottom: '8px',
                                marginLeft: '12px',
                                marginRight: '12px',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              <img
                                src={card.imageData}
                                alt="Pasted content"
                                style={{
                                  maxWidth: '100%',
                                  maxHeight: '150px',
                                  borderRadius: '4px',
                                  border: '1px solid #ccc',
                                }}
                              />
                            </div>
                          )}
                          <div 
                            style={{ 
                              wordWrap: 'break-word', 
                              overflowWrap: 'break-word',
                              maxWidth: '100%',
                              marginTop: '8px',
                              marginBottom: '8px',
                              marginLeft: '12px',
                              marginRight: '12px',
                              color: getTextColor(),
                            }}
                            className={links.some(link => link.toId === card.id && link.fromId === generatingCardId) ? 'streaming-text' : ''}
                            dangerouslySetInnerHTML={{ __html: renderedContents[card.id] || '' }}
                          />
                          <div
                            style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '20px',
                              background: `linear-gradient(to bottom, rgba(${getBackgroundRGB()},0) 0%, rgba(${getBackgroundRGB()},1) 100%)`,
                              pointerEvents: 'none',
                            }}
                          />
                        </>
                      )}
                    </div>
                  )}
                  <div 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      marginTop: 'auto',
                      position: 'absolute',
                      bottom: '12px',
                      left: '12px',
                      right: '12px',
                      opacity: hoveredCardId === card.id ? 1 : 0,
                      transition: 'opacity 0.3s ease-in-out',
                      animation: hoveredCardId === card.id ? 'fadeIn 0.3s ease-in-out' : 'none',
                    }}
                  >
                    <div style={{ display: 'flex', gap: '8px' }}>
                                                  <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCards(prevCards =>
                              prevCards.map(c =>
                                c.id === card.id ? { 
                                  ...c, 
                                  model: c.model === 'openai' 
                                    ? 'deepseek'
                                    : c.model === 'deepseek'
                                    ? 'claude-sonnet-4-20250514'
                                    : c.model === 'claude-sonnet-4-20250514'
                                    ? 'all-models'
                                    : 'openai'
                                } : c
                              )
                            );
                          }}
                          style={{
                            backgroundColor: getButtonColors().background,
                            color: getButtonColors().color,
                            border: `1px solid ${getButtonColors().border}`,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                            fontFamily: "Times New Roman, Times, serif",
                            fontSize: '14px',
                          }}
                        >
                          {(card.model || selectedModel) === 'openai' 
                            ? 'OpenAI'
                            : (card.model || selectedModel) === 'deepseek'
                            ? 'DeepSeek'
                            : (card.model || selectedModel) === 'claude-sonnet-4-20250514'
                            ? 'Claude S'
                            : (card.model || selectedModel) === 'all-models'
                            ? 'All Models'
                            : 'Claude S'}
                        </button>
                        {card.imageData && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setCards(prevCards =>
                                prevCards.map(c =>
                                  c.id === card.id ? { ...c, imageData: undefined } : c
                                )
                              );
                            }}
                            style={{
                              backgroundColor: '#ff6b6b',
                              color: 'white',
                              border: '1px solid #ff6b6b',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                              fontFamily: "Times New Roman, Times, serif",
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '32px',
                            }}
                            title="Remove pasted image"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
                              <path d="M12 4L4 12M4 4l8 8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedCardId(card.id);
                            setDocumentMode(true);
                          }}
                          style={{
                            backgroundColor: getButtonColors().background,
                            color: getButtonColors().color,
                            border: `1px solid ${getButtonColors().border}`,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                            fontFamily: "Times New Roman, Times, serif",
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                          }}
                          title="Expand to document view"
                        >
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 6V1H6M15 6V1H10M1 10V15H6M10 15H15V10" stroke={getButtonColors().color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                                                </button>
                        {card.isInteractive && card.htmlContent && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Create a new card with the HTML source code
                              const sourceCard: Card = {
                                id: Date.now(),
                                title: `Source: ${card.title || 'Interactive Element'}`,
                                content: `\`\`\`html\n${card.htmlContent}\n\`\`\``,
                                position: {
                                  x: card.position.x + 260,
                                  y: card.position.y + 100,
                                },
                                conversationHistory: [],
                                width: 400,
                                height: 300,
                              };
                              setCards(prevCards => [...prevCards, sourceCard]);
                              setLinks(prevLinks => [...prevLinks, { fromId: card.id, toId: sourceCard.id }]);
                            }}
                            style={{
                              backgroundColor: getButtonColors().background,
                              color: getButtonColors().color,
                              border: `1px solid ${getButtonColors().border}`,
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                              fontFamily: "Times New Roman, Times, serif",
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              width: '32px',
                            }}
                            title="View source code"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M4.5 7.5L2 10l2.5 2.5M11.5 7.5L14 10l-2.5 2.5M9 3L7 13" stroke={getButtonColors().color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCardExpiration(card.id, !card.expiresAt);
                          }}
                          style={{
                            backgroundColor: card.expiresAt ? '#ff7675' : getButtonColors().background,
                            color: card.expiresAt ? 'white' : getButtonColors().color,
                            border: `1px solid ${getButtonColors().border}`,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                            fontFamily: "Times New Roman, Times, serif",
                            fontSize: '14px',
                          }}
                        >
                          {card.expiresAt ? '24h ✓' : '24h'}
                        </button>
                        {/* Voice Recording Button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (recordingCardId === card.id) {
                              stopRecording();
                            } else if (recordingCardId === null) {
                              startRecording(card.id);
                            }
                          }}
                          disabled={recordingCardId !== null && recordingCardId !== card.id}
                          style={{
                            backgroundColor: recordingCardId === card.id 
                              ? '#ff6b6b' 
                              : isTranscribing 
                              ? '#ff9800' 
                              : getButtonColors().background,
                            color: recordingCardId === card.id || isTranscribing ? 'white' : getButtonColors().color,
                            border: `1px solid ${getButtonColors().border}`,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            cursor: (recordingCardId !== null && recordingCardId !== card.id) ? 'not-allowed' : 'pointer',
                            boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                            fontFamily: "Times New Roman, Times, serif",
                            fontSize: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '32px',
                            opacity: (recordingCardId !== null && recordingCardId !== card.id) ? 0.5 : 1,
                            position: 'relative',
                          }}
                          title={
                            recordingCardId === card.id 
                              ? `Recording... ${recordingTime}s (click to stop)` 
                              : isTranscribing 
                              ? 'Transcribing audio...'
                              : 'Record voice memo'
                          }
                        >
                          {recordingCardId === card.id ? (
                            // Recording icon with pulsing animation
                            <div style={{
                              width: '16px',
                              height: '16px',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              animation: 'pulsingOutline 1s ease-in-out infinite',
                            }} />
                          ) : isTranscribing ? (
                            // Transcribing spinner
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                              <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" opacity="0.25"/>
                              <path d="M12,4a8,8,0,0,1,7.89,6.7A1.53,1.53,0,0,0,21.38,12h0a1.5,1.5,0,0,0,1.48-1.75,11,11,0,0,0-21.72,0A1.5,1.5,0,0,0,2.62,12h0a1.53,1.53,0,0,0,1.49-1.3A8,8,0,0,1,12,4Z">
                                <animateTransform attributeName="transform" type="rotate" dur="0.75s" values="0 12 12;360 12 12" repeatCount="indefinite"/>
                              </path>
                            </svg>
                          ) : (
                            // Microphone icon
                            <svg width="16" height="16" viewBox="0 0 16 16" fill={getButtonColors().color}>
                              <path d="M3.5 6.5A.5.5 0 0 1 4 7v1a4 4 0 0 0 8 0V7a.5.5 0 0 1 1 0v1a5 5 0 0 1-4.5 4.975V15h3a.5.5 0 0 1 0 1h-7a.5.5 0 0 1 0-1h3v-2.025A5 5 0 0 1 3 8V7a.5.5 0 0 1 .5-.5z"/>
                              <path d="M10 8a2 2 0 1 1-4 0V3a2 2 0 1 1 4 0v5zM8 0a3 3 0 0 0-3 3v5a3 3 0 0 0 6 0V3a3 3 0 0 0-3-3z"/>
                            </svg>
                          )}
                          {recordingCardId === card.id && (
                            <div style={{
                              position: 'absolute',
                              bottom: '-18px',
                              left: '50%',
                              transform: 'translateX(-50%)',
                              fontSize: '10px',
                              color: '#ff6b6b',
                              whiteSpace: 'nowrap',
                              fontWeight: 'bold',
                            }}>
                              {recordingTime}s
                            </div>
                          )}
                        </button>
                        {/* PDF Upload Button for Parse Cards */}
                        {(card.content.trim() === '/parse' || card.content.includes('Upload a PDF using the button below') || card.title === 'Parse Options') && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePdfParseUpload(card.id, e);
                            }}
                            style={{
                              backgroundColor: '#2196F3',
                              color: 'white',
                              border: '1px solid #2196F3',
                              padding: '4px 8px',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                              fontFamily: "Times New Roman, Times, serif",
                              fontSize: '14px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}
                            title="Upload PDF for text extraction"
                          >
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
                              <path d="M8.5 6a.5.5 0 0 0-1 0v1.5H6a.5.5 0 0 0 0 1h1.5V10a.5.5 0 0 0 1 0V8.5H10a.5.5 0 0 0 0-1H8.5V6z"/>
                              <path d="M14 4.5V14a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V2a2 2 0 0 1 2-2h5.5L14 4.5zm-3 0A1.5 1.5 0 0 1 9.5 3V1H4a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V4.5h-2z"/>
                            </svg>
                            PDF
                          </button>
                        )}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {editingCardId !== card.id && (
                          <>
                            {/* Check if this card is connected to an interactive card */}
                            {(() => {
                              const connectedCards = getConnectedCards(card.id);
                              const interactiveCard = connectedCards.find(c => c.isInteractive);
                              return interactiveCard && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (interactiveCard.conversationHistory.length > 0) {
                                      const originalPrompt = interactiveCard.conversationHistory[0];
                                      generateInteractiveElement(interactiveCard.id, originalPrompt);
                                    }
                                  }}
                                  disabled={generatingCardId === interactiveCard.id}
                                  style={{
                                    backgroundColor: '#4CAF50',
                                    color: 'white',
                                    border: '1px solid #4CAF50',
                                    padding: '5px 10px',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                                    fontFamily: "Times New Roman, Times, serif",
                                    fontSize: '14px',
                                  }}
                                  title="Regenerate connected interactive element"
                                >
                                  {generatingCardId === interactiveCard.id ? 'Regenerating...' : 'Regen Interactive'}
                                </button>
                              );
                            })()}
                            {(isChildCard(card.id) || generatedCardIds.has(card.id)) && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGenClick(card.id);
                                }}
                                disabled={generatingCardId === card.id}
                                style={{
                                  backgroundColor: getButtonColors().background,
                                  color: getButtonColors().color,
                                  border: `1px solid ${getButtonColors().border}`,
                                  padding: '5px 10px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                                  fontFamily: "Times New Roman, Times, serif",
                                  fontSize: '14px',
                                }}
                              >
                                {generatingCardId === card.id ? 'Generating...' : 'Gen'}
                              </button>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isChildCard(card.id) || generatedCardIds.has(card.id)) {
                                  handleAddClick(card.id);
                                } else {
                                  handleGenerateClick(card.id);
                                }
                              }}
                              disabled={generatingCardId === card.id}
                              style={{
                                backgroundColor: getButtonColors().background,
                                color: getButtonColors().color,
                                border: `1px solid ${getButtonColors().border}`,
                                padding: '5px 10px',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                                fontFamily: "Times New Roman, Times, serif",
                                fontSize: '14px',
                              }}
                            >
                              {isChildCard(card.id) || generatedCardIds.has(card.id) ? 'Add' : (generatingCardId === card.id ? 'Generating...' : 'Generate')}
                            </button>

                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {Object.entries(RESIZE_HANDLES).map(([handle, config]) => (
                    <div
                      key={handle}
                      style={{
                        position: 'absolute',
                        width: '10px',
                        height: '10px',
                        cursor: config.cursor,
                        ...config.position,
                        background: 'transparent',
                        zIndex: 2,
                      }}
                      onMouseDown={(e) => handleResizeStart(e, card.id, handle)}
                    />
                  ))}
                </div>
              ))}

            {images.filter(image => {
              const imageElement = document.querySelector(`img[src="${image.url}"]`) as HTMLImageElement;
              const width = imageElement?.width || 300;
              const height = imageElement?.height || 300;
              return isElementInViewport(image.position, width, height);
            }).map(image => (
              <div
                key={image.id}
                className={selectedItems.has(image.id) ? 'selected-card' : ''}
                style={{
                  position: 'absolute',
                  left: `${image.position.x}px`,
                  top: `${image.position.y}px`,
                  cursor: selectedItems.has(image.id) && isActuallyDragging ? 'grabbing' : 'grab',
                  border: selectedItems.has(image.id) ? '1pt solid #ffafcc' : 'none',
                  borderRadius: '4px',
                                      boxShadow: selectedItems.has(image.id) ? '0 0 0 1pt #ffafcc' : 'none',
                  transition: 'none',
                  zIndex: 1,
                }}
                onMouseDown={(e) => handleMouseDown(e, image.id)}
                onClick={(e) => e.stopPropagation()}
                onMouseEnter={() => setHoveredImageId(image.id)}
                onMouseLeave={() => setHoveredImageId(null)}
              >
                <img
                  src={image.url}
                  alt="Uploaded"
                  style={{
                    maxWidth: '300px',
                    maxHeight: '300px',
                    pointerEvents: 'none',
                  }}
                />
                <div
                  style={{
                    position: 'absolute',
                    bottom: '10px',
                    right: '10px',
                    display: 'flex',
                    gap: '8px',
                    opacity: hoveredImageId === image.id ? 1 : 0,
                    transition: 'opacity 0.3s ease-in-out',
                  }}
                >
                  {/* Find the source card that generated this image */}
                  {links.some(link => {
                    const sourceCard = cards.find(card => card.id === link.fromId);
                    return link.toId === image.id && sourceCard?.content?.trim().startsWith('/image');
                  }) ? (
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        // Find the original prompt card
                        const link = links.find(l => l.toId === image.id);
                        const promptCard = cards.find(c => c.id === link?.fromId);
                        if (promptCard) {
                          const prompt = promptCard.content.replace('/image', '').trim();
                          const newImageUrl = await generateImage(prompt);
                          if (newImageUrl) {
                            const newImage: Image = {
                              id: Date.now(),
                              url: newImageUrl,
                              position: {
                                x: image.position.x + 20,
                                y: image.position.y + 20,
                              },
                              description: prompt
                            };
                            setImages(prevImages => [...prevImages, newImage]);
                            // Link the new image to the original prompt card
                            setLinks(prevLinks => [...prevLinks, {
                              fromId: promptCard.id,
                              toId: newImage.id
                            }]);
                          }
                        }
                      }}
                      style={{
                        backgroundColor: getButtonColors().background,
                        color: getButtonColors().color,
                        border: `1px solid ${getButtonColors().border}`,
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                        fontFamily: "Times New Roman, Times, serif",
                        fontSize: '14px',
                      }}
                    >
                      Generate
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Create a new card for asking questions with image context
                          const newCard: Card = {
                            id: Date.now(),
                            content: "Ask a question about this image...",
                            position: {
                              x: image.position.x + 260,
                              y: image.position.y,
                            },
                            conversationHistory: [`This conversation is about an image. When you respond, reference the image that was uploaded.`],
                            width: 250,
                            height: 100,
                          };
                          setCards(prevCards => [...prevCards, newCard]);
                          setLinks(prevLinks => [...prevLinks, { fromId: image.id, toId: newCard.id }]);
                          setEditingCardId(newCard.id);
                          setPendingInputCards(prev => new Set(prev).add(newCard.id));
                        }}
                        style={{
                          backgroundColor: getButtonColors().background,
                          color: getButtonColors().color,
                          border: `1px solid ${getButtonColors().border}`,
                          padding: '5px 10px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                          fontFamily: "Times New Roman, Times, serif",
                          fontSize: '14px',
                        }}
                      >
                        Ask
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          analyzeExistingImage(image.id);
                        }}
                        style={{
                          backgroundColor: getButtonColors().background,
                          color: getButtonColors().color,
                          border: `1px solid ${getButtonColors().border}`,
                          padding: '5px 10px',
                          borderRadius: '4px',
                          cursor: interpretingImageId === image.id ? 'wait' : 'pointer',
                          boxShadow: '0 2px 0 rgba(0, 0, 0, 0.2)',
                          fontFamily: "Times New Roman, Times, serif",
                          fontSize: '14px',
                        }}
                      >
                        {interpretingImageId === image.id ? 'Interpreting...' : 'Interpret'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}

            {pdfs.filter(pdf => isElementInViewport(pdf.position, pdf.width, pdf.height))
              .map(pdf => (
                <div
                  key={pdf.id}
                  className={selectedItems.has(pdf.id) ? 'selected-card' : ''}
                  style={{
                    position: 'absolute',
                    left: `${pdf.position.x}px`,
                    top: `${pdf.position.y}px`,
                    cursor: selectedItems.has(pdf.id) && isActuallyDragging ? 'grabbing' : 'grab',
                    border: selectedItems.has(pdf.id) ? '1pt solid #ffafcc' : '2px solid #ccc',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    padding: '10px',
                    backgroundColor: backgroundColor,
                    boxShadow: selectedItems.has(pdf.id) ? '0 0 0 1pt #ffafcc' : '0 2px 10px rgba(0,0,0,0.1)',
                    width: `${pdf.width}px`,
                    height: `${pdf.height}px`,
                    transition: 'none',
                    zIndex: 1,
                  }}
                  onMouseDown={(e) => handleMouseDown(e, pdf.id)}
                  onClick={(e) => e.stopPropagation()}
                >
                  <iframe
                    src={pdf.url}
                    title="PDF Viewer"
                    style={{
                      width: '100%',
                      height: '100%',
                      border: 'none',
                      borderRadius: '4px',
                    }}
                  />
                  {Object.entries(RESIZE_HANDLES).map(([handle, config]) => (
                    <div
                      key={handle}
                      style={{
                        position: 'absolute',
                        width: '10px',
                        height: '10px',
                        cursor: config.cursor,
                        ...config.position,
                        background: 'transparent',
                        zIndex: 2,
                      }}
                      onMouseDown={(e) => handleResizeStart(e, pdf.id, handle)}
                    />
                  ))}
                </div>
              ))}
          </div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleImageUpload}
          accept="image/*"
        />
        <input
          type="file"
          ref={backgroundImageInputRef}
          style={{ display: 'none' }}
          onChange={handleBackgroundImageUpload}
          accept="image/*"
        />
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          display: 'flex',
          gap: '10px',
          zIndex: 1000,
        }}>
          <button
            onClick={() => setShowConfigModal(true)}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: `1px solid ${getButtonColors().border}`,
              backgroundColor: getButtonColors().background,
              cursor: 'pointer',
              fontFamily: "Times New Roman, Times, serif",
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: getButtonColors().color,
            }}
          >
            <span>Models</span>
          </button>
          <button
            onClick={cycleBackgroundColor}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: `1px solid ${getButtonColors().border}`,
              backgroundColor: getButtonColors().background,
              cursor: 'pointer',
              fontFamily: "Times New Roman, Times, serif",
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: getButtonColors().color,
            }}
            title={`Current: ${backgroundColor === '#ffffff' ? 'White' : backgroundColor === '#faedcd' ? 'Beige' : backgroundColor === '#ccd5ae' ? 'Green' : backgroundColor === '#000000' ? 'Black' : 'Beige'}`}
          >
            <div
              style={{
                width: '16px',
                height: '16px',
                backgroundColor: backgroundColor,
                border: '1px solid #ccc',
                borderRadius: '2px',
              }}
            />
            <span>Theme</span>
          </button>
        </div>
        
        {/* Background image controls on the right side */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          right: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px',
          zIndex: 1000,
        }}>
          <button
            onClick={triggerBackgroundImageUpload}
            style={{
              padding: '8px 16px',
              borderRadius: '4px',
              border: `1px solid ${getButtonColors().border}`,
              backgroundColor: getButtonColors().background,
              cursor: 'pointer',
              fontFamily: "Times New Roman, Times, serif",
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              color: getButtonColors().color,
            }}
            title="Upload background image"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill={getButtonColors().color}>
              <path d="M1 3.5A1.5 1.5 0 0 1 2.5 2h11A1.5 1.5 0 0 1 15 3.5v8a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 1 11.5v-8zM2.5 3a.5.5 0 0 0-.5.5v7l2.086-4.172a.5.5 0 0 1 .894 0L6.879 9.5l1.414-1.414a.5.5 0 0 1 .707 0L12 11V3.5a.5.5 0 0 0-.5-.5h-9zM4.5 5.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z"/>
            </svg>
            <span>Background</span>
          </button>
          {backgroundImage && (
            <button
              onClick={clearBackgroundImage}
              style={{
                padding: '8px 16px',
                borderRadius: '4px',
                border: '1px solid #ff6b6b',
                backgroundColor: '#ff6b6b',
                cursor: 'pointer',
                fontFamily: "Times New Roman, Times, serif",
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                color: 'white',
              }}
              title="Clear background image"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="white">
                <path d="M12 4L4 12M4 4l8 8" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
              <span>Clear BG</span>
            </button>
          )}
        </div>
        {showConfigModal && <ConfigModal setShowConfigModal={setShowConfigModal} />}
        {/* Always show the multiselection toggle */}
        <div style={{
          position: 'fixed',
          bottom: '20px',
          left: '50%',
          transform: 'translateX(-2%)',
          backgroundColor: isSelectionModeOn ? 'rgba(255, 175, 204, 0.9)' : 'rgba(255, 255, 255, 0.9)',
          padding: '6px 12px',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          zIndex: 1000,
          fontFamily: "Times New Roman, Times, serif",
          fontSize: '14px',
          color: '#333',
          border: '1px solid #ffafcc',
          pointerEvents: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          {isShiftPressed ? (
            <span>Multiselect Mode (Shift)</span>
          ) : isSelectionModeOn ? (
            <span>Selection Mode On</span>
          ) : (
            <span>Multiselect</span>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsSelectionModeOn(prev => !prev);
            }}
            style={{
              backgroundColor: isSelectionModeOn ? '#ffafcc' : getButtonColors().background,
              color: isSelectionModeOn ? 'white' : getButtonColors().color,
              border: '1px solid #ffafcc',
              padding: '3px 6px',
              borderRadius: '3px',
              cursor: 'pointer',
              fontFamily: "Times New Roman, Times, serif",
              fontSize: '12px',
            }}
          >
            {isSelectionModeOn ? 'Turn Off' : 'Turn On'}
          </button>
        </div>
      </div>
      {expandedCardId && <DocumentView cardId={expandedCardId} />}
    </>
  );
}

export default App;
